import { persistNavigationDraft } from './data/navigationDraft'
import { portableMarkdownNote } from './data/portableMarkdown'
import { contextsAccount } from './contexts/account'
import { dismissContextStep } from './navigation/contextBack'
import { readContextRoute, replaceContextRoute, readContextCollectionRoute, replaceContextCollectionRoute, contextPath, readLinksRoute, replaceLinksRoute } from './navigation/noteRoute'
import { StudioBrand } from './design/studio/StudioBrand'
import { MobileSidebarDialog } from './design/studio/MobileSidebarDialog'
import { AccountAvatar } from './components/AccountAvatar'
import { useMinimumProgressLabel } from './utils/useMinimumProgress'
import { StudioTagPicker } from './design/studio/StudioTagPicker'
import { StudioSettingsNavigation, SettingsCategoryPanels, StudioAppearanceChoices, studioCategoryForSection, matchingSettingsCategories, type StudioSettingsCategory } from './design/studio/StudioSettings'
import { DataStorageSettings } from './design/studio/DataStorageSettings'
import { SettingsSectionMenu, type SettingsSectionMenuItem } from './design/studio/SettingsSectionMenu'
import {
  lazy,
  startTransition,
  Suspense,
  useCallback,
  useDeferredValue,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type CSSProperties,
  type ChangeEvent,
  type DragEvent,
  type KeyboardEvent,
} from 'react'
import { Capacitor } from '@capacitor/core'
import { useStudioDesign } from './design/studio/useStudioDesign'
import { searchFilterScopePresentation, studioShortcutFilters } from './design/studio/filters'
import { DesktopSidebar, type StudioDestination } from './design/studio/DesktopSidebar'
import { renderWorkspaceSurface } from './design/studio/WorkspaceSurface'
import type { StudioPreference } from './design/studio/preference'
import { createAssetTransport } from './assets/assetTransport'
import {
  IMAGE_CONFIG,
  IMAGE_FILE_ACCEPT,
  assetRequiresSynchronization,
  createLocalImageAsset,
  type NoteAsset,
} from './assets/assetTypes'
import { normalizedImageMimeType, validateImageFile } from './assets/imagePolicy'
import { ImageWorkerProcessor } from './assets/imageProcessor'
import { ImageQueue } from './assets/imageQueue'
import {
  cacheRemoteAssetPreviews,
  remoteAssetNeedsPreviewCache,
  remotePreviewCacheKey,
  remotePreviewRetryDelay,
} from './assets/remotePreviewCache'
import { takeIncomingShare } from './assets/shareTargetInbox'
import { commitSharedDraft, wasShareAdopted } from './assets/shareTargetAdoption'
import { createPortal } from 'react-dom'
import { APP_SETTINGS } from './config/appSettings'
import { authProvider } from './auth/sharedAuthProvider'
import { activeWorkspace, assertWorkspaceActive, assertWorkspaceIdentity, markWorkspaceSignedOut, registerWorkspaceDraftReader, workspaceDatabaseName } from './data/workspace'
import { authorizeWorkspaceRemoval } from './data/workspaceRemoval'
import { takeDeletionResume } from './auth/accountDeletion'
import { markAccountDeleted } from './auth/accountDeletedFlag'
import type { SignInOptions } from './auth/authProvider'
import { getInitialIdentity } from './auth/cachedIdentity'
import { startAuthSessionRestoration } from './auth/authSessionRestoration'
import type { AuthSession, NativeOAuthHandlingResult } from './auth/authProvider'
import { startDurableOAuthNavigation } from './auth/durableOAuthNavigation'
import { authBusyAfterNativeOAuthResult } from './auth/nativeOAuth'
import { getMyAccessProfile, type UserAccessProfile } from './auth/userAccessProfile'
import { addNotesToContext, deleteManualContext, readManualContexts, undoContextAddition, type ManualContext } from './intelligence/contextLab/manualContexts'
import { SmartContextSuggestion, SmartContextSuggestionBar } from './intelligence/contextLab/SmartContextSuggestion'
import { notePreview } from './intelligence/contextLab/notePreview'
import { readSmartPilotPreferences, writeSmartPilotPreferences, type SmartPilotPreferences } from './intelligence/smartPilot/preferences'
import type { SmartPilotResponse } from './intelligence/smartPilot/client'
import { createDevFlow, devFlowReason, flushDevFlowEvents, type DevFlow } from './intelligence/smartPilot/devFlowLog'
import {
  accountCanAddNote,
  getMyAccountAccess,
  type AccountAccess,
} from './auth/accountAccess'
import { PlanLimitError, planLimitMessage, rememberAccountLimits } from './auth/planLimits'
import { onSignInRequested } from './contextsV2/signInRequest'
import { contextsAsMarkdown, type ContextsExport } from './data/contextsExport'
import { readContextsExport, readRecoverableContexts } from './data/readContextsExport'
import { downloadBlob, readableSize } from './utils/downloadFile'
import { restoreRecoveredContexts } from './data/recoveredContexts'
import type { NotesExportStatus } from './design/studio/DataStorageSettings'
import { AccountPlanUsage } from './auth/AccountPlanUsage'
import { assertRestoreFitsImageLimit, imageCapacity } from './assets/imageLimits'
import { BackButton } from './components/BackButton'
import { Icon } from './components/Icon'
import { leaveSheet } from './components/sheetMotion'
import { NoteImageStage } from './components/NoteImageStage'
import { ShareTidyStrip } from './components/ShareTidyStrip'
import { LinkNoteCard } from './components/LinkNoteCard'
import { SheetDialog } from './components/SheetDialog'
import { LinkActionsMenu } from './components/LinkActionsMenu'
import { LinkAnswersOverlay } from './components/LinkAnswers'
import { createGistController } from './links/gistController'
import { libraryEnabled } from './links/library/libraryModel'
import type { LinkAttributePatch } from './links/library/LinksLibrary'
import { GIST_SETTINGS, type GistTrigger } from './links/gistPolicy'
import { gistsEnabled } from './links/gistClient'
import { leadingLinkCard, linkTargetKey, type LinkDisplayChoice } from './links/linkDisplay'
import { ImageLightbox, type ImagePreview } from './components/ImageLightbox'
import { useImageStageLayout } from './images/useImageStageLayout'
import {
  MarkdownEditor,
  type MarkdownEditorHandle,
  type MarkdownFormat,
} from './components/MarkdownEditor'
import { ActionsLog } from './components/ActionsLog'
import { NotificationsViewport, notify, type NotificationId } from './components/Notifications'
import {
  dispatchNativeReminderNotificationAction,
  getNativeReminderNotifications,
  reconcileNativeReminderNotifications,
  reminderScheduleInputs,
  requestNativeReminderPermission,
} from './native/reminderNotifications'
import { NoteCard } from './components/NoteCard'
import { NativeReminderSnoozeDialog } from './components/NativeReminderSnoozeDialog'
import { ReminderEditor } from './components/ReminderEditor'
import { SearchResultCard } from './components/SearchResultCard'
import { NoteSelectionDialog, type SelectionAction } from './components/NoteSelectionDialog'
import { OperationNotice } from './components/OperationNotice'
import { downloadAssetMaster } from './assets/downloadAssetMaster'
import { copyMergeAssets, commitNoteOperation, operationMessage, operationPrefix, planMerge, planTags, undoNoteOperation, type NoteOperation } from './domain/noteOperations'
import { SearchSelectionBar } from './components/SearchSelectionBar'
import { SearchLayoutSelector } from './components/SearchLayoutSelector'
import { SearchClearButton } from './components/SearchClearButton'
import { SearchEmptyState } from './components/SearchEmptyState'
import { searchEmptyCause as searchEmptyCauseFor } from './search/emptyCause'
import onboardingContent from '@minimany/content/onboarding.json'
import { useWelcome } from './welcome/useWelcome'
import { useSignInStrip } from './welcome/useSignInStrip'
import { TagAutocomplete } from './components/TagAutocomplete'
import { TagLabel } from './components/TagLabel'
import { GoogleKeepGlobalStatus } from './components/GoogleKeepImport'
import { useGoogleKeepImports } from './imports/googleKeep/client'
import { NoteExportBrowseStatus, NoteExportDialog } from './components/NoteExport'
import { useNoteExports } from './exports/client'
import type { NoteExportOptions } from './exports/contract'
import { getDesktopShortcut } from './utils/desktopShortcuts'
import { installScrollResponsiveComposerHeader } from './utils/composerHeaderScroll'
import { scheduleComposerAttachmentReveal } from './utils/composerAttachmentReveal'
import { markdownForClipboard } from './utils/markdownWysiwyg'
import {
  deriveNoteUploadStatus,
  UploadStatusIndicator,
} from './components/UploadStatusIndicator'
import {
  dismissSearchSelection as createDismissedSearchSelection,
  startSearchSelection as createSearchSelection,
  toggleSearchSelection as toggleSearchSelectionState,
} from './search/searchSelection'
import {
  NoteRepository,
  requestPersistentStorage,
  type NotePageCursor,
} from './data/db'
import type { LocalDataContinuitySnapshot } from './data/continuity'
import {
  buildLocalRecoveryExport,
  fingerprintRecoveryAccount,
} from './data/localRecoveryExport'
import {
  buildLocalRecoveryImportPlan,
  parseLocalRecoveryExport,
  verifyRecoveryBinding,
} from './data/localRecoveryImport'
import {
  bumpNote,
  applyPastedUrlAttachmentChoice,
  attachmentUrlKey,
  attachmentUrlsForDraft,
  classifyContent,
  createBlankDraft,
  DEFAULT_TAGS,
  draftFromNote,
  expireNoteIfDue,
  extractUrls,
  formatRelativeTime,
  hasDraftContent,
  inferredTagsForNote,
  isGoogleKeepRollbackTombstone,
  mergeUrlMetadata,
  noteFromDraft,
  normalizeTags,
  setNoteQuickAccess,
  withUnpackedTitle,
  transitionNoteStatus,
  type ActiveDraft,
  type LinkAnswer,
  type Note,
  type NoteStatus,
  type UrlEntity,
} from './domain/note'
import { developerAccessNeeded, developerAccessVerified, developerToolsAvailable, syncAuthorizedContexts } from './intelligence/contextLabAccess'
import { readContextsPreference, writeContextsPreference } from './intelligence/contextsPreference'
import { mayBeBookingShare } from './screenshotCleanup/bookingWords'
import { cleanupAvailable, developerFeatureShown, readCleanupPreference, readRememberedAdmin, rememberAdmin, readUnpackPreferences, writeCleanupPreference, writeUnpackPreferences, type UnpackPreferences } from './screenshotCleanup/gate'
import type { NoteCover } from './screenshotCleanup/covers'
import type { UnpackMenuEntry } from './screenshotCleanup/CleanupPanel'
import { sameCard, type UnpackedCard } from './screenshotCleanup/card'
import { sameSubject } from './links/mergeUnpacked'
import { MEASUREMENT_PREFERENCES, normalizeMeasurementPreference, resolveMeasurementSystem, type MeasurementPreference } from './domain/units'
import {
  acknowledgeReminder,
  createReminder,
  isReminderElapsed,
  reminderNaturalTime,
  snoozeReminder,
  type Reminder,
  type WeekStartsOn,
} from './domain/reminder'
import { removeUrlFromMarkdown } from './utils/markdownText'
import { authoredUrlLabelInMarkdown, capturedShare, formatNoteUrlsAsMarkdown, isSharedFileLabel } from './utils/urlIntake'
import { writeShareOriginal } from './links/shareOriginal'
import { linkOnlyEditorTitle } from './links/linkEditorPresentation'
import { runNoteCardTransition } from './utils/noteCardTransition'
import { scrollSearchNavigationIntoView } from './utils/searchResultScroll'
import { installSearchResultPaintWindow } from './utils/searchResultPaintWindow'
import { tagsByMostRecentUse } from './utils/tagRecency'
import {
  AUTO_EXPIRE_OPTIONS,
  createDefaultDeviceSettings,
  createDefaultGlobalSettings,
  detectDefaultDeviceName,
  detectPreferredDeviceName,
  displayDeviceName,
  normalizeCustomDeviceName,
  readBootstrapPerformanceDiagnostics,
  readBootstrapStartMode,
  updateDeviceSettings,
  updateGlobalSettings,
  updateReminderQuickOptions,
  updateMeasurementSystem,
  updateWeekStartsOn,
  writeBootstrapStartMode,
  writeBootstrapPerformanceDiagnostics,
  writeBootstrapUiSize,
  type AppStartMode,
  type AutoExpireDays,
  type DeviceSettings,
  type GlobalSettings,
  type SettingsRecord,
  type UiSize,
} from './domain/settings'
import {
  createLinkMetadataProvider,
  deferLinkMetadataRetry,
  deriveLinkMetadataStatus,
  enrichUrl,
  isLinkMetadataIncomplete,
  LinkMetadataCoordinationError,
  LinkMetadataRemoteBackoffError,
  linkMetadataRetryDelayMs,
  LINK_METADATA_POLICY_VERSION,
  needsLinkMetadataRefresh,
  recordLinkMetadataFailure,
  resolveLinkMetadataApiBaseUrl,
  resetLinkMetadataRetry,
  shouldRetryLinkMetadataOnOpen,
  type LinkMetadata,
  type LinkMetadataStatus,
} from './links/linkMetadata'
import { collectLinkMetadataCandidates, linkMetadataUrlKey } from './links/linkMetadataWork'
import {
  APP_UPDATE_AVAILABLE_EVENT,
  APP_UPDATE_READY_EVENT,
  activateWaitingServiceWorker,
  clearAppUpdateReminder,
  dispatchSafeAppReload,
  queryDeployedBuildId,
  readAppUpdateReminderAt,
  readAppReloadRequest,
  requestAppNavigationThroughWorker,
  safelyReloadAfterLocalDurability,
  shouldPromptForAppUpdate,
  snoozeAppUpdateReminder,
  verifyFreshAppShell,
} from './pwaUpdate'
import { DeferredNoteSearchIndex } from './search/deferredNoteSearch'
import {
  browseStartupPresentation,
  type BrowseInventoryPhase,
} from './search/browseStartup'
import type { NoteSearchResult, SearchFilters } from './search/noteSearch'
import {
  completeTagSearchQuery,
  parseTagSearchQuery,
  removeTagFromSearchQuery,
  tagSearchPrefix,
} from './search/tagQuery'
import {
  readSearchLayout,
  writeSearchLayout,
  type SearchLayout,
} from './search/layoutPreference'
import {
  browsePresentationCss,
  DEFAULT_BROWSE_PRESENTATION,
} from './search/browsePresentation'
import {
  clampSearchNavigationIndex,
  getAppBackAction,
  getSettingsCloseFocusTarget,
  getSearchEscapeAction,
  moveSearchNavigationIndex,
  shouldKeepBackGuardAfterEscape,
} from './search/searchNavigation'
import { isPullToRefreshGesture, type PullToRefreshGesture } from './utils/pullToRefresh'
import {
  openMobileSearchSurface,
} from './search/mobileSearchSurface'
import {
  adminPath,
  clearNoteRoute,
  pushGuardedRouteEntry,
  readAdminRoute,
  readNoteRoute,
  replaceNoteRoute,
} from './navigation/noteRoute'
import { getNativeBackAction } from './navigation/nativeBackNavigation'
import {
  applyAppLaunchIntent,
  captureEntryIsInstant,
  cleanAppLaunchPath,
  createAppLaunchDispatcher,
  parseAppLaunchIntent,
  shouldRestoreActiveDraft,
  subscribeToWebLaunches,
  type AppLaunchIntent,
} from './navigation/appLaunchIntent'
import { getSupabaseConfig } from './supabase/config'
import { getSupabaseClient } from './supabase/client'
import { SyncCoordinator } from './sync/syncCoordinator'
import { createSyncTransport } from './sync/syncTransport'
import { subscribeToSyncInvalidations } from './sync/syncRealtime'
import {
  SyncRuntime,
  emptySyncCycleResult,
  type SyncCycleResult,
  type SyncDomain,
  type SyncRequest,
} from './sync/syncRuntime'
import { SyncTabCoordinator } from './sync/syncTabCoordinator'
import {
  acceptedSynchronizedNoteUpdates,
  draftMatchesNote,
  mergeSynchronizedNotes,
  recoveryNoteForExplicitCapture,
  shouldPersistActiveDraft,
  synchronizedDraftReplacement,
} from './sync/activeNoteSync'
import { copyText } from './utils/clipboard'
import { dismissComposer } from './utils/composerTransition'
import { editorInitialFocusForEntry, shouldAutofocusExistingNote, shouldRestoreBrowseInputFocus } from './utils/editorEntryFocus'
import {
  browseViewportActionForScopeChange,
  captureBrowseViewport,
  restoreBrowseViewportWhenReady,
  type BrowseViewportSnapshot,
} from './utils/browseViewport'
import { createId } from './utils/id'
import { installMeasuredMasonryLayout } from './utils/masonryLayout'
import {
  EMPTY_EDITOR_FORMAT_STATE,
  type EditorFormatState,
} from './utils/editorDom'
import {
  beginOAuthHistoryNavigation,
  cancelOAuthHistoryNavigation,
  completeOAuthHistoryNavigation,
  hasOAuthCallbackParameters,
} from './utils/oauthHistory'
import { installVisualViewportCssVariables, revealFocusedLine } from './utils/visualViewport'
import {
  createEditorHistory,
  recordEditorChange,
  redoEditorChange,
  undoEditorChange,
  type EditorChangeKind,
} from './utils/markdownEditing'
import { applyUiSize } from './utils/uiSize'
import {
  previewDeviceOverrideLabel,
  readPreviewDeviceOverride,
  writePreviewDeviceOverride,
  type PreviewDeviceOverride,
} from './utils/previewDeviceOverride'
import {
  measureDiagnosticActivity,
  recordDiagnosticMilestone,
  setPerformanceDiagnosticsEnabled,
} from './utils/performanceDiagnostics'
import {
  getComposerContinuityStatus,
  getContinuityStatus,
  nextSyncFailureState,
  scheduleStallNotice,
  shouldExposeSyncFailure,
  storedNoteSaveState,
  type AccountRestorePhase,
  type BackgroundSyncState,
  type SyncFailureState,
} from './utils/syncStatus'
import { applyRestoreCycleEvidence } from './sync/restoreReconciliation'
import {
  reconcileRestoreInventory,
  synchronizedTrashNotesMissingFromRemote,
} from './sync/restoreInventory'
import { getNativePlatform } from './utils/nativeRuntime'
import {
  getNativeIntentInbox,
  offerValidatedNativeIntents,
} from './native/nativeIntentInbox'
import { createInitialWidgetRoutePriorityGate } from './native/initialWidgetRoutePriority'
import { revealPaintedNativeNote } from './native/nativeInitialLaunch'
import {
  nativeQuickAccessCredentialPolicy,
  nativeQuickAccessCredentialSessionPolicy,
} from './native/nativeQuickAccessCredentialPolicy'
import {
  deriveNativeAccountWorkspaceBinding,
  nativeEditorImagePayload,
  nativeCaptureText,
  isNativeShareKind,
  nativeOpenNoteId,
  nativeSharePayload,
  nativeSearchQuery,
  type NativeIntentEnvelope,
} from './native/nativeIntentProtocol'

type AppMode = 'capture' | 'search'
type SearchWindowState = { key: string; count: number }
type AppUpdatePhase = 'idle' | 'checking' | 'current' | 'available' | 'applying' | 'error'

interface AppUpdateState {
  phase: AppUpdatePhase
  deployedBuildId: string | null
  prompt: boolean
}

interface SearchReturnState {
  query: string
  filters: SearchFilters
  filtersOpen: boolean
  navigationIndex: number | null
  activeResultId: string | null
  window: SearchWindowState
  viewport: BrowseViewportSnapshot
}
type SaveState = 'ready' | 'local' | 'error'

type NoteRoutePhase =
  | 'none'
  | 'resolving'
  | 'open'
  | 'sign-in'
  | 'offline'
  | 'not-found'
  | 'error'

interface AppProps {
  initialDeviceId: string
  initialNativeLaunchIntent?: AppLaunchIntent | null
  initialUiSize: UiSize
}

interface PendingNativeReminderAction {
  action: 'acknowledge' | 'snooze'
  noteId: string
  occurrenceAt: number | null
}

const repository = new NoteRepository()
const linkMetadataProvider = createLinkMetadataProvider()
const supabaseConfig = getSupabaseConfig()
function acceptsWorkspaceSession(session: AuthSession | null): boolean {
  try { assertWorkspaceIdentity(supabaseConfig ? session?.user.id ?? null : null); return true }
  catch { return false }
}
const syncTransport = createSyncTransport()
const syncCoordinator =
  syncTransport && supabaseConfig
    ? new SyncCoordinator(repository, syncTransport, supabaseConfig.environment)
    : null
const assetTransport = createAssetTransport()
const loadFullImage = (asset: NoteAsset): Promise<Blob> => assetTransport
  ? downloadAssetMaster(assetTransport, asset)
  : Promise.reject(new Error('Full image download is unavailable offline.'))
const imageQueue = new ImageQueue(repository, new ImageWorkerProcessor(), assetTransport)
const NOTE_BATCH_SIZE = 50
const COMPOSER_EXIT_MS = 110
const HISTORY_GUARD_KEY = 'minimanyEscapeGuard'
const APP_UPDATE_CHECKS_ENABLED = import.meta.env.PROD
const PREVIEW_DEVICE_OVERRIDES_ENABLED = import.meta.env.DEV
const DEVELOPER_TOOLS_AVAILABLE = developerToolsAvailable(import.meta.env.DEV, import.meta.env.VITE_APP_ENV)
const MarkdownNotesImport = DEVELOPER_TOOLS_AVAILABLE ? lazy(() => import('./intelligence/devTools/MarkdownNotesImport')) : null
const AddToContextDialog = lazy(() => import('./intelligence/contextLab/AddToContextDialog'))
const NoteContextLinks = lazy(() => import('./intelligence/smartPilot/NoteContextLinks'))
const ContextLab = lazy(() => import('./intelligence/contextLab/ContextLab'))
const LinksLibrary = lazy(() => import('./links/library/LinksLibrary'))
const SignInStrip = lazy(() => import('./welcome/SignInStrip').then(module => ({ default: module.SignInStrip })))
const WelcomePage = lazy(() => import('./welcome/WelcomePage').then(module => ({ default: module.WelcomePage })))
const ContextStrip = lazy(() => import('./intelligence/contextLab/ContextLab').then((module) => ({
  default: module.ContextStrip,
})))
const APP_UPDATE_REMINDER_HOURS = APP_SETTINGS.appUpdates.reminderSnoozeMs / (60 * 60 * 1_000)
const SignOutDialog = lazy(() => import('./auth/SignOutDialog').then(module => ({ default: module.SignOutDialog })))
const DeleteAccountDialog = lazy(() => import('./auth/DeleteAccountDialog').then(module => ({ default: module.DeleteAccountDialog })))
const DeleteDeviceDataDialog = lazy(() => import('./auth/DeleteDeviceDataDialog').then(module => ({ default: module.DeleteDeviceDataDialog })))
const AdminWorkspace = lazy(() => import('./admin/AdminWorkspace'))
const CleanupPanel = lazy(() => import('./screenshotCleanup/CleanupPanel').then(module => ({ default: module.CleanupPanel })))
const SearchComparison = lazy(() => import('./intelligence/smartPilot/SearchComparison'))
const FeatureInvitation = lazy(() => import('./admin/FeatureInvitation'))
const BROWSE_PRESENTATION_STYLES = browsePresentationCss(DEFAULT_BROWSE_PRESENTATION)

function waitForComposerExit(): Promise<void> {
  return new Promise((resolve) => window.setTimeout(resolve, COMPOSER_EXIT_MS))
}

function waitForNativeHandoffDelay(delayMs: number): Promise<void> {
  return new Promise((resolve) => window.setTimeout(resolve, delayMs))
}

function waitForNativeHandoffFrame(): Promise<void> {
  return new Promise((resolve) => requestAnimationFrame(() => resolve()))
}

function nativeIntentErrorCode(error: unknown): string | null {
  if (typeof error !== 'object' || error === null || !('code' in error)) return null
  return typeof error.code === 'string' ? error.code : null
}

function modeForLaunchIntent(intent: AppLaunchIntent): AppMode {
  return intent.kind === 'search' || intent.kind === 'open-context' ? 'search' : 'capture'
}

function noteIdForLaunchIntent(intent: AppLaunchIntent): string | null {
  return intent.kind === 'open-note' ? intent.noteId : null
}

function shareBodyForLaunchIntent(intent: AppLaunchIntent): string {
  return intent.kind === 'capture' ? intent.sharedBody : ''
}

function initialDraft(intent: AppLaunchIntent): ActiveDraft {
  const draft = createBlankDraft()
  const body = shareBodyForLaunchIntent(intent)
  if (!body) return draft
  return { ...draft, body, updatedAt: Date.now() }
}

function updateNoteCollection(notes: Note[], updated: Note): Note[] {
  const next = notes.filter((note) => note.id !== updated.id)
  next.push(updated)
  return next.sort((left, right) => right.modifiedAt - left.modifiedAt)
}

function linkPreviewAttemptKey(_noteId: string, url: UrlEntity): string {
  return linkMetadataUrlKey(url)
}

function appendOlderNotes(notes: Note[], additions: Note[]): Note[] {
  if (additions.length === 0) return notes
  const existingIds = new Set(notes.map((note) => note.id))
  const uniqueAdditions = additions.filter((note) => !existingIds.has(note.id))
  return uniqueAdditions.length > 0 ? [...notes, ...uniqueAdditions] : notes
}

function hasNonDefaultSearchFilters(filters: SearchFilters): boolean {
  return (
    (filters.status ?? 'all') !== 'all' ||
    Boolean(filters.property) ||
    Boolean(filters.hasAlarm) ||
    Boolean(filters.quickAccess) ||
    Boolean(filters.tag) || Boolean(filters.tags?.length)
  )
}

function quickAccessWidgetPayload(notes: Iterable<Note>): Array<{ id: string; title: string }> {
  return [...notes]
    .filter((note) => note.quickAccess && note.status !== 'trash')
    .sort((left, right) =>
      (right.quickAccessAt ?? right.modifiedAt) - (left.quickAccessAt ?? left.modifiedAt)
      || right.modifiedAt - left.modifiedAt
      || left.id.localeCompare(right.id))
    .slice(0, APP_SETTINGS.widgets.quickAccessMaxItems)
    .map((note) => ({
      id: note.id,
      title: (note.manualTitle ?? note.derivedTitle).replace(/\s+/g, ' ').trim() || 'Untitled note',
    }))
}

function updateNativeQuickAccessWidget(
  notes: Iterable<Note>,
  completeRefresh = false,
): Promise<void> {
  return updateNativeQuickAccessWidgetPayload(quickAccessWidgetPayload(notes), completeRefresh)
}

function updateNativeQuickAccessWidgetPayload(
  notes: Array<{ id: string; title: string }>,
  completeRefresh = false,
): Promise<void> {
  const plugin = getNativeIntentInbox(window)
  if (!plugin?.setQuickAccessNotes) return Promise.resolve()
  assertWorkspaceActive()
  return plugin.setQuickAccessNotes({
    notes,
    completeRefresh,
    refreshMinimumIndicatorMs: APP_SETTINGS.widgets.quickAccessRefreshMinimumIndicatorMs,
    refreshFeedbackDurationMs: APP_SETTINGS.widgets.quickAccessRefreshFeedbackDurationMs,
  }).catch(() => {
    // The local note assignment remains durable even when the launcher widget is unavailable.
  })
}

async function clearNativeQuickAccessSyncCredentials(clearNotes: boolean): Promise<void> {
  const plugin = getNativeIntentInbox(window)
  await plugin?.clearQuickAccessSyncCredentials?.({ clearNotes }).catch(() => undefined)
}

async function updateNativeQuickAccessSyncCredentials(expectedUserId: string): Promise<void> {
  const plugin = getNativeIntentInbox(window)
  if (!plugin?.setQuickAccessSyncCredentials || !plugin.clearQuickAccessSyncCredentials) return
  const config = getSupabaseConfig()
  if (!config) {
    recordDiagnosticMilestone('quick-access:credentials-preserved', { reason: 'config-unavailable' })
    return
  }
  const retainedCredential = await plugin.getQuickAccessSyncCredentialStatus?.({
    userId: expectedUserId,
    renewalWindowMs: APP_SETTINGS.widgets.quickAccessCredentialRenewalWindowMs,
  }).catch(() => undefined)
  if (retainedCredential?.usable) {
    recordDiagnosticMilestone('quick-access:credentials-current', {
      credentialMode: retainedCredential.credentialMode,
      expiresInMs: Math.max(0, retainedCredential.expiresAtMs - Date.now()),
    })
    return
  }
  const sessionRead = await getSupabaseClient()
    .then(client => client.auth.getSession())
    .catch(() => null)
  if (!sessionRead) {
    recordDiagnosticMilestone('quick-access:credentials-preserved', { reason: 'session-read-failed' })
    if (config.isDev) console.warn('[miniMany][quick-access-sync] credential session read failed; prior shadow preserved')
    return
  }
  const { data, error } = sessionRead
  const session = data.session
  const sessionAction = nativeQuickAccessCredentialSessionPolicy({
    expectedUserId,
    sessionUserId: session?.user.id ?? null,
    hasAccessToken: Boolean(session?.access_token),
    readFailed: Boolean(error),
  })
  if (sessionAction === 'preserve' || !session) {
    recordDiagnosticMilestone('quick-access:credentials-preserved', {
      reason: error ? 'session-error' : 'session-inconclusive',
    })
    if (config.isDev) console.warn('[miniMany][quick-access-sync] credential session inconclusive; prior shadow preserved')
    return
  }
  let credentialUpdateStage: 'session-request' | 'native-save' = 'session-request'
  try {
    const apiBaseUrl = resolveLinkMetadataApiBaseUrl({
      appEnvironment: config.environment,
      configuredBaseUrl: import.meta.env.VITE_API_BASE_URL,
      nativePlatform: getNativePlatform(window),
    })
    if (!apiBaseUrl) throw new Error('Quick Access API origin is unavailable.')
    const controller = new AbortController()
    const timeout = globalThis.setTimeout(
      () => controller.abort(),
      APP_SETTINGS.widgets.quickAccessRefreshNetworkTimeoutMs,
    )
    let response: Response
    try {
      response = await fetch(new URL('/api/widgets/quick-access/session', apiBaseUrl), {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${session.access_token}`,
          'Content-Type': 'application/json',
        },
        body: '{}',
        cache: 'no-store',
        signal: controller.signal,
      })
    } finally {
      globalThis.clearTimeout(timeout)
    }
    if (!response.ok || !response.headers.get('Content-Type')?.includes('application/json')) {
      throw new Error(`Quick Access credential request failed with ${response.status}.`)
    }
    const credential = await response.json() as {
      schemaVersion?: unknown
      credential?: unknown
      expiresAtMs?: unknown
      userId?: unknown
      isDev?: unknown
      projectionUrl?: unknown
    }
    const projectionUrl = typeof credential.projectionUrl === 'string'
      ? new URL(credential.projectionUrl)
      : null
    if (
      credential.schemaVersion !== 1
      || credential.userId !== expectedUserId
      || credential.isDev !== config.isDev
      || typeof credential.credential !== 'string'
      || credential.credential.length < 64
      || credential.credential.length > 4_096
      || typeof credential.expiresAtMs !== 'number'
      || credential.expiresAtMs <= Date.now()
      || !projectionUrl
      || projectionUrl.protocol !== 'https:'
      || projectionUrl.origin !== new URL(apiBaseUrl).origin
      || projectionUrl.pathname !== '/api/widgets/quick-access'
      || projectionUrl.search !== ''
      || projectionUrl.hash !== ''
    ) throw new Error('Quick Access credential response is invalid.')
    assertWorkspaceIdentity(expectedUserId)
    credentialUpdateStage = 'native-save'
    await plugin.setQuickAccessSyncCredentials({
      userId: expectedUserId,
      projectionUrl: projectionUrl.toString(),
      widgetCredential: credential.credential,
      expiresAtMs: credential.expiresAtMs,
      isDev: config.isDev,
      backgroundRefreshIntervalMinutes: APP_SETTINGS.widgets.quickAccessBackgroundRefreshIntervalMinutes,
      networkTimeoutMs: APP_SETTINGS.widgets.quickAccessRefreshNetworkTimeoutMs,
      maxNetworkAttempts: APP_SETTINGS.widgets.quickAccessRefreshMaxNetworkAttempts,
      retryDelayMs: APP_SETTINGS.widgets.quickAccessRefreshRetryDelayMs,
      minimumIndicatorMs: APP_SETTINGS.widgets.quickAccessRefreshMinimumIndicatorMs,
      successIndicatorMs: APP_SETTINGS.widgets.quickAccessRefreshFeedbackDurationMs,
      failureIndicatorMs: APP_SETTINGS.widgets.quickAccessRefreshFailureFeedbackDurationMs,
      reconnectMinRejectedAttempts: APP_SETTINGS.widgets.quickAccessReconnectMinRejectedAttempts,
      reconnectGraceMs: APP_SETTINGS.widgets.quickAccessReconnectGraceMs,
      backgroundRepairBackoffMs: APP_SETTINGS.widgets.quickAccessBackgroundRepairBackoffMs,
      backgroundRepairMaxAttempts: APP_SETTINGS.widgets.quickAccessBackgroundRepairMaxAttempts,
    })
    const retained = await plugin.getQuickAccessSyncDiagnostics?.().catch(() => undefined)
    recordDiagnosticMilestone('quick-access:credentials-refreshed', retained ? {
      retainedOutcome: retained.outcome,
      retainedStage: retained.stage,
      retainedCode: retained.code,
      retainedFailures: retained.consecutiveFailures,
    } : undefined)
    if (config.isDev) console.info('[miniMany][quick-access-sync] credential shadow refreshed', retained ?? {})
  } catch (error) {
    recordDiagnosticMilestone('quick-access:credentials-preserved', {
      reason: `${credentialUpdateStage}-failed`,
      errorType: error instanceof Error ? error.name : typeof error,
    })
    if (config.isDev) console.warn(
      '[miniMany][quick-access-sync] credential update delayed; prior scoped credential preserved',
      { stage: credentialUpdateStage, errorType: error instanceof Error ? error.name : typeof error },
    )
  }
}

function isDesktopBrowseRuntime(): boolean {
  return document.documentElement.dataset.viewportFormFactor === 'desktop'
}

function isDesktopPointerRuntime(): boolean {
  const root = document.documentElement
  return root.dataset.viewportFormFactor === 'desktop'
    && root.dataset.viewportInputMode === 'pointer'
}

export function App({
  initialDeviceId,
  initialNativeLaunchIntent = null,
  initialUiSize,
}: AppProps) {
  const initialLaunchIntentRef = useRef<AppLaunchIntent>(
    initialNativeLaunchIntent
      ?? parseAppLaunchIntent(window.location.href, window.location.origin, readBootstrapStartMode())!,
  )
  const initialLaunchIntent = initialLaunchIntentRef.current
  const restoreInitialActiveDraft = shouldRestoreActiveDraft(initialLaunchIntent)
  const initialWidgetRoutePriorityGateRef = useRef<ReturnType<typeof createInitialWidgetRoutePriorityGate> | null>(null)
  if (!initialWidgetRoutePriorityGateRef.current) {
    initialWidgetRoutePriorityGateRef.current = createInitialWidgetRoutePriorityGate(
      initialNativeLaunchIntent?.kind === 'open-note',
      APP_SETTINGS.nativeIntents.initialWidgetRoutePriorityBudgetMs,
    )
  }
  const initialWidgetRoutePriorityGate = initialWidgetRoutePriorityGateRef.current
  const studio = useStudioDesign()
  // A desktop keeps a note's quick actions in its bar; a phone keeps them in the note's ⋯ (apps/app/DESIGN.md, "A note's bar").
  const desktopNote = studio.runtime.formFactor === 'desktop'
  const studioActive = studio.desktopLayout
  const androidMobileShell = !studioActive && studio.runtime.platform === 'android'
  const workspaceNavigationDrawer = studioActive || studio.runtime.formFactor === 'phone'
  const [studioNavigationBusy, setStudioNavigationBusy] = useState(false)
  const [mobileSidebarOpen, setMobileSidebarOpen] = useState(false)
  const [mode, setMode] = useState<AppMode>(() => modeForLaunchIntent(initialLaunchIntent))
  const [draft, setDraft] = useState<ActiveDraft>(() =>
    initialDraft(initialLaunchIntent),
  )
  const [notes, setNotes] = useState<Note[]>([])
  const [contextStripVisible, setContextStripVisible] = useState(true)
  const [contextLabSelectedContextId, setContextLabSelectedContextId] = useState<string | null>(() => initialLaunchIntent.kind === 'open-context' ? initialLaunchIntent.contextId : null)
  const [contextCollectionOpen, setContextCollectionOpen] = useState(initialLaunchIntent.kind === 'search' && Boolean(initialLaunchIntent.collection))
  // The Links screen (links/library/): a full-screen section of its own, opened from the sidebar.
  const [linksOpen, setLinksOpen] = useState(() => libraryEnabled() && readLinksRoute())
  const [contextLabBrowseOverride, setContextLabBrowseOverride] = useState(initialLaunchIntent.kind !== 'open-context' && !(initialLaunchIntent.kind === 'search' && initialLaunchIntent.collection))
  const [assets, setAssets] = useState<NoteAsset[]>([])
  const [remoteAssetUrls, setRemoteAssetUrls] = useState<Map<string, string>>(new Map())
  const [imageDropActive, setImageDropActive] = useState(false)
  const [pendingImageRevealId, setPendingImageRevealId] = useState<string | null>(null)
  const [revealedImageId, setRevealedImageId] = useState<string | null>(null)
  const [saveState, setSaveState] = useState<SaveState>('ready')
  const [tagToolbarOpen, setTagToolbarOpen] = useState(false)
  const [reminderEditorOpen, setReminderEditorOpen] = useState(false)
  const [pendingNativeReminderAction, setPendingNativeReminderAction] = useState<PendingNativeReminderAction | null>(null)
  const [nativeReminderSnoozeNoteId, setNativeReminderSnoozeNoteId] = useState<string | null>(null)
  const [searchQuery, setSearchQuery] = useState('')
  const [searchFilters, setSearchFilters] = useState<SearchFilters>({ status: 'all' })
  const [searchFiltersOpen, setSearchFiltersOpen] = useState(false)
  const [mobileSearchSurfaceOpen, setMobileSearchSurfaceOpen] = useState(false)
  const [searchTagsExpanded, setSearchTagsExpanded] = useState(false)
  const [searchTagSuggestionsOpen, setSearchTagSuggestionsOpen] = useState(false)
  const [searchTagSuggestionIndex, setSearchTagSuggestionIndex] = useState<number | null>(null)
  const [searchLayout, setSearchLayout] = useState<SearchLayout>(readSearchLayout)
  const [searchSelection, setSearchSelection] = useState(() => createDismissedSearchSelection())
  const [removingSearchNoteIds, setRemovingSearchNoteIds] = useState<Set<string>>(() => new Set())
  const searchSelectionMode = searchSelection.active
  const [addToContextNotes, setAddToContextNotes] = useState<{ scope: string; notes: Note[]; returnToNote?: boolean; suggestedContextIds?: string[] } | null>(null)
  const selectedSearchNoteIds = searchSelection.ids
  const [tagInputOpen, setTagInputOpen] = useState(false)
  const [tagInput, setTagInput] = useState('')
  const [keyboardShortcutsOpen, setKeyboardShortcutsOpen] = useState(false)
  const shortcutsDialogRef = useRef<HTMLDialogElement>(null)
  // The shortcuts sheet leaves through the sheet exit like every other (components/sheetMotion.ts).
  const leaveShortcuts = useCallback(() => leaveSheet(shortcutsDialogRef.current, () => setKeyboardShortcutsOpen(false)), [])
  const [sessionTags, setSessionTags] = useState<string[]>([])
  const [accountPanelOpen, setAccountPanelOpen] = useState(false)
  const [settingsSectionMenuOpen, setSettingsSectionMenuOpen] = useState(false)
  const [studioSettingsSection, setStudioSettingsSection] = useState<StudioSettingsCategory>('appearance')
  const [adminRouteActive, setAdminRouteActive] = useState(readAdminRoute)
  const [comparisonRouteActive, setComparisonRouteActive] = useState(() => window.location.pathname === '/admin/experiments/search')
  const [invitationRouteActive, setInvitationRouteActive] = useState(() => window.location.pathname === '/access/accept')
  const [invitationToken] = useState(() => {
    if (window.location.pathname !== '/access/accept') return ''
    const value = new URLSearchParams(window.location.hash.slice(1)).get('token') || ''
    return /^[a-f0-9-]{36}\.[a-f0-9]{64}$/.test(value) ? value : ''
  })
  useEffect(() => { const update = () => setInvitationRouteActive(window.location.pathname === '/access/accept'); window.addEventListener('popstate', update); return () => window.removeEventListener('popstate', update) }, [])
  const [appUpdate, setAppUpdate] = useState<AppUpdateState>({
    phase: 'idle',
    deployedBuildId: null,
    prompt: false,
  })
  const [appUpdateReminderAt, setAppUpdateReminderAt] = useState(readAppUpdateReminderAt)
  const [isOnline, setIsOnline] = useState(() => navigator.onLine)
  const [storagePersistent, setStoragePersistent] = useState<boolean | null>(null)
  const [localContinuity, setLocalContinuity] = useState<LocalDataContinuitySnapshot | null>(null)
  const [recoveryExportBusy, setRecoveryExportBusy] = useState(false)
  const [recoveryImportBusy, setRecoveryImportBusy] = useState(false)
  const [googleKeepImportOpen, setGoogleKeepImportOpen] = useState(false)
  const [markdownNotesImportOpen, setMarkdownNotesImportOpen] = useState(false)
  const [authSession, setAuthSession] = useState<AuthSession | null>(getInitialIdentity)
  // LocalAuthProvider's placeholder identifies local storage, not a remote account.
  const hasRemoteAccount = authProvider.id !== 'local' && Boolean(authSession)
  const [smartPilotPreferences, setSmartPilotPreferences] = useState(() => readSmartPilotPreferences(authSession?.user.id ?? null))
  const [smartSearchResult, setSmartSearchResult] = useState<{ query: string; result: SmartPilotResponse; contexts: ManualContext[] } | null>(null)
  const [smartSearchSelected, setSmartSearchSelected] = useState(false)
  const [smartSearchBusy, setSmartSearchBusy] = useState(false)
  const [smartSearchError, setSmartSearchError] = useState<string | null>(null)
  const smartSearchSequenceRef = useRef(0)
  const [smartSuggestion, setSmartSuggestion] = useState<{ note: Note; ids: string[]; records: ManualContext[]; flow: DevFlow; placement: 'top' | 'sheet' } | null>(null)
  const [smartSuggestionBusy, setSmartSuggestionBusy] = useState(false)
  // The note to bring into view when a Context opens from "Go to context" on the toast for adding it.
  const [contextFocus, setContextFocus] = useState<{ contextId: string; noteId: string } | null>(null)
  const pendingNewNoteIdsRef = useRef(new Set(initialLaunchIntent.kind === 'capture' && !restoreInitialActiveDraft ? [draft.noteId] : []))
  const pendingNewNoteFlowsRef = useRef(new Map<string, DevFlow>())
  const operationScope = authSession?.user.id ?? 'local'
  const [selectionDialog, setSelectionDialog] = useState<{ kind: 'merge' | 'tags'; notes: Note[]; scope: string } | null>(null)
  const [noteExportDialog, setNoteExportDialog] = useState<{ notes: Note[]; scope: string } | null>(null)
  const [exportBrowseNotice, setExportBrowseNotice] = useState<'hidden' | 'status' | 'settings-hint'>('hidden')
  const [exportRequestPending, setExportRequestPending] = useState(false)
  const [exportRequestError, setExportRequestError] = useState<string | null>(null)
  const exportToastIdsRef = useRef(new Map<string, NotificationId>())
  const [noteOperations, setNoteOperations] = useState<{ scope: string; items: NoteOperation[] }>({ scope: '', items: [] })
  const [operationProgress, setOperationProgress] = useState('')
  const [operationBusy, setOperationBusy] = useState(false)
  const operationLock = useRef(false)
  const operationScopeRef = useRef(operationScope)
  operationScopeRef.current = operationScope
  const visibleOperations = noteOperations.scope === operationScope ? noteOperations.items : []
  const [authRestoring, setAuthRestoring] = useState(authProvider.id !== 'local')
  useEffect(() => {
    setAddToContextNotes(null)
    setNoteExportDialog(null)
    setExportBrowseNotice('hidden')
    setExportRequestPending(false)
    setExportRequestError(null)
    exportToastIdsRef.current.clear()
    setSmartPilotPreferences(readSmartPilotPreferences(authSession?.user.id ?? null))
    setSmartSearchResult(null)
    setSmartSearchSelected(false)
    setSmartSuggestion(null)
    smartSearchSequenceRef.current++
  }, [authSession?.user.id])
  useEffect(() => {
    if (exportBrowseNotice !== 'settings-hint') return
    const timer = window.setTimeout(
      () => setExportBrowseNotice('hidden'),
      APP_SETTINGS.exports.notes.dismissRedirectHintMs,
    )
    return () => window.clearTimeout(timer)
  }, [exportBrowseNotice])
  const contextsOwner = authSession?.user.id ?? null
  const [contextsPreference, setContextsPreference] = useState(() => ({ owner: contextsOwner, enabled: readContextsPreference(contextsOwner) }))
  const contextsRequested = contextsPreference.owner === contextsOwner && contextsPreference.enabled
  useEffect(() => {
    const refresh = () => setContextsPreference({ owner: contextsOwner, enabled: readContextsPreference(contextsOwner) })
    refresh()
    window.addEventListener('storage', refresh)
    window.addEventListener('focus', refresh)
    return () => { window.removeEventListener('storage', refresh); window.removeEventListener('focus', refresh) }
  }, [contextsOwner])
  const [developerAccess, setDeveloperAccess] = useState<'hidden' | 'checking' | 'admin'>('hidden')
  const [developerAccessUserId, setDeveloperAccessUserId] = useState<string | null>(null)
  const developerAuthorized = developerAccessVerified(
    developerAccess, authSession?.user.id, developerAccessUserId,
  )
  // Screenshot Clean-up (docs/screenshot-cleanup/PLAN.md) is a developer feature: verified admin plus this device's switch.
  const [cleanupPreference, setCleanupPreference] = useState(() => ({ owner: contextsOwner, enabled: readCleanupPreference(contextsOwner) }))
  useEffect(() => { setCleanupPreference({ owner: contextsOwner, enabled: readCleanupPreference(contextsOwner) }) }, [contextsOwner])
  const cleanupRequested = cleanupPreference.owner === contextsOwner && cleanupPreference.enabled
  const cleanupEnabled = cleanupAvailable(developerAuthorized, cleanupRequested)
  // What is already unpacked on this device shows offline too (screenshotCleanup/gate.ts): the last admin verdict stands in unless an online check denied it.
  const [developerAccessDeniedFor, setDeveloperAccessDeniedFor] = useState<string | null>(null)
  const rememberedAdmin = useMemo(() => readRememberedAdmin(contextsOwner), [contextsOwner, developerAccess, developerAccessUserId]) // eslint-disable-line react-hooks/exhaustive-deps
  const cleanupShownEnabled = developerFeatureShown(cleanupRequested, developerAuthorized, developerAccessDeniedFor !== null && developerAccessDeniedFor === contextsOwner, rememberedAdmin)
  const [unpackPreferences, setUnpackPreferences] = useState(() => ({ owner: contextsOwner, value: readUnpackPreferences(contextsOwner) }))
  useEffect(() => { setUnpackPreferences({ owner: contextsOwner, value: readUnpackPreferences(contextsOwner) }) }, [contextsOwner])
  function changeUnpackPreference(name: keyof UnpackPreferences, enabled: boolean) {
    if (!contextsOwner) return
    const value = { ...unpackPreferences.value, [name]: enabled }
    try { writeUnpackPreferences(contextsOwner, value); setUnpackPreferences({ owner: contextsOwner, value }) } catch { void notify.error('Could not save this device setting.') }
  }
  function changeCleanupPreference(enabled: boolean) {
    if (!developerAuthorized || !contextsOwner) return
    try {
      writeCleanupPreference(contextsOwner, enabled)
      setCleanupPreference({ owner: contextsOwner, enabled })
    } catch { void notify.error('Could not save this device setting.') }
  }
  // Images just shared into or added to a note: Unpack starts by itself once they are all in it (screenshotCleanup/CleanupPanel.tsx).
  // `text`: a message shared as text, which Unpack reads when it is a booking (screenshotCleanup/sharedText.ts).
  const [cleanupAuto, setCleanupAuto] = useState<{ noteId: string; assetIds: string[]; text?: boolean } | null>(null)
  // While an unpacked shared message is still exactly as shared, its text folds under the card until the person writes or asks for it.
  const [sharedTextFolds, setSharedTextFolds] = useState(false)
  const [sharedTextShown, setSharedTextShown] = useState<string | null>(null)
  // Unpack's entry in the note's ⋯ menu (Unpack again, or Unpack), from the panel that knows the note's runs; the cards have their own ⋯.
  const [unpackMenu, setUnpackMenu] = useState<UnpackMenuEntry[]>([])
  // The note whose screenshots Unpack is reading right now: its image stage shows the scan (design/studio/screenshot-cleanup.css).
  const [unpackReading, setUnpackReading] = useState<string | null>(null)
  // The note's ⋯: tags, Copy, Archive and Trash on every device, and on a phone also Quick Access and the reminder.
  const [noteMenuOpen, setNoteMenuOpen] = useState(false)
  const noteMenuButtonRef = useRef<HTMLButtonElement>(null)
  // Per note on this device for this session: the clean card in the images' place, or the images.
  const [cleanupViews, setCleanupViews] = useState<ReadonlyMap<string, 'clean' | 'original'>>(() => new Map())
  // A cleaned note's own picture (a dish, a chart) replaces its screenshots on its card in Browse.
  const [cleanupCovers, setCleanupCovers] = useState<ReadonlyMap<string, NoteCover>>(() => new Map())
  // An unpacked note's kind and title, shown on its card in Browse (screenshotCleanup/card.ts).
  const [cleanupCards, setCleanupCards] = useState<ReadonlyMap<string, UnpackedCard>>(() => new Map())
  const cleanupCardsRef = useRef(cleanupCards)
  cleanupCardsRef.current = cleanupCards
  const setCleanupCard = useCallback((noteId: string, card: UnpackedCard | null) => {
    setCleanupCards(current => {
      if (sameCard(current.get(noteId), card)) return current
      // What the screenshots say is searchable like the note's own words (search/noteSearch.ts).
      searchIndex.setUnpacked(noteId, card?.words ?? null)
      startTransition(() => setSearchIndexRevision(value => value + 1))
      const next = new Map(current)
      if (card) next.set(noteId, card)
      else next.delete(noteId)
      return next
    })
  }, [])
  const setCleanupCover = useCallback((noteId: string, cover: NoteCover | null) => {
    setCleanupCovers(current => {
      if (cover ? current.get(noteId)?.assetId === cover.assetId && current.get(noteId)?.box?.join() === cover.box?.join() : !current.has(noteId)) return current
      const next = new Map(current)
      if (cover) next.set(noteId, cover)
      else next.delete(noteId)
      return next
    })
  }, [])
  const cleanupWorkspace = cleanupShownEnabled ? activeWorkspace()?.databaseName ?? null : null
  const cleanupOwner = authSession?.user.id ?? null
  useEffect(() => {
    if (!cleanupWorkspace) { setCleanupCovers(new Map()); setCleanupCards(new Map()); return }
    let live = true
    // Loaded apart from the main bundle: only a developer with Clean up on needs it. This device's runs show at once; then the account's
    // newest runs from every device are fetched, so a note cleaned up elsewhere shows the same picture here without being opened first.
    // The note view's Unpack code is fetched now too, so an unpacked note opens straight on its content, with no empty moment.
    void import('./screenshotCleanup/CleanupPanel').catch(() => undefined)
    void Promise.all([import('./screenshotCleanup/runs'), import('./screenshotCleanup/covers'), import('./screenshotCleanup/sync'), import('./screenshotCleanup/card')])
      .then(async ([{ listRuns }, { coversByNote }, { accountRemote, syncRuns }, { cardsByNote }]) => {
        const show = (runs: Awaited<ReturnType<typeof listRuns>>) => {
          if (!live) return
          const cards = cardsByNote(runs)
          setCleanupCovers(coversByNote(runs))
          setCleanupCards(cards)
          searchIndex.loadUnpacked(new Map([...cards].map(([noteId, card]) => [noteId, card.words])))
          startTransition(() => setSearchIndexRevision(value => value + 1))
        }
        show(await listRuns(cleanupWorkspace))
        if (!cleanupOwner) return
        await syncRuns(cleanupWorkspace, accountRemote(cleanupOwner), null)
        show(await listRuns(cleanupWorkspace))
      })
      .catch(() => undefined)
    return () => { live = false }
  }, [cleanupWorkspace, cleanupOwner])
  const chooseCleanupView = useCallback((noteId: string, view: 'clean' | 'original') => {
    setCleanupViews(current => (current.get(noteId) === view ? current : new Map(current).set(noteId, view)))
  }, [])
  // Contexts is an experiment: the setting never grants access without the current server-verified admin.
  const contextSyncOwnerRef = useRef<string | null>(null)
  // Contexts already on this device show offline too (screenshotCleanup/gate.ts); syncing and the Context routes wait for the live check, or for no connection at all.
  const contextsEnabled = developerFeatureShown(contextsRequested, developerAuthorized, developerAccessDeniedFor !== null && developerAccessDeniedFor === contextsOwner, rememberedAdmin)
  contextSyncOwnerRef.current = contextsEnabled && (developerAuthorized || !isOnline) ? contextsOwner : null
  const contextsLive = developerAuthorized && contextsRequested
  const devToolsAllowed = developerAuthorized
  const smartSearchEnabled = devToolsAllowed && smartPilotPreferences.search
  const smartSuggestionsEnabled = contextsLive && smartPilotPreferences.suggestions
  const smartSuggestionsEnabledRef = useRef(smartSuggestionsEnabled)
  smartSuggestionsEnabledRef.current = smartSuggestionsEnabled
  useEffect(() => {
    const owner = devToolsAllowed ? authSession?.user.id : null
    if (!owner) return
    void flushDevFlowEvents(owner)
    const retry = () => void flushDevFlowEvents(owner)
    window.addEventListener('online', retry)
    return () => window.removeEventListener('online', retry)
  }, [authSession?.user.id, devToolsAllowed])
  function changeSmartPilotPreference(key: keyof SmartPilotPreferences, value: boolean) {
    const owner = authSession?.user.id
    if (!devToolsAllowed || !owner) return
    const next = { ...smartPilotPreferences, [key]: value }
    try {
      writeSmartPilotPreferences(owner, next)
      setSmartPilotPreferences(next)
      if (!value && key === 'search') { smartSearchSequenceRef.current++; setSmartSearchResult(null); setSmartSearchError(null); setSmartSearchBusy(false) }
      if (!value && key === 'suggestions') setSmartSuggestion(null)
    }
    catch { void notify.error('Could not save this device setting.') }
  }
  const invitationVisible = invitationRouteActive
  const contextLabOpen = contextsEnabled && (contextLabSelectedContextId !== null || contextCollectionOpen) && !contextLabBrowseOverride
  const contextLabVisible = contextLabOpen && mode === 'search'
  const linksVisible = libraryEnabled() && linksOpen && mode === 'search'
  // Either full-screen section covers Browse, which then neither takes focus, scroll nor keys.
  const browseCovered = contextLabVisible || linksVisible
  useEffect(() => {
    if (contextsEnabled) return
    setAddToContextNotes(null)
    setSmartSuggestion(null)
    // Invalidate a pending smart-search response so it cannot restore Context results after switch-off.
    smartSearchSequenceRef.current++
    setSmartSearchResult(null)
    setSmartSearchBusy(false)
    if (!authRestoring && contextsPreference.owner === contextsOwner && (!contextsRequested || (developerAccess === 'hidden' && developerAccessUserId === contextsOwner))) {
      setContextLabSelectedContextId(null)
      setContextCollectionOpen(false)
      setContextLabBrowseOverride(true)
      if (readContextRoute() || readContextCollectionRoute()) clearNoteRoute()
    }
    if (studioSettingsSection === 'contexts') setStudioSettingsSection('appearance')
  }, [contextsEnabled, contextsRequested, contextsOwner, contextsPreference.owner, authRestoring, developerAccess, developerAccessUserId, contextLabSelectedContextId, contextCollectionOpen, studioSettingsSection])
  function changeContextsPreference(enabled: boolean) {
    if (!developerAuthorized || !contextsOwner) return
    try {
      writeContextsPreference(contextsOwner, enabled)
      setContextsPreference({ owner: contextsOwner, enabled })
    } catch { void notify.error('Could not save this device setting.') }
  }
  useEffect(() => {
    if (contextLabVisible && contextLabSelectedContextId) replaceContextRoute(contextLabSelectedContextId)
    else if (contextLabVisible && contextCollectionOpen) replaceContextCollectionRoute()
    else if ((readContextRoute() || readContextCollectionRoute()) && (contextLabBrowseOverride || mode === 'capture')) clearNoteRoute()
  }, [contextLabVisible, contextCollectionOpen, contextLabSelectedContextId, contextLabBrowseOverride, mode])
  useEffect(() => {
    if (linksVisible) replaceLinksRoute()
    else if (readLinksRoute() && (!linksOpen || mode === 'capture')) clearNoteRoute()
  }, [linksVisible, linksOpen, mode])

  const [accountAccess, setAccountAccess] = useState<AccountAccess | null>(null)
  const [accountAccessError, setAccountAccessError] = useState(false)
  const [accountAccessRefreshKey, setAccountAccessRefreshKey] = useState(0)
  const [authBusy, setAuthBusy] = useState(false)
  const [authError, setAuthError] = useState<string | null>(null)
  const [backgroundSyncState, setBackgroundSyncState] = useState<BackgroundSyncState>('idle')
  const [quickAccessRefreshRequest, setQuickAccessRefreshRequest] = useState(0)
  const [accountRestorePhase, setAccountRestorePhase] = useState<AccountRestorePhase>('idle')
  const [syncError, setSyncError] = useState<string | null>(null)
  const [pendingSyncVisible, setPendingSyncVisible] = useState(false)
  const [noteSyncAttempts, setNoteSyncAttempts] = useState<Map<string, number>>(() => new Map())
  const [uploadStatusClock, setUploadStatusClock] = useState(Date.now)
  const [copiedNoteId, setCopiedNoteId] = useState<string | null>(null)
  const [historyAvailability, setHistoryAvailability] = useState({ canUndo: false, canRedo: false })
  const [editorFormatState, setEditorFormatState] = useState<EditorFormatState>(
    EMPTY_EDITOR_FORMAT_STATE,
  )
  const [composerClosing, setComposerClosing] = useState(false)
  const [composerKey, setComposerKey] = useState(0)
  const [composerInstantEntry, setComposerInstantEntry] = useState(() =>
    captureEntryIsInstant(initialLaunchIntent),
  )
  // Widget routing needs the active draft, because switching away must first be durable,
  // but it must not wait for assets, settings, or the first browse page to finish loading.
  const [nativeIntentReady, setNativeIntentReady] = useState(false)
  const [searchWindow, setSearchWindow] = useState<SearchWindowState>({ key: '', count: NOTE_BATCH_SIZE })
  const [searchNavigationIndex, setSearchNavigationIndex] = useState<number | null>(null)
  const [activeImagePreview, setActiveImagePreview] = useState<ImagePreview | null>(null)
  const [loaded, setLoaded] = useState(false)
  useEffect(() => {
    if (!loaded) return
    let active = true
    void repository.listNoteOperationReceipts(operationPrefix(operationScope)).then(records => {
      if (active) setNoteOperations({ scope: operationScope, items: records.map(record => JSON.parse(record) as NoteOperation).sort((a, b) => b.createdAt - a.createdAt) })
    }).catch(() => { if (active) void notify.error('Could not load the actions log') })
    return () => { active = false }
  }, [loaded, operationScope])
  const [browseInventoryPhase, setBrowseInventoryPhase] = useState<BrowseInventoryPhase>('loading')
  const [requestedNoteId, setRequestedNoteId] = useState(() => noteIdForLaunchIntent(initialLaunchIntent))
  const [noteRoutePhase, setNoteRoutePhase] = useState<NoteRoutePhase>(
    noteIdForLaunchIntent(initialLaunchIntent) ? 'resolving' : 'none',
  )
  const presentedNoteRouteRef = useRef<string | null>(null)
  presentedNoteRouteRef.current = mode === 'capture' && noteRoutePhase === 'open' && draft.noteId === requestedNoteId
    ? requestedNoteId : null
  const [noteRouteLocalChecked, setNoteRouteLocalChecked] = useState(false)
  const [noteRouteLocalFound, setNoteRouteLocalFound] = useState(false)
  const [noteRouteResolveAttempt, setNoteRouteResolveAttempt] = useState(0)
  const googleKeepImports = useGoogleKeepImports(Boolean(authSession))
  const noteExports = useNoteExports(authSession?.user.id ?? null)
  const [deviceId] = useState(initialDeviceId)
  const [globalSettings, setGlobalSettings] = useState<GlobalSettings>(() =>
    createDefaultGlobalSettings(deviceId),
  )
  const [deviceSettings, setDeviceSettings] = useState<DeviceSettings>(() =>
    createDefaultDeviceSettings(
      deviceId,
      detectDefaultDeviceName(navigator.userAgent, navigator.maxTouchPoints, deviceId),
      Date.now(),
      initialUiSize,
    ),
  )
  const [deviceNameDraft, setDeviceNameDraft] = useState(() =>
    detectDefaultDeviceName(navigator.userAgent, navigator.maxTouchPoints, deviceId),
  )
  const [previewDeviceOverride, setPreviewDeviceOverride] = useState<PreviewDeviceOverride>(
    readPreviewDeviceOverride,
  )
  const [searchIndexRevision, setSearchIndexRevision] = useState(0)
  const [linkPreviewRetryRevision, setLinkPreviewRetryRevision] = useState(0)
  const [linkPreviewStatuses, setLinkPreviewStatuses] = useState<Map<string, LinkMetadataStatus>>(
    () => new Map(),
  )
  const [searchIndex] = useState(() => new DeferredNoteSearchIndex(() => {
    recordDiagnosticMilestone('search:index-ready')
    startTransition(() => setSearchIndexRevision((current) => current + 1))
  }))
  const editorRef = useRef<MarkdownEditorHandle>(null)
  const restoreEditorSelectionFrameRef = useRef<number | null>(null)
  const composerRef = useRef<HTMLElement>(null)
  const composerScrollRegionRef = useRef<HTMLDivElement>(null)
  const imageInputRef = useRef<HTMLInputElement>(null)
  const recoveryImportInputRef = useRef<HTMLInputElement>(null)
  const searchRef = useRef<HTMLInputElement>(null)
  const filterToggleRef = useRef<HTMLButtonElement>(null)
  const searchFiltersViewportRef = useRef<BrowseViewportSnapshot | null>(null)
  const searchFabRef = useRef<HTMLButtonElement>(null)
  const searchControlsRef = useRef<HTMLDivElement>(null)
  const searchSummaryRef = useRef<HTMLDivElement>(null)
  const searchNavigationDirectionRef = useRef<-1 | 1 | null>(null)
  const tagInputRef = useRef<HTMLInputElement>(null)
  const deviceNameInputRef = useRef<HTMLInputElement>(null)
  const accountButtonRef = useRef<HTMLButtonElement>(null)
  const accountPanelCloseRef = useRef<HTMLButtonElement>(null)
  const appShellRef = useRef<HTMLDivElement>(null)
  const mobileSidebarTriggerRef = useRef<HTMLButtonElement>(null)
  const appUpdateButtonRef = useRef<HTMLButtonElement>(null)
  const accountPanelScrollRef = useRef<number | null>(null)
  const suppressAccountPanelFocusRestoreRef = useRef(false)
  const searchLoadMoreRef = useRef<HTMLButtonElement>(null)
  const searchMasonryRef = useRef<HTMLDivElement>(null)
  const reminderEditorOpenRef = useRef(reminderEditorOpen)
  const modeRef = useRef(mode)
  const adminRouteActiveRef = useRef(adminRouteActive)
  const searchQueryRef = useRef(searchQuery)
  const searchFiltersRef = useRef(searchFilters)
  const draftRef = useRef(draft)
  const notesRef = useRef(new Map<string, Note>())
  const userInteractedRef = useRef(Boolean(
    !restoreInitialActiveDraft
      || noteIdForLaunchIntent(initialLaunchIntent)
      || shareBodyForLaunchIntent(initialLaunchIntent)
      || (initialLaunchIntent.kind === 'capture' && initialLaunchIntent.shareId)
  ))
  const pendingDraftCommitsRef = useRef(new Map<string, number>())
  const commitQueueRef = useRef<Promise<Note | null>>(Promise.resolve(null))
  const settingsCommitQueueRef = useRef<Promise<void>>(Promise.resolve())
  const syncRunRef = useRef<Promise<SyncCycleResult> | null>(null)
  const syncFailureRef = useRef<SyncFailureState | null>(null)
  const syncRuntimeRef = useRef<SyncRuntime | null>(null)
  const syncTabCoordinatorRef = useRef<SyncTabCoordinator | null>(null)
  const requestSyncRef = useRef<(request: SyncRequest, delayMs?: number) => void>(() => undefined)
  const accessProfileRequestRef = useRef<{
    ownerId: string
    promise: Promise<UserAccessProfile>
  } | null>(null)
  const assetPullCursorRef = useRef<string | null>(null)
  const assetPullCursorReadyRef = useRef(false)
  const deviceSettingsRef = useRef(deviceSettings)
  const authSessionRef = useRef(authSession)
  const authBusyRef = useRef(authBusy)
  const accountRestoreReadyRef = useRef(false)
  const restoreDomainsVerifiedRef = useRef(new Set<SyncDomain>())
  const restoreInventoryRetryCountRef = useRef(0)
  const linkPreviewAttemptsRef = useRef(new Set<string>())
  const linkPreviewRetryTimersRef = useRef(new Map<string, number>())
  const openLinkMetadataRetryKeysRef = useRef(new Set<string>())
  const linkMetadataOpenIdentityRef = useRef<string | null>(null)
  const imagePreviewMilestonesRef = useRef(new Set<string>())
  const copyFeedbackTimerRef = useRef<number | null>(null)
  const updateReloadStartedRef = useRef(false)
  const editorHistoryRef = useRef(createEditorHistory())
  const composerTransitionRef = useRef(false)
  // When the running transition began: Back waits for one only briefly, so a step that never settles cannot hold Back forever.
  const composerTransitionSinceRef = useRef(0)
  const searchReturnRef = useRef<SearchReturnState | null>(null)
  // Browse was reached by stepping away from an open note (not by closing it): Back on Browse returns to it (search/searchNavigation.ts).
  const browseFromNoteRef = useRef(false)
  const searchRestoreViewportRef = useRef<BrowseViewportSnapshot | null>(null)
  const pendingBrowseViewportRef = useRef<BrowseViewportSnapshot | null>(null)
  const browseScopeKeyRef = useRef<string | null>(null)
  const searchResultOpenScrollRef = useRef(false)
  const skipActiveSearchScrollRef = useRef(false)
  const backGuardArmedRef = useRef(false)
  const backGuardInitializedRef = useRef(false)
  const suppressBackGuardRearmRef = useRef(false)
  const oauthCallbackPendingRef = useRef(hasOAuthCallbackParameters())
  const noteRouteOpenedRef = useRef(false)
  const noteRouteRenderedNoteRef = useRef<Note | null>(null)
  const noteRouteResolutionKeyRef = useRef<string | null>(null)
  const nativeIntentDrainQueueRef = useRef<Promise<void>>(Promise.resolve())
  const nativeEditorFocusedRef = useRef(false)
  const handledQuickAccessRefreshRequestRef = useRef(0)
  const quickAccessRefreshPendingRef = useRef(false)
  const retainBrowseSurface = !studioActive && mode === 'capture' && searchReturnRef.current !== null
  const browseSurfaceMounted = mode === 'search' || retainBrowseSurface
  const [oauthCallbackHandled, setOauthCallbackHandled] = useState(
    !oauthCallbackPendingRef.current,
  )
  // Supabase can emit a refreshed Session object for the same signed-in user.
  // Route resolution must depend on the stable identity, otherwise that refresh
  // cancels the in-flight result while the duplicate guard prevents reattaching it.
  const authenticatedUserId = authSession?.user.id ?? null

  useLayoutEffect(() => {
    if (!pendingImageRevealId) return
    // A picture that is not on screen (folded to a band or while typing, or behind "+N") cannot be scrolled to: highlight it where it is.
    const picture = composerRef.current?.querySelector<HTMLElement>(`[data-asset-id="${pendingImageRevealId}"]`)
    if (!picture?.offsetHeight) {
      setRevealedImageId(pendingImageRevealId)
      setPendingImageRevealId(null)
      return
    }
    const scrollHost = composerScrollRegionRef.current
    if (!scrollHost) return
    return scheduleComposerAttachmentReveal(scrollHost, pendingImageRevealId, {
      settleMs: APP_SETTINGS.interactions.imagePasteRevealScrollSettleMs,
      onSettled: () => {
        setRevealedImageId(pendingImageRevealId)
        setPendingImageRevealId(null)
      },
    }) ?? undefined
  }, [pendingImageRevealId, studio.runtime.formFactor])

  useEffect(() => {
    setPendingImageRevealId(null)
    setRevealedImageId(null)
  }, [draft.noteId])

  useEffect(() => {
    if (mode !== 'capture') return
    if (document.documentElement.dataset.viewportFormFactor !== 'phone') return
    const composer = composerRef.current
    const scrollHost = composerScrollRegionRef.current
    if (!composer || !scrollHost) return

    return installScrollResponsiveComposerHeader(composer, scrollHost, {
      directionThresholdPx: APP_SETTINGS.interactions.composerHeaderDirectionThresholdPx,
      transitionMs: APP_SETTINGS.interactions.composerHeaderTransitionMs,
    })
  }, [composerKey, mode])

  useEffect(() => {
    const action = nativeQuickAccessCredentialPolicy({
      authRestoring,
      authError,
      userId: authenticatedUserId,
    })
    if (action === 'refresh' && authenticatedUserId) {
      void updateNativeQuickAccessSyncCredentials(authenticatedUserId)
    } else if (action === 'suspend') {
      void clearNativeQuickAccessSyncCredentials(false)
    }
  }, [authError, authRestoring, authSession?.expiresAt, authenticatedUserId])

  useEffect(() => { if (mode === 'capture') browseFromNoteRef.current = false }, [mode])
  modeRef.current = mode
  reminderEditorOpenRef.current = reminderEditorOpen
  adminRouteActiveRef.current = adminRouteActive
  searchQueryRef.current = searchQuery
  searchFiltersRef.current = searchFilters
  draftRef.current = draft
  deviceSettingsRef.current = deviceSettings
  authSessionRef.current = authSession
  authBusyRef.current = authBusy
  const notesById = useMemo(() => new Map(notes.map((note) => [note.id, note])), [notes])

  useEffect(() => {
    if (!loaded || !linkMetadataProvider || mode !== 'capture') return
    const openIdentity = `${composerKey}:${draft.noteId}`
    if (linkMetadataOpenIdentityRef.current === openIdentity) return
    linkMetadataOpenIdentityRef.current = openIdentity
    openLinkMetadataRetryKeysRef.current.clear()

    const note = notesById.get(draft.noteId)
    if (!note) return
    for (const url of note.urls) {
      const key = linkMetadataUrlKey(url)
      if (
        shouldRetryLinkMetadataOnOpen(url)
        && !linkPreviewAttemptsRef.current.has(key)
      ) {
        openLinkMetadataRetryKeysRef.current.add(key)
      }
    }
    if (openLinkMetadataRetryKeysRef.current.size > 0) {
      setLinkPreviewRetryRevision((current) => current + 1)
    }
  }, [composerKey, draft.noteId, linkMetadataProvider, loaded, mode, notesById])
  const updateLinkPreviewStatus = useCallback((key: string, status: LinkMetadataStatus | null) => {
    setLinkPreviewStatuses((current) => {
      if (status === null && !current.has(key)) return current
      if (status !== null && current.get(key) === status) return current
      const next = new Map(current)
      if (status === null) next.delete(key)
      else next.set(key, status)
      return next
    })
  }, [])
  const linkMetadataStatusForUrl = useCallback((noteId: string, url: UrlEntity) => (
    linkMetadataProvider
      ? linkPreviewStatuses.get(linkPreviewAttemptKey(noteId, url)) ?? deriveLinkMetadataStatus(url)
      : null
  ), [linkPreviewStatuses])
  // Link gists (docs/link-notes/GIST.md): read on attention, saved on the link in every note that has it. The save is
  // local, like link details; it rides along with the note's next real edit rather than adding a revision of its own.
  const [, setGistRevision] = useState(0)
  // Answers to the person's own questions are kept on the link in every note that has it, newest first, like a gist: saved at once on this
  // device, travelling with the note's next real edit.
  const saveLinkAnswers = useCallback(async (key: string, change: (answers: LinkAnswer[]) => LinkAnswer[]) => {
    const task = commitQueueRef.current.then(async () => {
      const affected = [...notesRef.current.values()].filter((note) => note.urls.some((url) => linkMetadataUrlKey(url) === key))
      const updated = (await Promise.all(affected.map((note) => repository.updateLinkMetadata(note.id, note.urls.map((url) => {
        if (linkMetadataUrlKey(url) !== key) return url
        const { answers: _previous, ...rest } = url
        const next = change(url.answers ?? [])
        return next.length ? { ...rest, answers: next } : rest
      }))))).filter((note): note is Note => note !== undefined)
      for (const note of updated) searchIndex.upsert(note)
      if (updated.length > 0) setNotes((existing) => updated.reduce(updateNoteCollection, existing))
      return null
    })
    commitQueueRef.current = task.catch(() => null)
    await task
  }, [])
  const gistController = useMemo(() => createGistController({
    linksWithKey: (key) => [...notesRef.current.values()].flatMap((note) => note.urls.filter((url) => linkMetadataUrlKey(url) === key)),
    changed: () => setGistRevision((current) => current + 1),
    saveAnswer: (key, answer) => saveLinkAnswers(key, (answers) => [answer, ...answers].slice(0, GIST_SETTINGS.ask.maxStoredPerLink)),
    removeAnswer: (key, id) => saveLinkAnswers(key, (answers) => answers.filter((answer) => answer.id !== id)),
    saveGist: async (key, gist) => {
      const task = commitQueueRef.current.then(async () => {
        const affected = [...notesRef.current.values()].filter((note) => note.urls.some((url) => linkMetadataUrlKey(url) === key))
        const updated = (await Promise.all(affected.map((note) => repository.updateLinkMetadata(note.id, note.urls.map((url) => {
          if (linkMetadataUrlKey(url) !== key) return url
          const { gist: _previous, ...rest } = url
          return gist ? { ...rest, gist } : rest
        }))))).filter((note): note is Note => note !== undefined)
        for (const note of updated) searchIndex.upsert(note)
        if (updated.length > 0) setNotes((existing) => updated.reduce(updateNoteCollection, existing))
        return null
      })
      commitQueueRef.current = task.catch(() => null)
      await task
    },
  }), [])
  // How the owner chose to show a link in a note (a pill or a row). Saved on the link like a gist: locally at once, and it
  // travels with the note's next real edit. Choosing nothing (null) returns the link to following where it sits.
  const saveLinkDisplay = useCallback(async (noteId: string, key: string, display: 'chip' | 'row' | 'preview' | null) => {
    const task = commitQueueRef.current.then(async () => {
      const note = notesRef.current.get(noteId)
      if (!note || !note.urls.some((url) => linkMetadataUrlKey(url) === key)) return null
      const updated = await repository.updateLinkMetadata(note.id, note.urls.map((url) => {
        if (linkMetadataUrlKey(url) !== key) return url
        const { display: _previous, ...rest } = url
        return display ? { ...rest, display } : rest
      }))
      if (updated) {
        searchIndex.upsert(updated)
        setNotes((existing) => updateNoteCollection(existing, updated))
      }
      return null
    })
    commitQueueRef.current = task.catch(() => null)
    await task
  }, [])
  // What the owner chose on the Links screen: a topic (null returns to the suggested one) and a pin. Saved on the link in every note that
  // holds it, like a gist: locally at once, travelling with each note's next real edit. The full list is read, because a link can be in a
  // note older than the page Browse has loaded.
  const loadAllNotes = useCallback(() => repository.listNotes(), [])
  const saveLinkAttributes = useCallback(async (keys: readonly string[], patch: LinkAttributePatch) => {
    const wanted = new Set(keys)
    const task = commitQueueRef.current.then(async () => {
      const all = await repository.listNotes()
      const now = Date.now()
      for (const note of all) {
        if (!note.urls.some((url) => wanted.has(linkMetadataUrlKey(url)))) continue
        const updated = await repository.updateLinkMetadata(note.id, note.urls.map((url) => {
          if (!wanted.has(linkMetadataUrlKey(url))) return url
          const { topic: previousTopic, pinnedAt: previousPin, ...rest } = url
          const topic = patch.topic === undefined ? previousTopic : patch.topic?.trim() || undefined
          const pinnedAt = patch.pinned === undefined ? previousPin : patch.pinned ? now : undefined
          return { ...rest, ...(topic ? { topic } : {}), ...(typeof pinnedAt === 'number' ? { pinnedAt } : {}) }
        }))
        if (!updated) continue
        searchIndex.upsert(updated)
        if (notesRef.current.has(updated.id)) setNotes((existing) => updateNoteCollection(existing, updated))
      }
      return null
    })
    commitQueueRef.current = task.catch(() => null)
    await task
  }, [searchIndex])
  const retryLinkMetadata = useCallback((noteId: string, url: UrlEntity) => {
    const candidateKey = linkMetadataUrlKey(url)
    const attemptKey = linkPreviewAttemptKey(noteId, url)
    const timer = linkPreviewRetryTimersRef.current.get(attemptKey)
    if (timer !== undefined) window.clearTimeout(timer)
    linkPreviewRetryTimersRef.current.delete(attemptKey)
    linkPreviewAttemptsRef.current.delete(attemptKey)
    updateLinkPreviewStatus(attemptKey, null)

    const task = commitQueueRef.current.then(async () => {
      const current = notesRef.current.get(noteId)
      if (!current) return null
      const urls = current.urls.map((currentUrl) => (
        linkMetadataUrlKey(currentUrl) === candidateKey
          ? resetLinkMetadataRetry(currentUrl)
          : currentUrl
      ))
      const updated = await repository.updateLinkMetadata(current.id, urls)
      if (!updated) return null
      searchIndex.upsert(updated)
      setNotes((existing) => updateNoteCollection(existing, updated))
      return updated
    })
    commitQueueRef.current = task.catch(() => null)
    void task.finally(() => {
      setLinkPreviewRetryRevision((current) => current + 1)
    })
  }, [searchIndex, updateLinkPreviewStatus])
  const assetsByNote = useMemo(() => {
    const grouped = new Map<string, NoteAsset[]>()
    for (const asset of assets) {
      if (asset.state === 'deleted') continue
      const noteAssets = grouped.get(asset.noteId) ?? []
      noteAssets.push(asset)
      grouped.set(asset.noteId, noteAssets)
    }
    for (const noteAssets of grouped.values()) {
      noteAssets.sort((left, right) => left.createdAt - right.createdAt)
    }
    return grouped
  }, [assets])
  notesRef.current = notesById

  const syncHistoryAvailability = useCallback(() => {
    const history = editorHistoryRef.current
    const next = { canUndo: history.past.length > 0, canRedo: history.future.length > 0 }
    setHistoryAvailability((current) =>
      current.canUndo === next.canUndo && current.canRedo === next.canRedo ? current : next,
    )
  }, [])

  const resetEditorHistory = useCallback(() => {
    editorHistoryRef.current = createEditorHistory()
    setHistoryAvailability((current) =>
      current.canUndo || current.canRedo ? { canUndo: false, canRedo: false } : current,
    )
  }, [])

  const closeOnDemandTools = useCallback(() => {
    setTagToolbarOpen(false)
    setReminderEditorOpen(false)
    setSearchFiltersOpen(false)
    setSearchTagsExpanded(false)
    setSearchTagSuggestionsOpen(false)
    setSearchTagSuggestionIndex(null)
    setTagInputOpen(false)
    setTagInput('')
  }, [])

  const clearDismissedModalFocus = useCallback(() => {
    requestAnimationFrame(() => {
      const activeElement = document.activeElement
      if (activeElement instanceof HTMLElement && activeElement !== document.body) activeElement.blur()
    })
  }, [])

  const restoreEditorSelectionAfterTransientUi = useCallback(() => {
    if (restoreEditorSelectionFrameRef.current !== null) {
      cancelAnimationFrame(restoreEditorSelectionFrameRef.current)
    }
    restoreEditorSelectionFrameRef.current = requestAnimationFrame(() => {
      restoreEditorSelectionFrameRef.current = null
      if (modeRef.current === 'capture') {
        editorRef.current?.restoreSelection({ preventScroll: true })
      }
    })
  }, [])

  const closeReminderEditor = useCallback(() => {
    setReminderEditorOpen(false)
    restoreEditorSelectionAfterTransientUi()
  }, [restoreEditorSelectionAfterTransientUi])

  useEffect(() => {
    let cancelled = false
    const isAuthCallback = oauthCallbackPendingRef.current
    const unsubscribe = authProvider.onSessionChange((session) => {
      if (cancelled || oauthCallbackPendingRef.current) return
      if (!acceptsWorkspaceSession(session)) return
      setAuthSession(session)
      setAuthRestoring(false)
      setAuthError(null)
    })
    recordDiagnosticMilestone('auth:restore-started')
    const lifecycle = startAuthSessionRestoration(authProvider, {
      onSession(session, source) {
        if (cancelled) return
        if (source === 'initial' && isAuthCallback) {
          const completion = completeOAuthHistoryNavigation()
          if (completion === 'traversed') return
          oauthCallbackPendingRef.current = false
          setOauthCallbackHandled(true)
        } else if (oauthCallbackPendingRef.current) return
        if (!acceptsWorkspaceSession(session)) return
        setAuthSession(session)
        setAuthRestoring(false)
        setAuthError(null)
        recordDiagnosticMilestone('auth:restore-ready', {
          authenticated: Boolean(session),
          source,
        })
      },
      onError(error, source) {
        if (cancelled) return
        if (source === 'initial' && isAuthCallback) {
          const completion = completeOAuthHistoryNavigation()
          if (completion === 'traversed') return
          oauthCallbackPendingRef.current = false
          setOauthCallbackHandled(true)
        } else if (oauthCallbackPendingRef.current) return
        setAuthRestoring(false)
        setAuthError(error instanceof Error ? error.message : 'Unable to restore the session.')
        recordDiagnosticMilestone('auth:restore-error', { source })
      },
    })

    return () => {
      cancelled = true
      unsubscribe()
      lifecycle.dispose()
    }
  }, [])

  useEffect(() => {
    if (
      getNativePlatform(window) !== 'android'
      || !authProvider.handleNativeOAuthCallback
      || !authProvider.resumeNativeOAuthCallback
      || !authProvider.cancelPendingNativeOAuth
    ) return

    let disposed = false
    let browserCloseTimer: number | null = null
    const listenerHandles: Array<{ remove: () => Promise<void> }> = []

    const applyResult = (result: NativeOAuthHandlingResult) => {
      if (disposed || result.status === 'ignored') return
      setAuthBusy(false)
      setAuthRestoring(false)
      if (result.status === 'success') {
        if (!acceptsWorkspaceSession(result.session)) return
        setAuthSession(result.session)
        setAuthError(null)
        recordDiagnosticMilestone('auth:native-callback-complete')
      } else {
        setAuthError('Google sign-in was canceled.')
        recordDiagnosticMilestone('auth:native-callback-canceled')
      }
    }

    const handleCallback = async (url: string) => {
      if (browserCloseTimer !== null) {
        window.clearTimeout(browserCloseTimer)
        browserCloseTimer = null
      }
      const wasBusy = authBusyRef.current
      setAuthBusy(true)
      try {
        const result = await authProvider.handleNativeOAuthCallback!(url)
        if (result.status === 'ignored') {
          if (!disposed) setAuthBusy(authBusyAfterNativeOAuthResult(wasBusy, result))
          return
        }
        applyResult(result)
      } catch (error) {
        if (disposed) return
        setAuthBusy(false)
        setAuthRestoring(false)
        setAuthError(error instanceof Error ? error.message : 'Unable to complete Google sign-in.')
        recordDiagnosticMilestone('auth:native-callback-error')
      }
    }

    const resumeCallback = async () => {
      try {
        applyResult(await authProvider.resumeNativeOAuthCallback!())
      } catch (error) {
        if (disposed) return
        setAuthBusy(false)
        setAuthError(error instanceof Error ? error.message : 'Unable to resume Google sign-in.')
      }
    }

    const initializeNativeOAuth = async () => {
      const [{ App: CapacitorApp }, { Browser }] = await Promise.all([
        import('@capacitor/app'),
        import('@capacitor/browser'),
      ])
      if (disposed) return
      listenerHandles.push(await CapacitorApp.addListener('appUrlOpen', ({ url }) => {
        void handleCallback(url)
      }))
      listenerHandles.push(await Browser.addListener('browserFinished', () => {
        if (browserCloseTimer !== null) window.clearTimeout(browserCloseTimer)
        browserCloseTimer = window.setTimeout(() => {
          browserCloseTimer = null
          void authProvider.cancelPendingNativeOAuth!().then((canceled) => {
            if (disposed || !canceled) return
            setAuthBusy(false)
            setAuthError('Google sign-in was canceled.')
          })
        }, APP_SETTINGS.auth.nativeOAuthBrowserCloseGraceMs)
      }))
      const launch = await CapacitorApp.getLaunchUrl()
      if (launch?.url) await handleCallback(launch.url)
      else await resumeCallback()
    }

    void initializeNativeOAuth().catch((error) => {
      if (disposed) return
      setAuthBusy(false)
      setAuthError(error instanceof Error ? error.message : 'Native sign-in could not start.')
    })
    window.addEventListener('online', resumeCallback)
    return () => {
      disposed = true
      if (browserCloseTimer !== null) window.clearTimeout(browserCloseTimer)
      window.removeEventListener('online', resumeCallback)
      for (const handle of listenerHandles) void handle.remove()
    }
  }, [])

  useEffect(() => {
    if (getNativePlatform(window) !== 'android') return

    let disposed = false
    let listenerHandle: { remove: () => Promise<void> } | null = null

    void import('@capacitor/app').then(async ({ App: CapacitorApp }) => {
      if (disposed) return
      listenerHandle = await CapacitorApp.addListener('backButton', () => {
        if (reminderEditorOpenRef.current) {
          closeReminderEditor()
          return
        }
        if (dismissContextStep()) return
        if (getNativeBackAction(backGuardArmedRef.current) === 'navigate-back') {
          window.history.back()
          return
        }
        void CapacitorApp.exitApp()
      })
      if (disposed) void listenerHandle.remove()
    })

    return () => {
      disposed = true
      if (listenerHandle) void listenerHandle.remove()
    }
  }, [closeReminderEditor])

  useEffect(() => {
    accountRestoreReadyRef.current = false
    restoreDomainsVerifiedRef.current.clear()
    restoreInventoryRetryCountRef.current = 0
    setAccountRestorePhase(hasRemoteAccount ? 'restoring' : 'idle')
  }, [authSession?.user.id, hasRemoteAccount])

  useEffect(() => {
    if (hasOAuthCallbackParameters()) return
    history.replaceState({}, '', cleanAppLaunchPath(initialLaunchIntent, window.location.href))
  }, [])

  useEffect(() => {
    if (performance.getEntriesByName('minimany:interactive').length > 0) return
    requestAnimationFrame(() => {
      performance.mark('minimany:interactive')
      if (performance.getEntriesByName('minimany:navigation-script').length > 0) {
        performance.measure('minimany:boot-to-interactive', 'minimany:navigation-script', 'minimany:interactive')
      }
      recordDiagnosticMilestone('react:interactive')
      window.dispatchEvent(new Event('minimany:interactive'))
    })
  }, [])

  useEffect(() => {
    let cancelled = false
    let idleHandle: number | null = null
    let timerHandle: number | null = null

    const expireBatch = (batch: Note[]) => {
      for (const dueNote of batch) {
        const expiredNote = expireNoteIfDue(dueNote, deviceId)
        if (!expiredNote) continue
        void repository.commitLocalNote(expiredNote, 'expiry').then(() => {
          if (cancelled) return
          searchIndex.upsert(expiredNote)
          setNotes((current) => updateNoteCollection(current, expiredNote))
        })
      }
    }

    async function loadNextPage(cursor: NotePageCursor): Promise<void> {
      try {
        const page = await repository.listNotesPage(NOTE_BATCH_SIZE, cursor)
        if (cancelled) return
        searchIndex.addBatch(page.notes)
        setNotes((current) => appendOlderNotes(current, page.notes))
        expireBatch(page.notes)
        if (page.nextCursor) scheduleNextPage(page.nextCursor)
        else setBrowseInventoryPhase('ready')
      } catch {
        if (cancelled) return
        setBrowseInventoryPhase('error')
        recordDiagnosticMilestone('notes:inventory-load-failed')
      }
    }

    function scheduleNextPage(cursor: NotePageCursor): void {
      const load = () => void loadNextPage(cursor)
      if ('requestIdleCallback' in window) {
        idleHandle = window.requestIdleCallback(load, { timeout: 400 })
      } else {
        timerHandle = globalThis.setTimeout(load, 16)
      }
    }

    const activeDraftPromise = measureDiagnosticActivity(
      'storage:active-draft',
      () => repository.getActiveDraft(),
    ).then(async (storedDraft) => {
      if (cancelled) return
      if (!storedDraft) {
        recordDiagnosticMilestone('draft:ready', { restored: false })
        return
      }

      if (restoreInitialActiveDraft && !userInteractedRef.current) {
        setDraft(storedDraft)
        requestAnimationFrame(() => editorRef.current?.placeCaretAtEnd())
        recordDiagnosticMilestone('draft:ready', { restored: true })
        return
      }

      if (!restoreInitialActiveDraft) {
        try {
          const savedNote = await repository.getNote(storedDraft.noteId)
          const recoveredNote = recoveryNoteForExplicitCapture(
            storedDraft,
            savedNote,
            deviceId,
          )
          if (recoveredNote) await repository.commitLocalNote(recoveredNote, 'restore')
          await repository.clearActiveDraft(storedDraft.noteId)
          recordDiagnosticMilestone('draft:ready', {
            restored: false,
            preservedBeforeCapture: Boolean(recoveredNote),
          })
        } catch {
          if (cancelled) return
          draftRef.current = storedDraft
          setDraft(storedDraft)
          setSaveState('error')
          recordDiagnosticMilestone('draft:ready', {
            restored: true,
            preserveBeforeCaptureFailed: true,
          })
        }
        return
      }

      recordDiagnosticMilestone('draft:ready', { restored: false })
    })
    void activeDraftPromise.then(() => {
      if (cancelled) return
      setNativeIntentReady(true)
      recordDiagnosticMilestone('native-intent:active-draft-ready')
    })
    const prioritizeInitialWidgetRoute = initialWidgetRoutePriorityGate.wait()
    const firstPagePromise = activeDraftPromise
      .then(() => prioritizeInitialWidgetRoute)
      .then(() => measureDiagnosticActivity(
        'storage:first-notes-page',
        () => repository.listNotesPage(NOTE_BATCH_SIZE),
      ))
    const assetsPromise = prioritizeInitialWidgetRoute.then(() => measureDiagnosticActivity(
      'storage:note-assets',
      () => repository.listAssets(),
    ))
    const settingsPromise = measureDiagnosticActivity(
      'storage:settings',
      () => repository.initializeSettings(
        createDefaultGlobalSettings(deviceId),
        createDefaultDeviceSettings(
          deviceId,
          detectDefaultDeviceName(navigator.userAgent, navigator.maxTouchPoints, deviceId),
          Date.now(),
          initialUiSize,
        ),
      ),
    )

    void measureDiagnosticActivity('storage:persistence', requestPersistentStorage)
      .then((persistence) => {
        if (!cancelled) setStoragePersistent(persistence)
      })

    void firstPagePromise.then((firstPage) => {
      if (cancelled) return
      // Prime the lightweight fallback index before publishing the note state.
      // The fuzzy-search module still loads asynchronously inside this call.
      searchIndex.load(firstPage.notes)
      setNotes(firstPage.notes)
      recordDiagnosticMilestone('notes:first-page-ready', { count: firstPage.notes.length })
      recordDiagnosticMilestone('search:index-load-requested', {
        count: firstPage.notes.length,
      })
      expireBatch(firstPage.notes)
      if (firstPage.nextCursor) scheduleNextPage(firstPage.nextCursor)
      else setBrowseInventoryPhase('ready')
    }).catch(() => {
      if (cancelled) return
      setBrowseInventoryPhase('error')
      recordDiagnosticMilestone('notes:inventory-load-failed')
    })

    void assetsPromise.then((storedAssets) => {
      if (!cancelled) setAssets(storedAssets)
    })

    void settingsPromise.then((storedSettings) => {
      if (cancelled) return
      const diagnosticsChanged = readBootstrapPerformanceDiagnostics()
        !== storedSettings.device.performanceDiagnosticsEnabled
      setGlobalSettings(storedSettings.global)
      setDeviceSettings(storedSettings.device)
      setDeviceNameDraft(displayDeviceName(storedSettings.device))
      writeBootstrapStartMode(storedSettings.device.startMode)
      writeBootstrapUiSize(storedSettings.device.uiSize)
      writeBootstrapPerformanceDiagnostics(storedSettings.device.performanceDiagnosticsEnabled)
      if (diagnosticsChanged) {
        setPerformanceDiagnosticsEnabled(storedSettings.device.performanceDiagnosticsEnabled)
      }
      applyUiSize(storedSettings.device.uiSize)
    })

    void Promise.all([firstPagePromise, settingsPromise, assetsPromise]).then(() => {
      if (cancelled) return
      setLoaded(true)
      recordDiagnosticMilestone('local-data:ready')
    }).catch(() => {
      if (!cancelled) recordDiagnosticMilestone('local-data:load-failed')
    })
    return () => {
      cancelled = true
      if (idleHandle !== null) window.cancelIdleCallback(idleHandle)
      if (timerHandle !== null) window.clearTimeout(timerHandle)
      initialWidgetRoutePriorityGate.dispose()
    }
  }, [
    deviceId,
    initialUiSize,
    initialWidgetRoutePriorityGate,
    searchIndex,
  ])

  useEffect(() => imageQueue.subscribe((updates) => {
    setAssets((current) => {
      const byId = new Map(current.map((asset) => [asset.id, asset]))
      for (const asset of updates) byId.set(asset.id, asset)
      return [...byId.values()]
    })
  }), [])

  useEffect(() => {
    const updateOnlineState = () => setIsOnline(navigator.onLine)
    window.addEventListener('online', updateOnlineState)
    window.addEventListener('offline', updateOnlineState)
    return () => {
      window.removeEventListener('online', updateOnlineState)
      window.removeEventListener('offline', updateOnlineState)
    }
  }, [])

  const checkForLatestApp = useCallback(async () => {
    if (!APP_UPDATE_CHECKS_ENABLED) {
      setAppUpdate({ phase: 'current', deployedBuildId: null, prompt: false })
      return
    }
    if (!navigator.onLine) {
      setAppUpdate((current) => ({ ...current, phase: 'error', prompt: false }))
      return
    }
    setAppUpdate((current) => ({ ...current, phase: 'checking', prompt: false }))
    const deployedBuildId = await queryDeployedBuildId()
    if (!deployedBuildId) {
      setAppUpdate((current) => ({ ...current, phase: 'error', prompt: false }))
      return
    }
    setAppUpdate({
      phase: deployedBuildId === __MINIMANY_BUILD_ID__ ? 'current' : 'available',
      deployedBuildId,
      prompt: false,
    })
  }, [])

  const applyLatestApp = useCallback(async () => {
    if (!APP_UPDATE_CHECKS_ENABLED) {
      setAppUpdate({ phase: 'current', deployedBuildId: null, prompt: false })
      return
    }
    if (!navigator.onLine) {
      setAppUpdate((current) => ({ ...current, phase: 'error', prompt: true }))
      return
    }
    setAppUpdate((current) => ({ ...current, phase: 'applying', prompt: true }))
    const deployedBuildId = await queryDeployedBuildId()
    if (!deployedBuildId) {
      setAppUpdate((current) => ({ ...current, phase: 'error', prompt: true }))
      return
    }
    if (deployedBuildId === __MINIMANY_BUILD_ID__) {
      setAppUpdate({ phase: 'current', deployedBuildId, prompt: false })
      return
    }
    if (!await verifyFreshAppShell(deployedBuildId)) {
      setAppUpdate({ phase: 'error', deployedBuildId, prompt: true })
      return
    }
    clearAppUpdateReminder()
    setAppUpdateReminderAt(0)
    dispatchSafeAppReload(window, undefined, { refreshShell: true, deployedBuildId })
  }, [])

  const remindAboutAppUpdateLater = useCallback(() => {
    const remindAt = snoozeAppUpdateReminder()
    setAppUpdateReminderAt(remindAt)
    setAppUpdate((current) => ({ ...current, prompt: false }))
  }, [])

  useEffect(() => {
    const showAvailableUpdate = (event: Event) => {
      const buildId = event instanceof CustomEvent && typeof event.detail?.buildId === 'string'
        ? event.detail.buildId
        : null
      if (!buildId) return
      const prompt = shouldPromptForAppUpdate()
      setAppUpdateReminderAt(readAppUpdateReminderAt())
      setAppUpdate({ phase: 'available', deployedBuildId: buildId, prompt })
    }
    window.addEventListener(APP_UPDATE_AVAILABLE_EVENT, showAvailableUpdate)
    return () => window.removeEventListener(APP_UPDATE_AVAILABLE_EVENT, showAvailableUpdate)
  }, [])

  useEffect(() => {
    if (
      appUpdate.phase !== 'available'
      || appUpdate.prompt
      || appUpdateReminderAt <= 0
    ) return
    const remainingMs = appUpdateReminderAt - Date.now()
    if (remainingMs <= 0) {
      setAppUpdate((current) => ({ ...current, prompt: true }))
      return
    }
    const timer = window.setTimeout(() => {
      setAppUpdate((current) => (
        current.phase === 'available' ? { ...current, prompt: true } : current
      ))
    }, remainingMs)
    return () => window.clearTimeout(timer)
  }, [appUpdate.phase, appUpdate.prompt, appUpdateReminderAt])

  useEffect(() => {
    if (!accountPanelOpen) return
    void checkForLatestApp()
  }, [accountPanelOpen, checkForLatestApp])

  useEffect(() => {
    if (!accountPanelOpen) setSettingsSectionMenuOpen(false)
  }, [accountPanelOpen])

  useLayoutEffect(() => installVisualViewportCssVariables(), [])

  useLayoutEffect(() => {
    if (mode !== 'capture' || studioActive) return
    document.documentElement.classList.add('note-modal-open')
    return () => document.documentElement.classList.remove('note-modal-open')
  }, [mode, studioActive])

  useLayoutEffect(() => {
    if (!accountPanelOpen) return
    const root = document.documentElement
    const scrollY = accountPanelScrollRef.current ?? window.scrollY
    root.style.setProperty('--settings-scroll-offset', `${scrollY}px`)
    if (!studioActive) root.classList.add('settings-modal-open')
    return () => {
      root.classList.remove('settings-modal-open')
      root.style.removeProperty('--settings-scroll-offset')
      const suppressFocusRestore = suppressAccountPanelFocusRestoreRef.current
      suppressAccountPanelFocusRestoreRef.current = false
      requestAnimationFrame(() => {
        window.scrollTo(0, scrollY)
        if (!suppressFocusRestore) {
          const focusTarget = getSettingsCloseFocusTarget(modeRef.current)
          if (focusTarget === 'search') {
            const searchTarget = shouldRestoreBrowseInputFocus(studio.runtime.formFactor)
              ? searchRef.current
              : searchFabRef.current
            searchTarget?.focus({ preventScroll: true })
          }
          else accountButtonRef.current?.focus({ preventScroll: true })
        }
        accountPanelScrollRef.current = null
      })
    }
  }, [accountPanelOpen, studio.runtime.formFactor, studioActive])

  const appUpdatePromptOpen = !adminRouteActive && appUpdate.prompt && (
    appUpdate.phase === 'available'
    || appUpdate.phase === 'applying'
    || appUpdate.phase === 'error'
  )

  useLayoutEffect(() => {
    if (!appUpdatePromptOpen) return
    document.documentElement.classList.add('app-update-modal-open')
    return () => document.documentElement.classList.remove('app-update-modal-open')
  }, [appUpdatePromptOpen])

  useEffect(() => {
    if (!accountPanelOpen) return
    accountPanelCloseRef.current?.focus({ preventScroll: true })
  }, [accountPanelOpen])

  useEffect(() => {
    if (!accountPanelOpen) return
    let canceled = false
    void repository.inspectLocalDataContinuity()
      .then((snapshot) => {
        if (!canceled) setLocalContinuity(snapshot)
      })
      .catch(() => {
        if (!canceled) setLocalContinuity(null)
      })
    return () => {
      canceled = true
    }
  }, [accountPanelOpen])

  useEffect(() => {
    if (!appUpdatePromptOpen || appUpdate.phase === 'applying') return
    appUpdateButtonRef.current?.focus({ preventScroll: true })
  }, [appUpdate.phase, appUpdatePromptOpen])

  useEffect(() => {
    if (
      mode === 'search'
      && !browseCovered
      && shouldRestoreBrowseInputFocus(studio.runtime.formFactor)
    ) {
      searchRef.current?.focus({ preventScroll: true })
    }
  }, [browseCovered, mode, studio.runtime.formFactor])

  useEffect(() => {
    if (tagInputOpen) tagInputRef.current?.focus({ preventScroll: true })
  }, [tagInputOpen])

  useEffect(
    () => () => {
      if (copyFeedbackTimerRef.current !== null) {
        window.clearTimeout(copyFeedbackTimerRef.current)
      }
      if (restoreEditorSelectionFrameRef.current !== null) {
        cancelAnimationFrame(restoreEditorSelectionFrameRef.current)
      }
    },
    [],
  )

  const commitSnapshot = useCallback(
    (snapshot: ActiveDraft): Promise<Note | null> => {
      const pending = pendingDraftCommitsRef.current
      pending.set(snapshot.noteId, (pending.get(snapshot.noteId) ?? 0) + 1)
      const task = commitQueueRef.current.then(async () => {
        const isStaleCurrentDraft =
          snapshot.noteId === draftRef.current.noteId && snapshot.updatedAt < draftRef.current.updatedAt
        if (isStaleCurrentDraft) return null

        if (!hasDraftContent(snapshot)) return null
        const baseNote = notesRef.current.get(snapshot.noteId) ?? null
        if (baseNote && draftMatchesNote(snapshot, baseNote)) {
          setSaveState(storedNoteSaveState(baseNote))
          return baseNote
        }
        const note = noteFromDraft(snapshot, baseNote, deviceId)
        await repository.commitLocalNote(note)
        requestSyncRef.current({ push: true, domains: [] })
        searchIndex.upsert(note)
        setNotes((current) => updateNoteCollection(current, note))
        if (
          modeRef.current === 'capture'
          && snapshot.noteId === draftRef.current.noteId
          && readNoteRoute() !== note.id
        ) {
          replaceNoteRoute(note.id)
        }
        if (draftRef.current.noteId === snapshot.noteId) setSaveState('local')
        return note
      }).finally(() => {
        const count = (pending.get(snapshot.noteId) ?? 1) - 1
        if (count) pending.set(snapshot.noteId, count)
        else pending.delete(snapshot.noteId)
      })
      commitQueueRef.current = task.catch(() => null)
      return task
    },
    [deviceId, searchIndex],
  )

  useEffect(() => {
    let retryTimer: number | null = null
    const safelyApplyUpdate = (event: Event) => {
      event.preventDefault()
      if (updateReloadStartedRef.current) return
      const reloadRequest = readAppReloadRequest(event)
      updateReloadStartedRef.current = true
      void (async () => {
        const snapshot = draftRef.current
        let activatedUpdateWorker: ServiceWorker | null = null
        try {
          await safelyReloadAfterLocalDurability(
            async () => {
              if (!hasDraftContent(snapshot)) return
              await repository.saveActiveDraft(snapshot)
              await commitSnapshot(snapshot).catch(() => null)
            },
            async () => {
              await Promise.all([commitQueueRef.current, settingsCommitQueueRef.current])
              if (reloadRequest.refreshShell) {
                const registration = await navigator.serviceWorker?.getRegistration()
                if (
                  !registration
                  || !reloadRequest.deployedBuildId
                  || !await activateWaitingServiceWorker(
                    registration,
                    reloadRequest.deployedBuildId,
                  )
                ) throw new Error('The updated app worker did not become active.')
                activatedUpdateWorker = registration.active
              }
            },
            () => {
              if (reloadRequest.refreshShell && activatedUpdateWorker) {
                if (!requestAppNavigationThroughWorker(
                  activatedUpdateWorker,
                  window.location.href,
                )) throw new Error('The updated app worker could not navigate the page.')
                return
              }
              window.location.reload()
            },
          )
        } catch {
          updateReloadStartedRef.current = false
          setSaveState('error')
          setAppUpdate((current) => ({ ...current, phase: 'error', prompt: true }))
          retryTimer = window.setTimeout(() => safelyApplyUpdate(new CustomEvent(
            APP_UPDATE_READY_EVENT,
            { detail: reloadRequest },
          )), APP_SETTINGS.appUpdates.reloadRetryMs)
        }
      })()
    }
    window.addEventListener(APP_UPDATE_READY_EVENT, safelyApplyUpdate)
    return () => {
      window.removeEventListener(APP_UPDATE_READY_EVENT, safelyApplyUpdate)
      if (retryTimer !== null) window.clearTimeout(retryTimer)
    }
  }, [commitSnapshot])

  const discardEmptySnapshot = useCallback(
    (snapshot: ActiveDraft): Promise<Note | null> => {
      const task = commitQueueRef.current.then(async () => {
        await repository.clearActiveDraft(snapshot.noteId)
        const baseNote = await repository.getNote(snapshot.noteId)
        if (!baseNote || baseNote.status === 'trash') {
          setSaveState('ready')
          return null
        }
        const discarded = transitionNoteStatus(baseNote, 'trash', deviceId)
        await repository.commitLocalNote(discarded, 'delete')
        searchIndex.upsert(discarded)
        setNotes((current) => updateNoteCollection(current, discarded))
        setSaveState('ready')
        return null
      })
      commitQueueRef.current = task.catch(() => null)
      return task
    },
    [deviceId, searchIndex],
  )

  useEffect(() => {
    if (!hasDraftContent(draft)) return
    const savedNote = notesById.get(draft.noteId)
    if (!shouldPersistActiveDraft(draft, savedNote)) {
      void repository.clearActiveDraft(draft.noteId).catch(() => setSaveState('error'))
      return
    }

    const durableTimer = window.setTimeout(() => {
      void repository
        .saveActiveDraft(draft)
        .then(() => setSaveState('local'))
        .catch(() => setSaveState('error'))
    }, APP_SETTINGS.persistence.activeDraftDebounceMs)
    const commitTimer = window.setTimeout(() => {
      void commitSnapshot(draft).catch(() => setSaveState('error'))
    }, APP_SETTINGS.persistence.noteRevisionDebounceMs)

    return () => {
      window.clearTimeout(durableTimer)
      window.clearTimeout(commitTimer)
    }
  }, [commitSnapshot, draft, notesById])

  useEffect(() => {
    if (!linkMetadataProvider || !loaded) return

    const candidates = collectLinkMetadataCandidates(
      notes,
      linkPreviewAttemptsRef.current,
      (url) => (
        openLinkMetadataRetryKeysRef.current.has(linkMetadataUrlKey(url))
        || needsLinkMetadataRefresh(url)
      ),
    ).slice(0, APP_SETTINGS.links.metadataFetchConcurrency)

    const scheduleRetry = (
      attemptKey: string,
      delayMs: number,
      status: Extract<LinkMetadataStatus, 'waiting' | 'partial'>,
    ) => {
      const previousTimer = linkPreviewRetryTimersRef.current.get(attemptKey)
      if (previousTimer !== undefined) window.clearTimeout(previousTimer)
      updateLinkPreviewStatus(attemptKey, status)
      const timer = window.setTimeout(() => {
        linkPreviewRetryTimersRef.current.delete(attemptKey)
        linkPreviewAttemptsRef.current.delete(attemptKey)
        updateLinkPreviewStatus(attemptKey, 'pending')
        setLinkPreviewRetryRevision((current) => current + 1)
      }, delayMs)
      linkPreviewRetryTimersRef.current.set(attemptKey, timer)
    }

    for (const candidate of candidates) {
      const candidateKey = linkMetadataUrlKey(candidate)
      const attemptKey = candidateKey
      openLinkMetadataRetryKeysRef.current.delete(candidateKey)
      linkPreviewAttemptsRef.current.add(attemptKey)
      updateLinkPreviewStatus(attemptKey, 'fetching')

      const applyMetadata = (
        metadata: LinkMetadata,
        fetchedAt: number,
        resetRetry = true,
      ) => {
        const task = commitQueueRef.current.then(async () => {
          const affectedNotes = [...notesRef.current.values()].filter((note) => (
            note.urls.some((url) => linkMetadataUrlKey(url) === candidateKey)
          ))
          const enrichedNotes = (await Promise.all(affectedNotes.map(async (current) => {
            const urls = current.urls.map((url) => {
              if (linkMetadataUrlKey(url) !== candidateKey) return url
              const enrichedUrl = enrichUrl(url, metadata, fetchedAt)
              return resetRetry ? enrichedUrl : {
                ...enrichedUrl,
                metadataFetchAttemptCount: url.metadataFetchAttemptCount,
                metadataNextRetryAt: url.metadataNextRetryAt,
              }
            })
            return repository.updateLinkMetadata(current.id, urls)
          }))).filter((note): note is Note => note !== undefined)
          for (const enriched of enrichedNotes) searchIndex.upsert(enriched)
          if (enrichedNotes.length > 0) {
            setNotes((existing) => enrichedNotes.reduce(updateNoteCollection, existing))
          }
          return enrichedNotes
        })
        commitQueueRef.current = task.then(() => null).catch(() => null)
        return task
      }

      const applyMetadataFailure = (failedAt: number) => {
        const task = commitQueueRef.current.then(async () => {
          const affectedNotes = [...notesRef.current.values()].filter((note) => (
            note.urls.some((url) => linkMetadataUrlKey(url) === candidateKey)
          ))
          const updatedNotes = (await Promise.all(affectedNotes.map(async (current) => (
            repository.updateLinkMetadata(current.id, current.urls.map((url) => (
              linkMetadataUrlKey(url) === candidateKey
                ? recordLinkMetadataFailure(url, failedAt)
                : url
            )))
          )))).filter((note): note is Note => note !== undefined)
          for (const updated of updatedNotes) searchIndex.upsert(updated)
          if (updatedNotes.length > 0) {
            setNotes((existing) => updatedNotes.reduce(updateNoteCollection, existing))
          }
          return updatedNotes
        })
        commitQueueRef.current = task.then(() => null).catch(() => null)
        return task
      }

      const applyMetadataDeferral = (deferredAt: number, retryAfterMs: number) => {
        const task = commitQueueRef.current.then(async () => {
          const affectedNotes = [...notesRef.current.values()].filter((note) => (
            note.urls.some((url) => linkMetadataUrlKey(url) === candidateKey)
          ))
          const updatedNotes = (await Promise.all(affectedNotes.map(async (current) => (
            repository.updateLinkMetadata(current.id, current.urls.map((url) => (
              linkMetadataUrlKey(url) === candidateKey
                ? deferLinkMetadataRetry(url, retryAfterMs, deferredAt)
                : url
            )))
          )))).filter((note): note is Note => note !== undefined)
          for (const updated of updatedNotes) searchIndex.upsert(updated)
          if (updatedNotes.length > 0) {
            setNotes((existing) => updatedNotes.reduce(updateNoteCollection, existing))
          }
          return updatedNotes
        })
        commitQueueRef.current = task.then(() => null).catch(() => null)
        return task
      }

      void repository
        .getCachedLinkMetadata(candidateKey)
        .then(async (cached) => {
          const cachedIsCurrent = cached?.metadataPolicyVersion === LINK_METADATA_POLICY_VERSION
          const cachedIsComplete = Boolean(
            cached && cachedIsCurrent && !isLinkMetadataIncomplete(cached.metadata, candidate),
          )
          const refreshIncomplete = Boolean(cached && cachedIsCurrent && !cachedIsComplete)
          if (cached && cachedIsCurrent) {
            await applyMetadata(cached.metadata, cached.fetchedAt, cachedIsComplete)
          }
          if (cachedIsComplete) {
            linkPreviewAttemptsRef.current.delete(attemptKey)
            updateLinkPreviewStatus(attemptKey, null)
            return null
          }
          if (!isOnline) {
            linkPreviewAttemptsRef.current.delete(attemptKey)
            updateLinkPreviewStatus(attemptKey, 'waiting')
            return null
          }
          return linkMetadataProvider
            .fetch(candidateKey, undefined, { refreshIncomplete })
            .then(async (metadata) => {
              const enriched = await applyMetadata(metadata, Date.now())
              const enrichedUrl = enriched
                .flatMap((note) => note.urls)
                .find((url) => linkMetadataUrlKey(url) === candidateKey)
              const retryDelay = enrichedUrl ? linkMetadataRetryDelayMs(enrichedUrl) : null
              if (retryDelay !== null) {
                linkPreviewAttemptsRef.current.delete(attemptKey)
                updateLinkPreviewStatus(attemptKey, 'partial')
              } else {
                linkPreviewAttemptsRef.current.delete(attemptKey)
                updateLinkPreviewStatus(attemptKey, null)
              }
              return enriched
            })
        })
        .catch((error: unknown) => {
          if (error instanceof LinkMetadataCoordinationError) {
            linkPreviewAttemptsRef.current.delete(attemptKey)
            scheduleRetry(attemptKey, error.retryAfterMs, 'waiting')
            return
          }
          if (error instanceof LinkMetadataRemoteBackoffError) {
            void applyMetadataDeferral(Date.now(), error.retryAfterMs)
              .then(() => {
                linkPreviewAttemptsRef.current.delete(attemptKey)
                updateLinkPreviewStatus(attemptKey, 'waiting')
              })
              .catch(() => {
                scheduleRetry(attemptKey, APP_SETTINGS.links.failedMetadataRetryBaseMs, 'waiting')
              })
            return
          }
          void applyMetadataFailure(Date.now())
            .then((updated) => {
              const updatedUrl = updated
                .flatMap((note) => note.urls)
                .find((url) => linkMetadataUrlKey(url) === candidateKey)
              const retryDelay = updatedUrl ? linkMetadataRetryDelayMs(updatedUrl) : null
              if (retryDelay === null) {
                linkPreviewAttemptsRef.current.delete(attemptKey)
                updateLinkPreviewStatus(attemptKey, 'pending')
                return
              }
              scheduleRetry(attemptKey, retryDelay, 'waiting')
            })
            .catch(() => {
              scheduleRetry(attemptKey, APP_SETTINGS.links.failedMetadataRetryBaseMs, 'waiting')
            })
        })
    }
  }, [
    isOnline,
    linkPreviewRetryRevision,
    loaded,
    notes,
    searchIndex,
    updateLinkPreviewStatus,
  ])

  useEffect(() => {
    if (!loaded || !linkMetadataProvider) return
    let nextRetryDelay: number | null = null
    for (const note of notes) {
      for (const url of note.urls) {
        const delay = linkMetadataRetryDelayMs(url)
        if (delay === null) continue
        nextRetryDelay = nextRetryDelay === null ? delay : Math.min(nextRetryDelay, delay)
      }
    }
    if (nextRetryDelay === null) return
    const timer = window.setTimeout(() => {
      setLinkPreviewRetryRevision((current) => current + 1)
    }, nextRetryDelay)
    return () => window.clearTimeout(timer)
  }, [loaded, notes])

  useEffect(() => () => {
    for (const timer of linkPreviewRetryTimersRef.current.values()) window.clearTimeout(timer)
    linkPreviewRetryTimersRef.current.clear()
  }, [])

  useEffect(() => {
    const persistBeforeBackground = () => {
      if (document.visibilityState !== 'hidden') return
      const snapshot = draftRef.current
      if (!hasDraftContent(snapshot)) {
        void repository.clearActiveDraft(snapshot.noteId)
        return
      }
      void repository.saveActiveDraft(snapshot)
      void commitSnapshot(snapshot)
    }
    document.addEventListener('visibilitychange', persistBeforeBackground)
    return () => document.removeEventListener('visibilitychange', persistBeforeBackground)
  }, [commitSnapshot])

  const flushDraft = useCallback(async (): Promise<Note | null> => {
    const current = draftRef.current
    // The editor keeps one disposable bottom line while open. Older drafts can
    // also contain terminal blank lines, so remove them at every durable exit.
    const body = current.body.replace(/(?:\r?\n[\t ]*)+$/, '')
    const snapshot = body === current.body ? current : { ...current, body, updatedAt: Date.now() }
    if (snapshot !== current) {
      draftRef.current = snapshot
      setDraft(snapshot)
    }
    return persistNavigationDraft(snapshot, notesRef.current.get(snapshot.noteId),
      pendingDraftCommitsRef.current.has(snapshot.noteId), {
        save: draft => repository.saveActiveDraft(draft),
        commit: commitSnapshot,
        discard: discardEmptySnapshot,
      })
  }, [commitSnapshot, discardEmptySnapshot])

  /**
   * Asks for a Context that strongly fits `note`. A "sheet" suggestion is offered once the note is closed and says so when
   * there is nothing to offer; a "top" one is offered while the note is still open (a shared link, say) and stays silent
   * unless the fit is strong.
   */
  const requestContextSuggestion = useCallback((note: Note, owner: string, flow: DevFlow, placement: 'top' | 'sheet') => {
    void (async () => {
      try {
        const records = await readManualContexts(owner)
        if (authSessionRef.current?.user.id !== owner || !smartSuggestionsEnabledRef.current) {
          flow.event('eligibility', 'skipped', 'account_or_toggle_changed'); return
        }
        if (!navigator.onLine) { flow.event('eligibility', 'skipped', 'offline'); return }
        const { suggestContexts } = await import('./intelligence/smartPilot/client')
        const result = await suggestContexts(owner, note, records, undefined, flow)
        if (authSessionRef.current?.user.id !== owner || !smartSuggestionsEnabledRef.current) {
          flow.event('decision', 'skipped', 'account_or_toggle_changed'); return
        }
        const ids = result.contexts.map(context => context.id).filter(id => !records.find(record => record.id === id)?.links.some(link => link.noteId === note.id))
        if (ids.length) { flow.event('decision', 'ok', 'suggested', { resultCount: ids.length }); setSmartSuggestion({ note, ids, records, flow, placement }) }
        else {
          flow.event('decision', 'ok', result.contexts.length ? 'already_connected' : 'no_strong_match')
          if (placement === 'sheet') void notify(result.contexts.length ? 'This note is already connected to the suggested Context.' : 'No strong Context suggestion for this note.')
        }
      } catch (error) {
        flow.event('decision', 'failed', devFlowReason(error))
        if (placement === 'sheet') void notify('Context suggestion could not be checked this time.')
      }
    })()
  }, [])

  // Notes that arrived by Share are offered a Context while they are still open, once their link details are in (or after a
  // short wait), instead of only when they are closed.
  const sharedOffersRef = useRef(new Map<string, { startedAt: number; state: 'waiting' | 'asked' }>())
  const [sharedOfferTick, setSharedOfferTick] = useState(0)
  const watchSharedNoteForContext = useCallback((noteId: string) => {
    sharedOffersRef.current.set(noteId, { startedAt: Date.now(), state: 'waiting' })
    window.setTimeout(() => setSharedOfferTick(tick => tick + 1), APP_SETTINGS.intelligence.contextSuggestion.sharedOfferWaitMs + 200)
  }, [])

  const offerNewNoteContext = useCallback((note: Note | null, noteId: string) => {
    const wasNew = pendingNewNoteIdsRef.current.delete(noteId)
    const startedFlow = pendingNewNoteFlowsRef.current.get(noteId)
    pendingNewNoteFlowsRef.current.delete(noteId)
    // A shared note was already offered a Context while it was open; closing it neither repeats that nor announces a miss.
    const shared = sharedOffersRef.current.get(noteId)
    sharedOffersRef.current.delete(noteId)
    if (shared?.state === 'asked') { setSmartSuggestion(current => current?.placement === 'top' && current.note.id === noteId ? null : current); return }
    if (!wasNew) return
    if (!smartSuggestionsEnabledRef.current) {
      startedFlow?.event('eligibility', 'skipped', 'toggle_off'); return
    }
    const owner = authSessionRef.current?.user.id
    if (!owner) { startedFlow?.event('eligibility', 'skipped', 'session_missing'); return }
    const flow = startedFlow ?? createDevFlow(owner, 'context_suggest', { noteId })
    if (note) flow.identifyNote(note.id, note.revisionId)
    flow.event('note_closed', 'started')
    if (!note) { flow.event('eligibility', 'skipped', 'empty_note'); return }
    if (!navigator.onLine) { flow.event('eligibility', 'skipped', 'offline'); return }
    requestContextSuggestion(note, owner, flow, 'sheet')
  }, [requestContextSuggestion])

  useEffect(() => {
    if (!sharedOffersRef.current.size || mode !== 'capture') return
    for (const [noteId, offer] of sharedOffersRef.current) {
      if (offer.state !== 'waiting' || draftRef.current.noteId !== noteId) continue
      const note = notesById.get(noteId)
      if (!note || note.status === 'trash') continue
      // Its links' details make the suggestion much better, so wait for them, but not for long.
      const settled = note.urls.every(url => deriveLinkMetadataStatus(url) === null)
      if (!settled && Date.now() - offer.startedAt < APP_SETTINGS.intelligence.contextSuggestion.sharedOfferWaitMs) continue
      offer.state = 'asked'
      const owner = authSession?.user.id
      if (!owner || !smartSuggestionsEnabledRef.current || !navigator.onLine) continue
      const flow = pendingNewNoteFlowsRef.current.get(noteId) ?? createDevFlow(owner, 'context_suggest', { noteId })
      pendingNewNoteFlowsRef.current.set(noteId, flow)
      flow.identifyNote(note.id, note.revisionId)
      flow.event('shared_note_open', 'started')
      requestContextSuggestion(note, owner, flow, 'top')
    }
  }, [authSession?.user.id, mode, notesById, requestContextSuggestion, sharedOfferTick])
  // The note to bring into view belongs to one arrival at one Context; leaving it forgets it.
  useEffect(() => { setContextFocus(current => current && current.contextId !== contextLabSelectedContextId ? null : current) }, [contextLabSelectedContextId])
  // A suggestion made for an open note goes when the note does.
  useEffect(() => { if (mode !== 'capture') setSmartSuggestion(current => current?.placement === 'top' ? null : current) }, [mode])

  const clearActiveNoteRoute = useCallback(() => {
    if (readNoteRoute()) clearNoteRoute()
    setRequestedNoteId(null)
    setNoteRoutePhase('none')
    setNoteRouteLocalChecked(false)
    setNoteRouteLocalFound(false)
    noteRouteOpenedRef.current = false
    noteRouteRenderedNoteRef.current = null
    noteRouteResolutionKeyRef.current = null
  }, [])

  const beginNewNote = useCallback(async (
    requireDurable = false,
    instantEntry = false,
    searchReturnState: SearchReturnState | null = null,
  ): Promise<boolean> => {
    if (!requireDurable && !accountCanAddNote(accountAccess)) {
      const limit = accountAccess?.capacities.notes
      void notify(`Your account has reached its ${limit?.toLocaleString()}-note limit. Move a note to trash or change account type before creating another.`)
      return false
    }
    if (composerTransitionRef.current) return false
    composerTransitionRef.current = true; composerTransitionSinceRef.current = Date.now()
    try {
      const previousNoteId = draftRef.current.noteId
      const replacedFlow = pendingNewNoteFlowsRef.current.get(previousNoteId)
      if (replacedFlow) {
        replacedFlow.event('eligibility', 'skipped', 'replaced_by_new_note')
        pendingNewNoteFlowsRef.current.delete(previousNoteId)
      }
      if (requireDurable) {
        await flushDraft()
        await repository.clearActiveDraft(previousNoteId)
      }
      clearActiveNoteRoute()
      searchReturnRef.current = searchReturnState
      const savePrevious = requireDurable ? Promise.resolve() : flushDraft().catch(() => undefined)
      const nextDraft = createBlankDraft()
      pendingNewNoteIdsRef.current.add(nextDraft.noteId)
      const owner = authSessionRef.current?.user.id
      if (owner && smartSuggestionsEnabledRef.current) {
        const flow = createDevFlow(owner, 'context_suggest', { noteId: nextDraft.noteId })
        flow.event('note_started', 'started')
        pendingNewNoteFlowsRef.current.set(nextDraft.noteId, flow)
      }
      userInteractedRef.current = false
      resetEditorHistory()
      closeOnDemandTools()
      draftRef.current = nextDraft
      setDraft(nextDraft)
      setSaveState('ready')
      setComposerInstantEntry(instantEntry)
      setComposerKey((current) => current + 1)
      setMode('capture')
      await savePrevious
      if (!requireDurable) await repository.clearActiveDraft(previousNoteId)
      return true
    } finally {
      composerTransitionRef.current = false
    }
  }, [accountAccess, clearActiveNoteRoute, closeOnDemandTools, flushDraft, resetEditorHistory])

  const leaveCapture = useCallback(async () => {
    if (composerTransitionRef.current) return
    composerTransitionRef.current = true; composerTransitionSinceRef.current = Date.now()
    setComposerClosing(true)
    try {
      const closingId = draftRef.current.noteId
      const [saved] = await Promise.all([
        flushDraft(),
        waitForComposerExit(),
      ])
      clearActiveNoteRoute()
      dismissComposer(setMode)
      offerNewNoteContext(saved, closingId)
    } catch {
      setSaveState('error')
      void notify.error('The note could not be saved. You are still in the editor.')
    } finally {
      setComposerClosing(false)
      composerTransitionRef.current = false
    }
  }, [clearActiveNoteRoute, flushDraft, offerNewNoteContext])

  const finishDraft = useCallback(async () => {
    if (composerTransitionRef.current) return
    composerTransitionRef.current = true; composerTransitionSinceRef.current = Date.now()
    setComposerClosing(true)
    const exitAnimation = waitForComposerExit()
    try {
      searchReturnRef.current = null
      const closingId = draftRef.current.noteId
      const saved = await flushDraft()
      await repository.clearActiveDraft()
      await exitAnimation
      clearActiveNoteRoute()
      userInteractedRef.current = false
      resetEditorHistory()
      closeOnDemandTools()
      setDraft(createBlankDraft())
      setSaveState('ready')
      setSearchQuery('')
      setSearchFilters({ status: 'all' })
      setSearchNavigationIndex(null)
      dismissComposer(setMode)
      offerNewNoteContext(saved, closingId)
    } catch {
      setSaveState('error')
    } finally {
      setComposerClosing(false)
      composerTransitionRef.current = false
    }
  }, [clearActiveNoteRoute, closeOnDemandTools, flushDraft, offerNewNoteContext, resetEditorHistory])

  const returnToSearchResultContext = useCallback(async () => {
    const returnState = searchReturnRef.current
    if (!returnState || composerTransitionRef.current) return
    composerTransitionRef.current = true; composerTransitionSinceRef.current = Date.now()
    setComposerClosing(true)
    const exitAnimation = waitForComposerExit()
    try {
      const closingId = draftRef.current.noteId
      const saved = await flushDraft()
      await repository.clearActiveDraft()
      await exitAnimation
      clearActiveNoteRoute()
      closeOnDemandTools()
      setSearchQuery(returnState.query)
      setSearchFilters({ ...returnState.filters })
      setSearchFiltersOpen(returnState.filtersOpen)
      const currentResults = searchIndex.search(returnState.query, returnState.filters)
      const activeResultIndex = returnState.activeResultId
        ? currentResults.findIndex((result) => result.id === returnState.activeResultId)
        : -1
      setSearchNavigationIndex(
        activeResultIndex >= 0 ? activeResultIndex : returnState.navigationIndex,
      )
      setSearchWindow({ ...returnState.window })
      searchRestoreViewportRef.current = returnState.viewport
      skipActiveSearchScrollRef.current = true
      searchReturnRef.current = null
      dismissComposer(setMode)
      offerNewNoteContext(saved, closingId)
    } catch {
      setSaveState('error')
    } finally {
      setComposerClosing(false)
      composerTransitionRef.current = false
    }
  }, [clearActiveNoteRoute, closeOnDemandTools, flushDraft, offerNewNoteContext, searchIndex])

  const openNote = useCallback(
    async (note: Note, matchTerm?: string, searchReturnState?: SearchReturnState) => {
      if (note.id !== draftRef.current.noteId) {
        const previousNoteId = draftRef.current.noteId
        try {
          await flushDraft()
          await repository.clearActiveDraft(previousNoteId)
        } catch {
          setSaveState('error')
          void notify.error('The note could not be saved. You are still in the editor.')
          return
        }
      }
      if (searchReturnState) {
        searchReturnRef.current = searchReturnState
        searchResultOpenScrollRef.current = true
      }
      resetEditorHistory()
      closeOnDemandTools()
      replaceNoteRoute(note.id)
      draftRef.current = draftFromNote(note)
      setDraft(draftRef.current)
      setMode('capture')
      setComposerInstantEntry(false)
      setComposerKey((current) => current + 1)
      setSaveState(storedNoteSaveState(note))
      const formFactor = studio.runtime.formFactor
      if (!shouldAutofocusExistingNote(formFactor)) {
        const activeElement = document.activeElement
        if (activeElement instanceof HTMLElement) activeElement.blur()
        return
      }
      requestAnimationFrame(() => {
        const editor = editorRef.current
        if (!editor) return
        editor.placeCaretAtEnd()
        if (!matchTerm) return
        editor.findAndSelect(matchTerm)
      })
    },
    [closeOnDemandTools, flushDraft, resetEditorHistory, studio.runtime.formFactor],
  )

  const persistNativeReminderAction = useCallback(
    async (
      noteId: string,
      updateReminder: (reminder: Reminder) => Reminder | null,
      successMessage: string,
    ): Promise<boolean> => {
      try {
        const note = notesRef.current.get(noteId)
        if (!note?.reminder) return false
        const reminder = updateReminder(note.reminder)
        const updated = { ...bumpNote(note, deviceId), reminder }
        await repository.commitLocalNote(updated)
        searchIndex.upsert(updated)
        setNotes((current) => updateNoteCollection(current, updated))
        requestSyncRef.current({ push: true, domains: [] })

        const notifications = getNativeReminderNotifications(window)
        if (notifications) {
          const reminders = reminderScheduleInputs([...[...notesRef.current.values()].filter((candidate) => candidate.id !== updated.id), updated])
          await reconcileNativeReminderNotifications(reminders, { plugin: notifications })
        }
        void notify.success(successMessage)
        return true
      } catch {
        void notify.error('Could not update this reminder.')
        return false
      }
    },
    [deviceId, searchIndex],
  )

  const acknowledgeNativeReminder = useCallback(
    async (noteId: string, occurrenceAt: number | null) => {
      return await persistNativeReminderAction(noteId, (reminder) => {
        // An action can arrive after another device has already advanced this series.
        // In that case, preserve the newer schedule instead of acknowledging it twice.
        if (occurrenceAt !== null && reminder.nextAt > occurrenceAt + 1_000) return reminder
        return acknowledgeReminder(reminder)
      }, 'Reminder acknowledged')
    },
    [persistNativeReminderAction],
  )

  const snoozeNativeReminder = useCallback(
    async (noteId: string, durationMs: number) => await persistNativeReminderAction(
      noteId,
      (reminder) => snoozeReminder(reminder, durationMs),
      'Reminder snoozed',
    ),
    [persistNativeReminderAction],
  )

  useEffect(() => {
    const notifications = getNativeReminderNotifications(window)
    if (!notifications) return
    let disposed = false
    let listener: { remove: () => Promise<void> } | null = null
    void notifications.addListener('localNotificationActionPerformed', (action) => {
      if (disposed) return
      dispatchNativeReminderNotificationAction(action, {
        openNote: (noteId) => {
          replaceNoteRoute(noteId)
          setRequestedNoteId(noteId)
          // Notification taps can target the note currently displayed; force the
          // existing local-first route resolver to handle that warm interaction too.
          setNoteRouteResolveAttempt((current) => current + 1)
        },
        queueAction: (notificationAction) => {
          setPendingNativeReminderAction({
            action: notificationAction.action,
            noteId: notificationAction.noteId,
            occurrenceAt: notificationAction.occurrenceAt,
          })
        },
      })
    }).then((handle) => {
      listener = handle
      if (disposed) void handle.remove()
    }).catch(() => undefined)
    return () => {
      disposed = true
      if (listener) void listener.remove()
    }
  }, [])

  useEffect(() => {
    if (!loaded || !pendingNativeReminderAction) return
    const note = notesRef.current.get(pendingNativeReminderAction.noteId)
    if (!note?.reminder) return
    const action = pendingNativeReminderAction
    setPendingNativeReminderAction(null)
    if (action.action === 'acknowledge') {
      void acknowledgeNativeReminder(action.noteId, action.occurrenceAt)
      return
    }
    setReminderEditorOpen(false)
    setNativeReminderSnoozeNoteId(action.noteId)
  }, [acknowledgeNativeReminder, loaded, notes, pendingNativeReminderAction])

  useEffect(() => {
    const notifications = getNativeReminderNotifications(window)
    if (!loaded || !notifications) return
    const timer = window.setTimeout(() => {
      void reconcileNativeReminderNotifications(reminderScheduleInputs(notes), { plugin: notifications }).catch(() => undefined)
    }, APP_SETTINGS.nativeNotifications.reconciliationDebounceMs)
    return () => window.clearTimeout(timer)
  }, [loaded, notes])

  useEffect(() => {
    if (!loaded || !getNativeReminderNotifications(window)) return
    let disposed = false
    let listener: { remove: () => Promise<void> } | null = null
    const reconcileAfterNativeLifecycleChange = () => {
      const notifications = getNativeReminderNotifications(window)
      if (!notifications) return
      const reminders = reminderScheduleInputs(notesRef.current.values())
      // Exact-alarm access can be changed outside the app. Rebuild our owned jobs
      // on return so Android receives an inexact fallback after revocation, or
      // precise schedules again after access is restored, without another prompt.
      void reconcileNativeReminderNotifications(reminders, { plugin: notifications }).catch(() => undefined)
    }
    void import('@capacitor/app').then(async ({ App: CapacitorApp }) => {
      const handle = await CapacitorApp.addListener('appStateChange', ({ isActive }) => {
        if (isActive) reconcileAfterNativeLifecycleChange()
      })
      if (disposed) void handle.remove()
      else listener = handle
    }).catch(() => undefined)
    return () => {
      disposed = true
      if (listener) void listener.remove()
    }
  }, [loaded])

  const presentRoutedNote = useCallback(async (note: Note) => {
    const previouslyRendered = notesRef.current.get(note.id) ?? noteRouteRenderedNoteRef.current
    const alreadyOpen = noteRouteOpenedRef.current && draftRef.current.noteId === note.id
    searchIndex.upsert(note)
    setNotes((current) => mergeSynchronizedNotes(current, [note]))

    if (!alreadyOpen) {
      await openNote(note)
      noteRouteOpenedRef.current = true
    } else if (previouslyRendered) {
      const replacement = synchronizedDraftReplacement(
        draftRef.current,
        previouslyRendered,
        note,
      )
      if (replacement) {
        draftRef.current = replacement
        resetEditorHistory()
        setDraft(replacement)
        setSaveState(storedNoteSaveState(note))
        void repository.saveActiveDraft(replacement).catch(() => setSaveState('error'))
      }
    }

    noteRouteRenderedNoteRef.current = note
    setNoteRoutePhase('open')
  }, [openNote, resetEditorHistory, searchIndex])

  useEffect(() => {
    if (noteRoutePhase !== 'open' || draft.noteId !== requestedNoteId) return
    return revealPaintedNativeNote(draft.noteId)
  }, [draft.noteId, noteRoutePhase, requestedNoteId])

  useEffect(() => {
    if (!requestedNoteId) return
    let cancelled = false
    setNoteRoutePhase('resolving')
    setNoteRouteLocalChecked(false)
    setNoteRouteLocalFound(false)
    noteRouteOpenedRef.current = false
    noteRouteRenderedNoteRef.current = null

    void repository.getNote(requestedNoteId)
      .then(async (note) => {
        if (cancelled) return
        if (note) await presentRoutedNote(note)
        if (cancelled) return
        setNoteRouteLocalFound(Boolean(note))
        setNoteRouteLocalChecked(true)
        if (note) initialWidgetRoutePriorityGate.releaseAfterPaint()
        else initialWidgetRoutePriorityGate.dispose()
      })
      .catch(() => {
        if (cancelled) return
        setNoteRouteLocalChecked(true)
        setNoteRoutePhase('error')
        initialWidgetRoutePriorityGate.dispose()
      })
    return () => { cancelled = true }
  }, [initialWidgetRoutePriorityGate, noteRouteResolveAttempt, presentRoutedNote, requestedNoteId])

  useEffect(() => {
    if (!requestedNoteId || !noteRouteLocalChecked) return
    if (!isOnline) {
      if (!noteRouteLocalFound) setNoteRoutePhase('offline')
      return
    }
    if (authRestoring) {
      if (!noteRouteLocalFound) setNoteRoutePhase('resolving')
      return
    }
    if (!authenticatedUserId) {
      if (!noteRouteLocalFound) {
        setNoteRoutePhase(authProvider.id === 'local' ? 'error' : 'sign-in')
      }
      return
    }
    if (!syncCoordinator) {
      if (!noteRouteLocalFound) setNoteRoutePhase('error')
      return
    }

    const resolutionKey = `${authenticatedUserId}:${requestedNoteId}:${noteRouteResolveAttempt}`
    if (noteRouteResolutionKeyRef.current === resolutionKey) return
    noteRouteResolutionKeyRef.current = resolutionKey
    let cancelled = false
    if (!noteRouteLocalFound) setNoteRoutePhase('resolving')
    const noteResolution = syncCoordinator.resolveNote(authenticatedUserId, requestedNoteId)
    const assetResolution = assetTransport
      ? assetTransport.pullForNote(requestedNoteId).catch(() => [])
      : Promise.resolve([])

    void Promise.all([noteResolution, assetResolution])
      .then(async ([note, remoteAssets]) => {
        if (cancelled) return
        if (remoteAssets.length > 0) {
          const appliedAssets = await Promise.all(
            remoteAssets.map((asset) => repository.applyRemoteAsset(asset)),
          )
          if (cancelled) return
          setAssets((current) => {
            const byId = new Map(current.map((asset) => [asset.id, asset]))
            for (const asset of appliedAssets) byId.set(asset.id, asset)
            return [...byId.values()]
          })
        }
        if (note) {
          await presentRoutedNote(note)
          if (!cancelled) setNoteRouteLocalFound(true)
        } else if (!noteRouteLocalFound && !cancelled) {
          setNoteRoutePhase('not-found')
        }
      })
      .catch((error: unknown) => {
        if (cancelled) return
        if (noteRouteLocalFound) {
          setNoteRoutePhase('open')
          setSyncError(error instanceof Error ? error.message : 'The server copy could not be checked.')
        } else {
          setNoteRoutePhase('error')
        }
      })
    return () => { cancelled = true }
  }, [
    authRestoring,
    authenticatedUserId,
    isOnline,
    noteRouteLocalChecked,
    noteRouteLocalFound,
    noteRouteResolveAttempt,
    presentRoutedNote,
    requestedNoteId,
  ])

  const persistNoteStatus = useCallback(
    async (note: Note, status: NoteStatus) => {
      if (modeRef.current === 'search') pendingBrowseViewportRef.current = captureBrowseViewport()
      // A restore brings the note's images back into the account's count; refuse it while that would not fit.
      if (note.status === 'trash' && status !== 'trash') {
        await assertRestoreFitsImageLimit(repository, authSessionRef.current?.user.id, note.id)
      }
      const updated = transitionNoteStatus(note, status, deviceId)
      await repository.commitLocalNote(updated, status === 'trash' ? 'delete' : 'edit')
      searchIndex.upsert(updated)
      setNotes((current) => updateNoteCollection(current, updated))
      setDraft((current) =>
        current.noteId === updated.id
          ? { ...current, expiresAt: updated.expiresAt, updatedAt: Date.now() }
          : current,
      )
      return updated
    },
    [deviceId, searchIndex],
  )

  const changeNoteStatus = useCallback(
    async (note: Note, status: NoteStatus) => {
      let updated: Note
      try {
        updated = await persistNoteStatus(note, status)
      } catch (error) {
        // A plan limit is a refusal with a next step, not a failure: nothing changes and the note stays where it was.
        if (error instanceof PlanLimitError) {
          void notify.error(error.message)
          return note
        }
        throw error
      }
      if (status === 'trash') {
        void notify('Moved to trash', {
          duration: 5000,
          action: {
            label: 'Undo',
            onClick: () => {
              const latest = notesRef.current.get(updated.id) ?? updated
              void persistNoteStatus(latest, 'inbox')
                .then(() => notify.success('Note restored'))
                .catch((error) => notify.error(error instanceof PlanLimitError ? error.message : 'Could not restore the note'))
            },
          },
        })
      }
      return updated
    },
    [persistNoteStatus],
  )

  const animateSearchNoteRemoval = useCallback(async (
    targetNotes: readonly Note[],
    update: () => void | Promise<unknown>,
  ) => {
    const targetIds = new Set(targetNotes.map((note) => note.id))
    if (targetIds.size === 0) return
    setRemovingSearchNoteIds((current) => new Set([...current, ...targetIds]))
    const reduceMotion = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches
    await new Promise<void>((resolve) => window.setTimeout(
      resolve,
      reduceMotion ? 0 : APP_SETTINGS.interactions.searchResultRemovalAnimationMs,
    ))
    try {
      await runNoteCardTransition(update)
    } finally {
      setRemovingSearchNoteIds((current) => {
        const next = new Set(current)
        for (const id of targetIds) next.delete(id)
        return next
      })
    }
  }, [])

  const saveActiveReminder = useCallback(
    async (reminder: Reminder | null) => {
      try {
        const committed = await flushDraft().catch(() => null)
        const note = committed ?? notesRef.current.get(draftRef.current.noteId)
        if (!note) {
          void notify.error('Add something to this note before setting a reminder.')
          return false
        }
        const nativeReminderNotifications = getNativeReminderNotifications(window)
        if (nativeReminderNotifications && reminder) {
          const permission = await requestNativeReminderPermission(nativeReminderNotifications)
          if (permission === 'denied') {
            void notify.error('Allow notifications to receive this reminder on Android.')
            return false
          }
        }
        const updated = {
          ...bumpNote(note, deviceId),
          reminder,
        }
        await repository.commitLocalNote(updated)
        searchIndex.upsert(updated)
        setNotes((current) => updateNoteCollection(current, updated))
        requestSyncRef.current({ push: true, domains: [] })
        if (nativeReminderNotifications) {
          try {
            const reminders = reminderScheduleInputs([...[...notesRef.current.values()].filter((candidate) => candidate.id !== updated.id), updated])
            const scheduled = await reconcileNativeReminderNotifications(
              reminders,
              { requestExactAlarm: Boolean(reminder), plugin: nativeReminderNotifications },
            )
            if (scheduled.status === 'scheduled' && !scheduled.exact && reminder) {
              void notify('Reminder set. Android may delay it while battery saving is active.')
            }
          } catch {
            void notify.error('Reminder saved, but Android could not schedule its notification.')
          }
        }
        void notify.success(reminder ? `Reminder set for ${reminderNaturalTime(reminder)}` : 'Reminder removed')
        return true
      } catch {
        void notify.error('Could not save this reminder locally.')
        return false
      }
    },
    [deviceId, flushDraft, searchIndex],
  )

  const bumpNoteToTop = useCallback(
    async (note: Note) => {
      if (modeRef.current === 'search') pendingBrowseViewportRef.current = captureBrowseViewport()
      let newestModifiedAt = Date.now()
      for (const candidate of notesRef.current.values()) {
        newestModifiedAt = Math.max(newestModifiedAt, candidate.modifiedAt + 1)
      }
      const updated = bumpNote(note, deviceId, newestModifiedAt)
      await repository.commitLocalNote(updated)
      searchIndex.upsert(updated)
      setNotes((current) => updateNoteCollection(current, updated))
      requestSyncRef.current({ push: true, domains: [] })
    },
    [deviceId, searchIndex],
  )

  const changeActiveStatus = useCallback(
    async (status: NoteStatus) => {
      const committed = await flushDraft().catch(() => null)
      const note = committed ?? notesRef.current.get(draftRef.current.noteId)
      if (!note) return
      await changeNoteStatus(note, status)
      if (status === 'trash') {
        if (searchReturnRef.current) {
          await returnToSearchResultContext()
          return
        }
        resetEditorHistory()
        closeOnDemandTools()
        clearActiveNoteRoute()
        setDraft(createBlankDraft())
        setSaveState('ready')
        await repository.clearActiveDraft()
        startTransition(() => setMode('search'))
      }
    },
    [
      changeNoteStatus,
      clearActiveNoteRoute,
      closeOnDemandTools,
      flushDraft,
      resetEditorHistory,
      returnToSearchResultContext,
    ],
  )

  const updateDraft = useCallback((update: (current: ActiveDraft) => ActiveDraft) => {
    userInteractedRef.current = true
    setDraft((current) => {
      const next = { ...update(current), updatedAt: Date.now() }
      draftRef.current = next
      return next
    })
  }, [])

  const addImageFiles = useCallback(async (incomingFiles: Iterable<File>): Promise<string[]> => {
    const accepted: Array<{ asset: NoteAsset; file: File }> = []
    for (const file of incomingFiles) {
      const validationError = validateImageFile(file)
      if (validationError) {
        void notify.error(validationError)
        continue
      }
      const asset = {
        ...createLocalImageAsset(createId(), draftRef.current.noteId, file),
        originalMimeType: normalizedImageMimeType(file) ?? file.type,
      }
      accepted.push({ asset, file })
    }
    if (accepted.length === 0) return []

    // The plan's image limit, as last told to this device, applies across the whole account (offline too).
    const capacity = await imageCapacity(repository, authSessionRef.current?.user.id)
    let usable = accepted
    if (capacity && accepted.length > capacity.room) {
      usable = accepted.slice(0, capacity.room)
      void notify.error(usable.length === 0
        ? planLimitMessage('images', capacity.limit)
        : `Added ${usable.length} of ${accepted.length} images. ${planLimitMessage('images', capacity.limit)}`)
    }
    if (usable.length === 0) return []

    const addedAssets = usable.map(({ asset }) => asset)
    const assetIds = [...new Set([
      ...(draftRef.current.assetIds ?? []),
      ...addedAssets.map((asset) => asset.id),
    ])]
    const durableDraft = {
      ...draftRef.current,
      assetIds,
      updatedAt: Date.now(),
    }
    userInteractedRef.current = true
    draftRef.current = durableDraft
    setDraft(durableDraft)
    setAssets((current) => [...current, ...addedAssets])
    recordDiagnosticMilestone('image:selected', { count: addedAssets.length })

    return repository.addAssetsToDraft(addedAssets, durableDraft)
      .then(() => {
        recordDiagnosticMilestone('image:local-persist-complete', { count: addedAssets.length })
        void imageQueue.wake(authSessionRef.current?.user.id ?? null)
        return addedAssets.map((asset) => asset.id)
      })
      .catch(() => {
        const failedIds = new Set(addedAssets.map((asset) => asset.id))
        setAssets((current) => current.filter((asset) => !failedIds.has(asset.id)))
        setDraft((current) => {
          if (current.noteId !== durableDraft.noteId) return current
          const corrected = {
            ...current,
            assetIds: (current.assetIds ?? []).filter((assetId) => !failedIds.has(assetId)),
            updatedAt: Date.now(),
          }
          draftRef.current = corrected
          return corrected
        })
        setSaveState('error')
        void notify.error('The image could not be saved offline.')
        return []
      })
  }, [])

  // While the person types, a phone shows the formatting bar and Done; reading a note, it shows neither.
  const [editorTyping, setEditorTyping] = useState(false)
  // The bar shortens the note as it appears, with no window resize: keep the line being typed above it (utils/visualViewport.ts).
  useEffect(() => {
    if (!editorTyping) return
    const frame = window.requestAnimationFrame(() => revealFocusedLine())
    return () => window.cancelAnimationFrame(frame)
  }, [editorTyping])
  const setNativeEditorFocused = useCallback((focused: boolean) => {
    nativeEditorFocusedRef.current = focused
    setEditorTyping(focused)
    const plugin = getNativeIntentInbox(window)
    if (!plugin) return
    const noteId = focused && modeRef.current === 'capture'
      ? draftRef.current.noteId
      : null
    void plugin.setActiveEditor({ noteId }).catch(() => undefined)
  }, [])

  useEffect(() => {
    const plugin = getNativeIntentInbox(window)
    if (!plugin) return
    const noteId = mode === 'capture' && nativeEditorFocusedRef.current
      ? draft.noteId
      : null
    void plugin.setActiveEditor({ noteId }).catch(() => undefined)
  }, [draft.noteId, mode])

  // Images the person adds to a note (picked, dropped or pasted) are unpacked by themselves, like a share.
  const addImagesAndUnpack = useCallback(async (files: Iterable<File>) => {
    const added = await addImageFiles(files)
    if (added.length > 0) setCleanupAuto({ noteId: draftRef.current.noteId, assetIds: added })
    return added
  }, [addImageFiles])

  const chooseImages = useCallback((event: ChangeEvent<HTMLInputElement>) => {
    if (event.target.files) void addImagesAndUnpack(event.target.files)
    event.target.value = ''
  }, [addImagesAndUnpack])

  const recordImagePreviewVisible = useCallback((asset: NoteAsset) => {
    if (imagePreviewMilestonesRef.current.has(asset.id)) return
    imagePreviewMilestonesRef.current.add(asset.id)
    recordDiagnosticMilestone('image:preview-visible')
  }, [])

  const removeImage = useCallback((asset: NoteAsset) => {
    updateDraft((current) => ({
      ...current,
      assetIds: (current.assetIds ?? []).filter((assetId) => assetId !== asset.id),
    }))
    setAssets((current) => current.map((candidate) =>
      candidate.id === asset.id ? { ...candidate, state: 'deleted' as const } : candidate,
    ))
    void repository.markAssetDeleted(asset.id).then(() => imageQueue.wake(authSession?.user.id ?? null))
  }, [authSession?.user.id, updateDraft])

  const handleImageDrop = useCallback((event: DragEvent<HTMLElement>) => {
    event.preventDefault()
    setImageDropActive(false)
    const files = [...event.dataTransfer.files]
    if (files.length > 0) void addImagesAndUnpack(files)
  }, [addImagesAndUnpack])

  const appendSharedBody = useCallback((body: string) => {
    if (!body) return
    const formattedBody = formatNoteUrlsAsMarkdown(body)
    const current = draftRef.current
    const sharedDraft = {
      ...current,
      body: current.body ? `${current.body}\n${formattedBody}` : formattedBody,
      updatedAt: Date.now(),
    }
    userInteractedRef.current = true
    draftRef.current = sharedDraft
    setDraft(sharedDraft)
  }, [])

  const consumingShareIds = useRef(new Set<string>())
  const consumeShareIntent = useCallback(async (
    intent: Extract<AppLaunchIntent, { kind: 'capture' }>,
    includeUrlBody: boolean,
  ) => {
    if (intent.shareError) notify.error('The shared image could not be opened.')
    if (includeUrlBody) appendSharedBody(intent.sharedBody)
    if (!intent.shareId || consumingShareIds.current.has(intent.shareId)) return
    consumingShareIds.current.add(intent.shareId)
    const workspace = activeWorkspace()
    if (!workspace) return
    const binding = workspaceDatabaseName(workspace.scope, workspace.owner)
    if (await wasShareAdopted(intent.shareId)) { await takeIncomingShare(intent.shareId, binding); return }
    const share = await takeIncomingShare(intent.shareId, binding, false)
    assertWorkspaceActive()
    if (!share) { notify.error('This share is unavailable in the current workspace. Its retained content has not been removed.'); return }
    // A shared screenshot's own file label ("Screenshot (22 Oct …)") is not the person's words.
    appendSharedBody(share.files.length > 0 && isSharedFileLabel(share.body) ? '' : share.body)
    const adoptedAssets = await addImageFiles(share.files.map((file) => new File([file.blob], file.name, {
      type: file.type,
      lastModified: file.lastModified,
    })))
    if (adoptedAssets.length !== share.files.length) {
      await repository.saveActiveDraft(draftRef.current)
      notify.error('Some shared images could not be saved. The original share is retained in this account.')
      return
    }
    await commitSharedDraft(intent.shareId, draftRef.current)
    assertWorkspaceActive()
    if (share.original) {
      await writeShareOriginal(draftRef.current.noteId, {
        tidy: formatNoteUrlsAsMarkdown(share.body),
        original: formatNoteUrlsAsMarkdown(share.original),
        mode: 'tidy',
      })
    }
    await takeIncomingShare(intent.shareId, binding)
    if (adoptedAssets.length > 0) setCleanupAuto({ noteId: draftRef.current.noteId, assetIds: adoptedAssets })
  }, [addImageFiles, appendSharedBody])

  useEffect(() => {
    if (initialLaunchIntent.kind !== 'capture' || initialLaunchIntent.source !== 'share') return
    void consumeShareIntent(initialLaunchIntent, false)
      .catch(() => notify.error('The shared image could not be opened.'))
  }, [consumeShareIntent])

  const dismissLaunchSurfaces = useCallback(() => {
    closeOnDemandTools()
    setActiveImagePreview(null)
    if (accountPanelOpen) suppressAccountPanelFocusRestoreRef.current = true
    setAccountPanelOpen(false)
    // Launcher routes are primary app entry points, not overlays on the admin page.
    // The dispatcher replaces the content-free launch URL after acceptance.
    setAdminRouteActive(false)
  }, [accountPanelOpen, closeOnDemandTools])

  const acceptLaunchIntent = useCallback(async (intent: AppLaunchIntent) => {
    await applyAppLaunchIntent(intent, {
      beginNewCapture: () => beginNewNote(true, captureEntryIsInstant(intent)),
      consumeSharedCapture: (captureIntent) => consumeShareIntent(captureIntent, true),
      preserveCurrentDraft: async () => { await flushDraft() },
      dismissTransientSurfaces: dismissLaunchSurfaces,
      showSearch: (focus, collection = false) => {
        setContextLabBrowseOverride(!collection)
        setContextCollectionOpen(collection)
        setContextLabSelectedContextId(null)
        clearActiveNoteRoute()
        setMobileSearchSurfaceOpen(focus)
        setMode('search')
        if (focus) requestAnimationFrame(() => searchRef.current?.focus({ preventScroll: true }))
      },
      showContext: (contextId) => {
        clearActiveNoteRoute()
        replaceContextRoute(contextId)
        setContextLabSelectedContextId(contextId)
        setContextLabBrowseOverride(false)
        setMode('search')
      },
      showNote: (noteId) => {
        replaceNoteRoute(noteId)
        setRequestedNoteId(noteId)
        // A warm shortcut/App Link may target the note that is already requested.
        // Advance the generation so same-ID launches rerun local-first resolution.
        setNoteRouteResolveAttempt((current) => current + 1)
        setNoteRoutePhase('resolving')
        setNoteRouteLocalChecked(false)
        setNoteRouteLocalFound(false)
        noteRouteOpenedRef.current = false
        noteRouteRenderedNoteRef.current = null
        noteRouteResolutionKeyRef.current = null
      },
    })
  }, [
    beginNewNote,
    clearActiveNoteRoute,
    consumeShareIntent,
    dismissLaunchSurfaces,
    flushDraft,
  ])

  const acceptLaunchIntentRef = useRef(acceptLaunchIntent)
  acceptLaunchIntentRef.current = acceptLaunchIntent
  const launchDispatcherRef = useRef<ReturnType<typeof createAppLaunchDispatcher> | null>(null)
  if (!launchDispatcherRef.current) {
    launchDispatcherRef.current = createAppLaunchDispatcher({
      appOrigin: window.location.origin,
      readStartMode: readBootstrapStartMode,
      duplicateWindowMs: APP_SETTINGS.interactions.launchIntentDuplicateWindowMs,
      initialIntent: initialLaunchIntent,
      accept: (intent) => acceptLaunchIntentRef.current(intent),
      onAccepted: (intent, targetUrl) => {
        const cleanPath = cleanAppLaunchPath(intent, targetUrl)
        window.history.replaceState(window.history.state, '', cleanPath)
        recordDiagnosticMilestone('launch:intent-accepted', {
          kind: intent.kind,
          source: intent.source,
        })
      },
      onError: () => {
        notify.error('miniMany could not finish opening that action. Your current note was kept.')
      },
    })
  }
  const launchDispatcher = launchDispatcherRef.current

  useEffect(() => subscribeToWebLaunches((targetUrl) => {
    void launchDispatcher.dispatch(targetUrl)
  }), [launchDispatcher])

  useEffect(() => {
    if (!nativeIntentReady || authRestoring) return
    const plugin = getNativeIntentInbox(window)
    if (!plugin) return
    let disposed = false
    let intentListener: { remove: () => Promise<void> } | null = null
    let appStateListener: { remove: () => Promise<void> } | null = null

    const adoptCapture = async (envelope: NativeIntentEnvelope): Promise<void> => {
      recordDiagnosticMilestone('native-intent:adoption-started', { kind: 'capture' })
      const text = nativeCaptureText(envelope)
      let transitionWaits = 0
      while (composerTransitionRef.current) {
        if (transitionWaits >= APP_SETTINGS.nativeIntents.maxAttempts) {
          throw new Error('Native Capture handoff could not enter the composer.')
        }
        transitionWaits += 1
        await waitForNativeHandoffDelay(APP_SETTINGS.nativeIntents.handoffRetryMs)
      }

      composerTransitionRef.current = true; composerTransitionSinceRef.current = Date.now()
      document.documentElement.dataset.nativeIntentHandoff = 'capture'
      try {
        if (draftRef.current.noteId !== envelope.id) await flushDraft()
        const adoption = await repository.adoptNativeCaptureDraft({
          id: envelope.id,
          revision: envelope.revision,
          text,
          createdAt: envelope.createdAt,
          updatedAt: envelope.updatedAt,
        })

        clearActiveNoteRoute()
        searchReturnRef.current = null
        closeOnDemandTools()
        setActiveImagePreview(null)
        suppressAccountPanelFocusRestoreRef.current = true
        setAccountPanelOpen(false)
        setAdminRouteActive(false)
        resetEditorHistory()
        userInteractedRef.current = text.length > 0
        if (!notesRef.current.has(adoption.draft.noteId)) { pendingNewNoteIdsRef.current.add(adoption.draft.noteId); watchSharedNoteForContext(adoption.draft.noteId) }
        draftRef.current = adoption.draft
        setDraft(adoption.draft)
        setSaveState('local')
        setComposerInstantEntry(true)
        setComposerClosing(false)
        setComposerKey((current) => current + 1)
        setMode('capture')

        await waitForNativeHandoffFrame()
        await waitForNativeHandoffFrame()
        editorRef.current?.placeCaretAtEnd()
        await waitForNativeHandoffFrame()
        await plugin.acknowledge({ id: envelope.id, revision: envelope.revision })
        await waitForNativeHandoffDelay(
          APP_SETTINGS.nativeIntents.nativeSurfaceFadeMs
          + APP_SETTINGS.nativeIntents.handoffRetryMs,
        )
        editorRef.current?.placeCaretAtEnd()
        await waitForNativeHandoffFrame()
        recordDiagnosticMilestone('native-intent:adoption-complete', { kind: 'capture' })
      } finally {
        delete document.documentElement.dataset.nativeIntentHandoff
        composerTransitionRef.current = false
      }
    }

    const adoptSearch = async (envelope: NativeIntentEnvelope): Promise<void> => {
      setContextLabBrowseOverride(true)
      recordDiagnosticMilestone('native-intent:adoption-started', { kind: 'search' })
      const query = nativeSearchQuery(envelope)
      document.documentElement.dataset.nativeIntentHandoff = 'search'
      try {
        await flushDraft()
        clearActiveNoteRoute()
        searchReturnRef.current = null
        closeOnDemandTools()
        setActiveImagePreview(null)
        suppressAccountPanelFocusRestoreRef.current = true
        setAccountPanelOpen(false)
        setAdminRouteActive(false)
        setSearchFilters({ status: 'all' })
        setSearchFiltersOpen(false)
        setSearchNavigationIndex(null)
        setSearchQuery(query)
        setMobileSearchSurfaceOpen(true)
        setMode('search')

        let inputReady = false
        for (let attempt = 0; attempt < APP_SETTINGS.nativeIntents.maxAttempts; attempt += 1) {
          await waitForNativeHandoffFrame()
          const input = searchRef.current
          if (input && input.value === query) {
            input.focus({ preventScroll: true })
            inputReady = true
            break
          }
        }
        if (!inputReady) throw new Error('Native Search handoff could not enter the final field.')
        recordDiagnosticMilestone('native-intent:search-field-ready')
        // The search input remains mounted underneath an outgoing routed-note
        // surface. Require two final paints and revalidate focus before native code
        // removes the exact-revision route cover.
        await waitForNativeHandoffFrame()
        await waitForNativeHandoffFrame()
        if (searchRef.current?.value !== query) {
          throw new Error('Native Search handoff lost its final field before reveal.')
        }
        searchRef.current.focus({ preventScroll: true })
        await plugin.acknowledge({ id: envelope.id, revision: envelope.revision })
        await waitForNativeHandoffDelay(
          APP_SETTINGS.nativeIntents.nativeSurfaceFadeMs
          + APP_SETTINGS.nativeIntents.handoffRetryMs,
        )
        searchRef.current?.focus({ preventScroll: true })
        await waitForNativeHandoffFrame()
        recordDiagnosticMilestone('native-intent:adoption-complete', { kind: 'search' })
      } finally {
        delete document.documentElement.dataset.nativeIntentHandoff
      }
    }

    const adoptShare = async (envelope: NativeIntentEnvelope): Promise<void> => {
      recordDiagnosticMilestone('native-intent:adoption-started', { kind: 'share' })
      const payload = nativeSharePayload(envelope)
      const incomingAssets: NoteAsset[] = []
      for (const metadata of payload.files) {
        const local = await plugin.getShareFile({ id: envelope.id, token: metadata.token })
        if (
          typeof local.uri !== 'string'
          || !local.uri.startsWith('file://')
          || local.size !== metadata.size
        ) {
          throw new Error('Native Share file reference is invalid.')
        }
        const response = await fetch(Capacitor.convertFileSrc(local.uri), { cache: 'no-store' })
        if (!response.ok) throw new Error('Native Share file could not be loaded.')
        const blob = await response.blob()
        if (blob.size !== metadata.size) throw new Error('Native Share file size changed.')
        const file = new File([blob], metadata.name, {
          type: metadata.mimeType,
          lastModified: metadata.lastModified,
        })
        const validationError = validateImageFile(file)
        if (validationError || normalizedImageMimeType(file) !== metadata.mimeType) {
          throw new Error('Native Share file failed web validation.')
        }
        incomingAssets.push({
          ...createLocalImageAsset(metadata.token, envelope.id, file, envelope.updatedAt),
          originalMimeType: metadata.mimeType,
        })
      }

      let transitionWaits = 0
      while (composerTransitionRef.current) {
        if (transitionWaits >= APP_SETTINGS.nativeIntents.maxAttempts) {
          throw new Error('Native Share handoff could not enter the composer.')
        }
        transitionWaits += 1
        await waitForNativeHandoffDelay(APP_SETTINGS.nativeIntents.handoffRetryMs)
      }

      composerTransitionRef.current = true; composerTransitionSinceRef.current = Date.now()
      document.documentElement.dataset.nativeIntentHandoff = 'share'
      try {
        if (draftRef.current.noteId !== envelope.id) await flushDraft()
        const { body: formattedBody, original: shareOriginal } = capturedShare(incomingAssets.length > 0 && isSharedFileLabel(payload.body) ? '' : payload.body, payload.sourceTitle)
        const adoption = await repository.adoptNativeShareDraft({
          id: envelope.id,
          revision: envelope.revision,
          body: formattedBody,
          ...(shareOriginal ? { shareOriginal } : {}),
          assets: incomingAssets,
          createdAt: envelope.createdAt,
          updatedAt: envelope.updatedAt,
        })

        clearActiveNoteRoute()
        searchReturnRef.current = null
        closeOnDemandTools()
        setActiveImagePreview(null)
        suppressAccountPanelFocusRestoreRef.current = true
        setAccountPanelOpen(false)
        setAdminRouteActive(false)
        resetEditorHistory()
        userInteractedRef.current = formattedBody.length > 0 || adoption.assets.length > 0
        if (!notesRef.current.has(adoption.draft.noteId)) { pendingNewNoteIdsRef.current.add(adoption.draft.noteId); watchSharedNoteForContext(adoption.draft.noteId) }
        draftRef.current = adoption.draft
        setDraft(adoption.draft)
        setAssets((current) => {
          const incomingIds = new Set(adoption.assets.map((asset) => asset.id))
          return [...current.filter((asset) => !incomingIds.has(asset.id)), ...adoption.assets]
        })
        setSaveState('local')
        setComposerInstantEntry(true)
        setComposerClosing(false)
        setComposerKey((current) => current + 1)
        setMode('capture')
        const unpacking = cleanupEnabled && (adoption.assets.length > 0 || mayBeBookingShare(adoption.draft.body))
        if (adoption.assets.length > 0) setCleanupAuto({ noteId: adoption.draft.noteId, assetIds: adoption.assets.map((asset) => asset.id) })
        else if (unpacking) setCleanupAuto({ noteId: adoption.draft.noteId, assetIds: [], text: true })

        await waitForNativeHandoffFrame()
        await waitForNativeHandoffFrame()
        // What Unpack reads is shown being read, keyboard closed, on a phone; tapping to write opens it (utils/editorEntryFocus.ts).
        if (!(unpacking && studio.runtime.formFactor === 'phone')) editorRef.current?.placeCaretAtEnd()
        await plugin.acknowledge({ id: envelope.id, revision: envelope.revision })
        await plugin.complete({ id: envelope.id, revision: envelope.revision })
        if (adoption.assets.length > 0) {
          void imageQueue.wake(authSessionRef.current?.user.id ?? null)
        }
        recordDiagnosticMilestone('native-intent:adoption-complete', { kind: 'share' })
      } finally {
        delete document.documentElement.dataset.nativeIntentHandoff
        composerTransitionRef.current = false
      }
    }

    const adoptEditorImage = async (envelope: NativeIntentEnvelope): Promise<void> => {
      recordDiagnosticMilestone('native-intent:adoption-started', { kind: 'paste-image' })
      const payload = nativeEditorImagePayload(envelope)
      const metadata = payload.files[0]
      const local = await plugin.getShareFile({ id: envelope.id, token: metadata.token })
      if (
        typeof local.uri !== 'string'
        || !local.uri.startsWith('file://')
        || local.size !== metadata.size
      ) {
        throw new Error('Native editor image reference is invalid.')
      }
      const response = await fetch(Capacitor.convertFileSrc(local.uri), { cache: 'no-store' })
      if (!response.ok) throw new Error('Native editor image could not be loaded.')
      const blob = await response.blob()
      if (blob.size !== metadata.size) throw new Error('Native editor image size changed.')
      const file = new File([blob], metadata.name, {
        type: metadata.mimeType,
        lastModified: metadata.lastModified,
      })
      const validationError = validateImageFile(file)
      if (validationError || normalizedImageMimeType(file) !== metadata.mimeType) {
        throw new Error('Native editor image failed web validation.')
      }

      if (modeRef.current !== 'capture' || draftRef.current.noteId !== payload.noteId) {
        await plugin.acknowledge({ id: envelope.id, revision: envelope.revision })
        await plugin.complete({ id: envelope.id, revision: envelope.revision })
        void notify.error('The note changed before the clipboard image could be pasted. Please try again.')
        return
      }

      const [addedImageId] = await addImagesAndUnpack([file])
      if (!addedImageId) {
        throw new Error('Native editor image could not be saved offline.')
      }
      setPendingImageRevealId(addedImageId)
      await plugin.acknowledge({ id: envelope.id, revision: envelope.revision })
      await plugin.complete({ id: envelope.id, revision: envelope.revision })
      recordDiagnosticMilestone('native-intent:adoption-complete', { kind: 'paste-image' })
    }

    const adoptOpenNote = async (envelope: NativeIntentEnvelope): Promise<void> => {
      recordDiagnosticMilestone('native-intent:adoption-started', { kind: 'open-note' })
      const noteId = nativeOpenNoteId(envelope)
      const target = new URL(`/n/${noteId}`, window.location.origin)
      if (envelope.source === 'shortcut') target.searchParams.set('source', 'shortcut')
      // The synchronous startup seed may already have opened this local note.
      // ACK that delivery without closing/reopening the editor after auth restores.
      if (presentedNoteRouteRef.current !== noteId) await launchDispatcher.dispatch(target.href)
      else dismissLaunchSurfaces()
      // Let React commit the note resolver/target surface before the exact native
      // acknowledgement removes Android's bounded route cover.
      await waitForNativeHandoffFrame()
      await waitForNativeHandoffFrame()
      await plugin.acknowledge({ id: envelope.id, revision: envelope.revision })
      await plugin.complete({ id: envelope.id, revision: envelope.revision })
      recordDiagnosticMilestone('native-intent:adoption-complete', { kind: 'open-note' })
    }

    const drainCurrentWorkspace = async (): Promise<void> => {
      assertWorkspaceActive()
      for (let attempt = 0; attempt < APP_SETTINGS.nativeIntents.maxAttempts; attempt += 1) {
        const offered = await offerValidatedNativeIntents(plugin)
        recordDiagnosticMilestone('native-intent:offer-complete', {
          count: offered.length,
        })
        const next = offered.find((envelope) =>
          envelope.kind === 'capture'
          || envelope.kind === 'search'
          || envelope.kind === 'open-note'
          || envelope.kind === 'paste-image'
          || isNativeShareKind(envelope.kind))
        if (!next) return
        try {
          if (next.kind === 'capture') await adoptCapture(next)
          else if (next.kind === 'search') await adoptSearch(next)
          else if (next.kind === 'open-note') await adoptOpenNote(next)
          else if (next.kind === 'paste-image') await adoptEditorImage(next)
          else await adoptShare(next)
        } catch (error) {
          if (nativeIntentErrorCode(error) !== 'STALE_REVISION') throw error
          await waitForNativeHandoffDelay(APP_SETTINGS.nativeIntents.handoffRetryMs)
        }
      }
      throw new Error('Native action handoff exceeded its retry budget.')
    }

    const drain = async (): Promise<void> => {
      const workspaceOwner = activeWorkspace()?.owner ?? authSession?.user.id
      if (authProvider.id !== 'local' && workspaceOwner) {
        const workspaceBinding = await deriveNativeAccountWorkspaceBinding(
          authProvider.id,
          supabaseConfig?.environment ?? 'local',
          workspaceOwner,
        )
        assertWorkspaceIdentity(workspaceOwner)
        await plugin.bindWorkspace({ workspaceBinding })
      } else {
        await plugin.useInstallWorkspace()
      }
      await drainCurrentWorkspace()
    }

    let interactiveReady = performance.getEntriesByName('minimany:interactive').length > 0
    let pendingDrainTrigger: 'initial' | 'event' | 'resume' | null = null
    const queueDrain = (trigger: 'initial' | 'event' | 'resume') => {
      if (disposed) return
      if (!interactiveReady) {
        pendingDrainTrigger = trigger
        recordDiagnosticMilestone('native-intent:drain-deferred', { trigger })
        return
      }
      recordDiagnosticMilestone('native-intent:drain-queued', { trigger })
      const queued = nativeIntentDrainQueueRef.current.catch(() => undefined).then(drain)
      nativeIntentDrainQueueRef.current = queued
      void queued.catch(() => {
        delete document.documentElement.dataset.nativeIntentHandoff
        composerTransitionRef.current = false
        void notify.error('Your Android action is safe on this device, but is not ready in miniMany yet.')
      })
    }

    const onInteractive = () => {
      interactiveReady = true
      const trigger = pendingDrainTrigger ?? 'initial'
      pendingDrainTrigger = null
      queueDrain(trigger)
    }
    window.addEventListener('minimany:interactive', onInteractive, { once: true })

    recordDiagnosticMilestone('native-intent:listener-registering')
    void plugin.addListener('intentAvailable', () => queueDrain('event')).then((handle) => {
      if (disposed) void handle.remove()
      else {
        intentListener = handle
        recordDiagnosticMilestone('native-intent:listener-ready')
      }
    })
    // Native lifecycle delivery is not assumed to coincide with listener registration.
    // Every foreground return reconciles the durable inbox, which is safe to replay until ACK.
    void import('@capacitor/app').then(async ({ App: CapacitorApp }) => {
      const handle = await CapacitorApp.addListener('appStateChange', ({ isActive }) => {
        if (!isActive) return
        queueDrain('resume')
        if (authSession?.user.id) void updateNativeQuickAccessSyncCredentials(authSession.user.id)
      })
      if (disposed) void handle.remove()
      else appStateListener = handle
    }).catch(() => undefined)
    if (interactiveReady) queueDrain('initial')
    else pendingDrainTrigger = 'initial'
    return () => {
      disposed = true
      window.removeEventListener('minimany:interactive', onInteractive)
      if (intentListener) void intentListener.remove()
      if (appStateListener) void appStateListener.remove()
    }
  }, [
    authRestoring,
    authSession?.user.id,
    addImagesAndUnpack,
    clearActiveNoteRoute,
    closeOnDemandTools,
    flushDraft,
    launchDispatcher,
    dismissLaunchSurfaces,
    nativeIntentReady,
    resetEditorHistory,
  ])

  useEffect(() => {
    const plugin = getNativeIntentInbox(window)
    if (!plugin) return
    let disposed = false
    let listener: { remove: () => Promise<void> } | null = null
    void plugin.addListener('quickAccessRefreshRequested', () => {
      if (!disposed) setQuickAccessRefreshRequest((current) => current + 1)
    }).then((handle) => {
      if (disposed) void handle.remove()
      else listener = handle
    })
    return () => {
      disposed = true
      if (listener) void listener.remove()
    }
  }, [])

  const updateEditorBody = useCallback(
    (body: string, kind: EditorChangeKind) => {
      const currentBody = draftRef.current.body
      if (body === currentBody) return
      editorHistoryRef.current = recordEditorChange(
        editorHistoryRef.current,
        currentBody,
        kind,
      )
      syncHistoryAvailability()
      updateDraft((current) => {
        const presentUrls = new Set(extractUrls(body).map(attachmentUrlKey))
        return {
          ...current,
          body,
          suppressedUrlKeys: current.suppressedUrlKeys?.filter((key) => presentUrls.has(key)),
        }
      })
    },
    [syncHistoryAvailability, updateDraft],
  )

  const applyInlineTags = useCallback((tags: readonly string[]) => {
    const inlineTags = normalizeTags([...tags])
    if (inlineTags.length === 0) return
    updateDraft((current) => {
      const userTags = normalizeTags([...current.userTags, ...inlineTags])
      return userTags.length === current.userTags.length
        && userTags.every((tag, index) => tag === current.userTags[index])
        ? current
        : { ...current, userTags }
    })
    setSessionTags((current) => {
      const next = normalizeTags([...inlineTags, ...current])
      return next.length === current.length && next.every((tag, index) => tag === current[index])
        ? current
        : next
    })
  }, [updateDraft])

  const removeEditorUrl = useCallback((url: UrlEntity) => {
    const body = removeUrlFromMarkdown(draftRef.current.body, url)
    updateEditorBody(body, 'format')
  }, [updateEditorBody])
  /** Removes the link and its words. */
  const removeEditorUrlWithWords = useCallback((url: UrlEntity) => {
    updateEditorBody(removeUrlFromMarkdown(draftRef.current.body, url, { keepWords: false }), 'format')
  }, [updateEditorBody])

  const copyMarkdown = useCallback(async (body: string, noteId: string) => {
    if (!body) return
    try {
      await copyText(markdownForClipboard(body))
      setCopiedNoteId(noteId)
      if (copyFeedbackTimerRef.current !== null) {
        window.clearTimeout(copyFeedbackTimerRef.current)
      }
      copyFeedbackTimerRef.current = window.setTimeout(() => setCopiedNoteId(null), 1400)
      void notify.success('Copied to clipboard')
    } catch {
      void notify.error('Clipboard access is unavailable in this browser')
    }
  }, [])

  const copyNote = useCallback(
    (note: Note) => void copyMarkdown(note.body, note.id),
    [copyMarkdown],
  )

  const selectedSearchNotes = useMemo(() => (
    [...selectedSearchNoteIds].flatMap((id) => {
      const note = notesById.get(id)
      return note ? [note] : []
    })
  ), [notesById, selectedSearchNoteIds])

  const startSearchSelection = useCallback((note?: Note) => {
    setSearchSelection(createSearchSelection(note?.id))
  }, [])

  const toggleSearchSelection = useCallback((note: Note) => {
    setSearchSelection((current) => toggleSearchSelectionState(current, note.id))
  }, [])

  const dismissSearchSelection = useCallback(() => {
    setSearchSelection(createDismissedSearchSelection())
  }, [])

  const publishOperationNotes = (changed: Note[]) => {
    if (modeRef.current === 'search') pendingBrowseViewportRef.current = captureBrowseViewport()
    for (const note of changed) searchIndex.upsert(note)
    setNotes(current => changed.reduce((collection, note) => updateNoteCollection(collection, note), current))
    requestSyncRef.current({ push: true, domains: [] })
  }

  const applySelectionAction = async (action: SelectionAction) => {
    if (operationLock.current || selectionDialog?.scope !== operationScope) return
    operationLock.current = true; setOperationBusy(true)
    try {
      await flushDraft()
      const operation = action.kind === 'merge'
        ? planMerge(action.notes, action.deleteOriginals, deviceId, action.title)
        : planTags(action.notes, action.remove, action.add, deviceId)
      if (!operation.after.length) { setSelectionDialog(null); void notify('These notes already have the requested tags'); return }
      setOperationProgress(operation.kind === 'merge' && operation.after[0]?.assetIds.length ? 'Preparing attachments and downloading full images when needed…' : 'Saving changes…')
      const copiedAssets = await copyMergeAssets(repository, operation, assetTransport ? asset => downloadAssetMaster(assetTransport, asset) : undefined)
      setOperationProgress('Saving changes…')
      if (operationScopeRef.current !== operationScope) return
      await commitNoteOperation(repository, operationScope, operation, copiedAssets)
      if (operationScopeRef.current !== operationScope) return
      publishOperationNotes(operation.after)
      if (copiedAssets.length) {
        setAssets(current => [...current, ...copiedAssets])
        void imageQueue.wake(authSessionRef.current?.user.id ?? null)
      }
      setNoteOperations(current => ({ scope: operationScope, items: [operation, ...(current.scope === operationScope ? current.items : [])] }))
      setSelectionDialog(null)
      dismissSearchSelection()
      if (operation.mergedNoteId) await openNote(operation.after[0]!, undefined, captureSearchReturnState())
      else void notify(operationMessage(operation), { action: { label: 'Undo', onClick: () => void undoOperation(operation) } })
    } finally { operationLock.current = false; setOperationBusy(false) }
  }

  const undoOperation = async (operation: NoteOperation) => {
    if (operationLock.current || operationScopeRef.current !== operationScope) return
    operationLock.current = true; setOperationBusy(true)
    try {
      await flushDraft()
      const result = await undoNoteOperation(repository, operationScope, operation, deviceId)
      if (operationScopeRef.current !== operationScope) return
      publishOperationNotes(result.notes)
      setNoteOperations(current => ({ scope: operationScope, items: current.items.map(item => item.id === operation.id ? result.operation : item) }))
      const currentNote = result.notes.find(note => note.id === draftRef.current.noteId)
      if (currentNote) {
        setDraft(draftFromNote(currentNote))
        if (currentNote.status === 'trash') { await repository.clearActiveDraft(currentNote.id); clearNoteRoute(); setMode('search') }
      }
      void notify.success('Action undone')
    } catch (error) { void notify.error(error instanceof Error ? error.message : 'Could not undo this action') }
    finally { operationLock.current = false; setOperationBusy(false) }
  }

  const dismissOperation = async (operation: NoteOperation) => {
    if (operationLock.current) return
    operationLock.current = true; setOperationBusy(true)
    try {
      const dismissed = { ...operation, dismissed: true }
      await repository.commitLocalNotes([], { key: operationPrefix(operationScope) + operation.id, value: JSON.stringify(dismissed), expected: JSON.stringify(operation) })
      if (operationScopeRef.current === operationScope) setNoteOperations(current => ({ scope: operationScope, items: current.items.map(item => item.id === operation.id ? dismissed : item) }))
    } catch { void notify.error('Could not dismiss this message') }
    finally { operationLock.current = false; setOperationBusy(false) }
  }

  const operationNotices = (noteId: string) => visibleOperations.filter(operation => operation.kind === 'merge' && !operation.undone && !operation.dismissed
    && operation.mergedNoteId === noteId).map(operation =>
      <OperationNotice key={operation.id} busy={operationBusy} actions={[{ label: 'Undo', onClick: () => void undoOperation(operation) }]} onDismiss={() => void dismissOperation(operation)}>{operationMessage(operation)}</OperationNotice>)

  const copySelectedSearchNotes = useCallback(() => {
    const markdown = selectedSearchNotes.map((note) => note.body).filter(Boolean).join('\n\n---\n\n')
    if (!markdown) return
    void copyMarkdown(markdown, `selected-${selectedSearchNotes.map((note) => note.id).join('-')}`)
  }, [copyMarkdown, selectedSearchNotes])

  const bumpSelectedSearchNotes = useCallback(async () => {
    try {
      for (const note of selectedSearchNotes) await bumpNoteToTop(note)
      if (selectedSearchNotes.length > 0) void notify.success(`Bumped ${selectedSearchNotes.length} note${selectedSearchNotes.length === 1 ? '' : 's'} to the top`)
      dismissSearchSelection()
    } catch {
      void notify.error('Could not bump the selected notes')
    }
  }, [bumpNoteToTop, dismissSearchSelection, selectedSearchNotes])

  const trashSelectedSearchNotes = useCallback(async () => {
    const changed: Note[] = []
    try {
      await animateSearchNoteRemoval(selectedSearchNotes, async () => {
        for (const note of selectedSearchNotes) changed.push(await persistNoteStatus(note, 'trash'))
      })
      dismissSearchSelection()
      if (changed.length > 0) {
        void notify(`Moved ${changed.length} note${changed.length === 1 ? '' : 's'} to trash`, {
          duration: 5000,
          action: {
            label: 'Undo',
            // One at a time: each restore must fit the image limit after the ones before it.
            onClick: () => void (async () => { for (const note of changed) await persistNoteStatus(note, 'inbox') })()
              .then(() => notify.success('Notes restored'))
              .catch((error) => notify.error(error instanceof PlanLimitError ? error.message : 'Could not restore the selected notes')),
          },
        })
      }
    } catch {
      void notify.error('Could not move the selected notes to trash')
    }
  }, [animateSearchNoteRemoval, dismissSearchSelection, persistNoteStatus, selectedSearchNotes])

  const toggleCurrentNoteQuickAccess = useCallback(async (unpackedTitle?: string | null) => {
    try {
      const committed = await flushDraft()
      const note = committed ?? notesRef.current.get(draftRef.current.noteId)
      if (!note) {
        void notify.error('Add something to this note before assigning Quick Access.')
        return
      }
      const assigning = !note.quickAccess
      const quickAccessCount = [...notesRef.current.values()]
        .filter((candidate) => candidate.quickAccess && candidate.status !== 'trash')
        .length
      if (assigning && quickAccessCount >= APP_SETTINGS.widgets.quickAccessMaxItems) {
        void notify.error('Quick Access is full. Remove a note before adding another.')
        return
      }
      const quickAccessUpdate = setNoteQuickAccess(note, assigning, deviceId)
      const updated = assigning ? withUnpackedTitle(quickAccessUpdate, unpackedTitle ?? cleanupCardsRef.current.get(note.id)?.title) : quickAccessUpdate
      await repository.commitLocalNote(updated)
      searchIndex.upsert(updated)
      setNotes((current) => updateNoteCollection(current, updated))
      requestSyncRef.current({ push: true, domains: [] })
      void notify.success(assigning ? 'Added to Quick Access' : 'Removed from Quick Access')
    } catch {
      void notify.error('Could not update Quick Access for this note.')
    }
  }, [deviceId, flushDraft, searchIndex])
  // Accepted Quick Access windows from booking suggestions (screenshotCleanup/suggestions.ts), carried out once a session after notes load.
  const quickAccessWindowsChecked = useRef(false)
  const notesLoaded = notes.length > 0
  useEffect(() => {
    if (!cleanupWorkspace || !notesLoaded || quickAccessWindowsChecked.current) return
    quickAccessWindowsChecked.current = true
    void Promise.all([import('./screenshotCleanup/runs'), import('./screenshotCleanup/suggestions'), import('./screenshotCleanup/card')]).then(async ([{ listRuns, updateRun }, { quickAccessSteps }, { cardsByNote }]) => {
      const runs = await listRuns(cleanupWorkspace)
      const cards = cardsByNote(runs)
      const steps = quickAccessSteps(runs, (noteId) => notesRef.current.get(noteId)?.quickAccess)
      let titledCount = 0
      // Picture-only notes put in Quick Access before they took their card's title there.
      for (const note of [...notesRef.current.values()]) {
        if (!note.quickAccess || note.status === 'trash' || steps.some((step) => step.noteId === note.id)) continue
        const titled = withUnpackedTitle(note, cards.get(note.id)?.title)
        if (titled === note) continue
        const updated = bumpNote(titled, deviceId)
        titledCount += 1
        await repository.commitLocalNote(updated)
        searchIndex.upsert(updated)
        setNotes((current) => updateNoteCollection(current, updated))
      }
      for (const step of steps) {
        const note = notesRef.current.get(step.noteId)
        if (!note || note.status === 'trash') continue
        const full = [...notesRef.current.values()].filter((candidate) => candidate.quickAccess && candidate.status !== 'trash').length >= APP_SETTINGS.widgets.quickAccessMaxItems
        if (step.action === 'add' && full) continue
        const moved = setNoteQuickAccess(note, step.action === 'add', deviceId)
        const updated = step.action === 'add' ? withUnpackedTitle(moved, cards.get(note.id)?.title) : moved
        await repository.commitLocalNote(updated)
        searchIndex.upsert(updated)
        setNotes((current) => updateNoteCollection(current, updated))
        const at = Date.now()
        await updateRun(cleanupWorkspace, step.runId, (run) => ({
          suggestions: { ...run.suggestions, quickAccess: { ...run.suggestions!.quickAccess!, ...(step.action === 'add' ? { addedAt: at } : { removedAt: at }) } },
        }))
      }
      if (steps.length || titledCount) requestSyncRef.current({ push: true, domains: [] })
    }).catch(() => undefined)
  }, [cleanupWorkspace, notesLoaded, deviceId, searchIndex])

  const toggleTag = useCallback(
    (tag: string) => {
      const normalizedTag = tag.trim().toLowerCase().replace(/^#/, '')
      if (!normalizedTag) return
      updateDraft((current) => ({
        ...current,
        userTags: current.userTags.includes(normalizedTag)
          ? current.userTags.filter((existing) => existing !== normalizedTag)
          : [...current.userTags, normalizedTag],
      }))
    },
    [updateDraft],
  )

  const queueSettingsCommit = useCallback((settings: SettingsRecord) => {
    const task = settingsCommitQueueRef.current.then(async () => {
      await repository.commitSettings(settings)
      requestSyncRef.current({ push: true, domains: [] })
    })
    settingsCommitQueueRef.current = task.catch(() => undefined)
    void task.catch(() => notify.error('Could not save this setting locally'))
  }, [])

  const changeReminderQuickOption = useCallback((key: 'firstMinutes' | 'secondMinutes' | 'tomorrowHour', value: number) => {
    const updated = updateReminderQuickOptions(globalSettings, {
      ...globalSettings.reminderQuickOptions,
      [key]: value,
    }, deviceId)
    setGlobalSettings(updated)
    queueSettingsCommit(updated)
  }, [deviceId, globalSettings, queueSettingsCommit])

  useEffect(() => {
    if (!loaded) return
    let cancelled = false
    void detectPreferredDeviceName(deviceId).then((detectedName) => {
      if (cancelled) return
      const current = deviceSettingsRef.current
      if (current.detectedName === detectedName) return
      const updated = updateDeviceSettings(current, { detectedName }, deviceId)
      setDeviceSettings(updated)
      queueSettingsCommit(updated)
      if (!current.customName && document.activeElement !== deviceNameInputRef.current) {
        setDeviceNameDraft(detectedName)
      }
    })
    return () => { cancelled = true }
  }, [deviceId, loaded, queueSettingsCommit])

  const changeAutoExpire = useCallback((days: AutoExpireDays) => {
    if (days === globalSettings.autoExpireDays) return
    const updated = updateGlobalSettings(globalSettings, days, deviceId)
    setGlobalSettings(updated)
    queueSettingsCommit(updated)
  }, [deviceId, globalSettings, queueSettingsCommit])

  const changeMeasurementPreference = useCallback((preference: MeasurementPreference) => {
    if (preference === normalizeMeasurementPreference(globalSettings.measurementSystem)) return
    const updated = updateMeasurementSystem(globalSettings, preference, deviceId)
    setGlobalSettings(updated)
    queueSettingsCommit(updated)
  }, [deviceId, globalSettings, queueSettingsCommit])

  const changeWeekStartsOn = useCallback((weekStartsOn: WeekStartsOn) => {
    if (weekStartsOn === globalSettings.weekStartsOn) return
    const updated = updateWeekStartsOn(globalSettings, weekStartsOn, deviceId)
    setGlobalSettings(updated)
    queueSettingsCommit(updated)
  }, [deviceId, globalSettings, queueSettingsCommit])

  const changeStartMode = useCallback((startMode: AppStartMode) => {
    if (startMode === deviceSettings.startMode) return
    const updated = updateDeviceSettings(deviceSettings, { startMode }, deviceId)
    setDeviceSettings(updated)
    writeBootstrapStartMode(startMode)
    queueSettingsCommit(updated)
  }, [deviceId, deviceSettings, queueSettingsCommit])

  const changeUiSize = useCallback((uiSize: UiSize) => {
    if (uiSize === deviceSettings.uiSize) return
    const updated = updateDeviceSettings(deviceSettings, { uiSize }, deviceId)
    setDeviceSettings(updated)
    writeBootstrapUiSize(uiSize)
    applyUiSize(uiSize)
    queueSettingsCommit(updated)
  }, [deviceId, deviceSettings, queueSettingsCommit])

  const changePreviewDeviceOverride = useCallback((next: PreviewDeviceOverride) => {
    writePreviewDeviceOverride(next)
    setPreviewDeviceOverride(next)
  }, [])

  const changePerformanceDiagnostics = useCallback((enabled: boolean) => {
    if (!developerAuthorized) return
    if (enabled === deviceSettings.performanceDiagnosticsEnabled) return
    const updated = updateDeviceSettings(
      deviceSettings,
      { performanceDiagnosticsEnabled: enabled },
      deviceId,
    )
    setDeviceSettings(updated)
    setPerformanceDiagnosticsEnabled(enabled)
    queueSettingsCommit(updated)
  }, [developerAuthorized, deviceId, deviceSettings, queueSettingsCommit])

  useEffect(() => {
    if (authProvider.id === 'local' || !authSession || !supabaseConfig) {
      setAccountAccess(null)
      setAccountAccessError(false)
      return
    }

    let cancelled = false
    setAccountAccessError(false)
    void getMyAccountAccess(supabaseConfig.isDev)
      .then(async (access) => {
        if (cancelled) return
        setAccountAccess(access)
        if (authSession) rememberAccountLimits(authSession.user.id, access)
        const noteLimit = access.capacities.notes
        if (noteLimit !== null && access.usage.notes >= noteLimit) return
        const retried = await repository.retryAccountLimitOperations()
        if (!cancelled && retried > 0) {
          requestSyncRef.current({ push: true, domains: ['notes'] }, 0)
        }
      })
      .catch(() => {
        if (!cancelled) setAccountAccessError(true)
      })
    return () => { cancelled = true }
  }, [accountAccessRefreshKey, accountPanelOpen, authSession?.user.id])

  const applyVerifiedAccessProfile = useCallback((
    ownerId: string,
    profile: UserAccessProfile,
  ) => {
    if (authSessionRef.current?.user.id !== ownerId) return
    setDeveloperAccessUserId(ownerId)
    setDeveloperAccess(profile.isAdmin ? 'admin' : 'hidden')
    rememberAdmin(ownerId, profile.isAdmin)
    setDeveloperAccessDeniedFor(profile.isAdmin ? null : ownerId)
    if (profile.isAdmin || !deviceSettingsRef.current.performanceDiagnosticsEnabled) return
    const updated = updateDeviceSettings(
      deviceSettingsRef.current,
      { performanceDiagnosticsEnabled: false },
      deviceId,
    )
    setDeviceSettings(updated)
    setPerformanceDiagnosticsEnabled(false)
    queueSettingsCommit(updated)
  }, [deviceId, queueSettingsCommit])

  const loadAccessProfile = useCallback((ownerId: string): Promise<UserAccessProfile> => {
    const active = accessProfileRequestRef.current
    if (active?.ownerId === ownerId) return active.promise
    const promise = measureDiagnosticActivity('auth:developer-access', getMyAccessProfile)
      .finally(() => {
        if (accessProfileRequestRef.current?.promise === promise) {
          accessProfileRequestRef.current = null
        }
      })
    accessProfileRequestRef.current = { ownerId, promise }
    return promise
  }, [])

  useEffect(() => {
    // Opted-in developer features need authorization outside Settings too. Refresh while active; never trust a saved grant.
    if (!developerAccessNeeded({ settingsOpen: accountPanelOpen, adminRoute: adminRouteActive, contextsRequested, cleanupRequested }) || authProvider.id === 'local' || !authSession || !isOnline) {
      setDeveloperAccess('hidden')
      setDeveloperAccessUserId(null)
      return
    }
    let cancelled = false
    const ownerId = authSession.user.id
    setDeveloperAccess(current => current === 'admin' ? current : 'checking')
    const refresh = () => {
      void loadAccessProfile(ownerId)
        .then(profile => { if (!cancelled) applyVerifiedAccessProfile(ownerId, profile) })
        .catch(() => {
          if (!cancelled) { setDeveloperAccess('hidden'); setDeveloperAccessUserId(ownerId) }
        })
    }
    refresh()
    const visibleRefresh = () => { if (document.visibilityState === 'visible') refresh() }
    const timer = window.setInterval(visibleRefresh, APP_SETTINGS.adminAccess.accessRefreshMs)
    window.addEventListener('focus', visibleRefresh)
    return () => { cancelled = true; window.clearInterval(timer); window.removeEventListener('focus', visibleRefresh) }
  }, [accountPanelOpen, adminRouteActive, contextsRequested, cleanupRequested, isOnline, applyVerifiedAccessProfile, authSession?.user.id, loadAccessProfile])

  useEffect(() => {
    if (!loaded) return
    const customName = normalizeCustomDeviceName(deviceNameDraft, deviceSettings.detectedName)
    if (customName === deviceSettings.customName) return
    const timer = window.setTimeout(() => {
      const updated = updateDeviceSettings(deviceSettings, { customName }, deviceId)
      setDeviceSettings(updated)
      queueSettingsCommit(updated)
    }, APP_SETTINGS.persistence.settingsTextDebounceMs)
    return () => window.clearTimeout(timer)
  }, [deviceId, deviceNameDraft, deviceSettings, loaded, queueSettingsCommit])

  const addCustomTag = (value = tagInput) => {
    const normalizedTag = value.trim().toLowerCase().replace(/^#/, '')
    if (!normalizedTag) return
    updateDraft((current) => ({
      ...current,
      userTags: current.userTags.includes(normalizedTag)
        ? current.userTags
        : [...current.userTags, normalizedTag],
    }))
    setSessionTags((current) =>
      current.includes(normalizedTag) ? current : [normalizedTag, ...current],
    )
    setTagInput('')
    tagInputRef.current?.focus({ preventScroll: true })
  }

  const undoEditor = useCallback(() => {
    const result = undoEditorChange(editorHistoryRef.current, draftRef.current.body)
    if (!result) return
    editorHistoryRef.current = result.history
    syncHistoryAvailability()
    updateDraft((current) => ({ ...current, body: result.body }))
    requestAnimationFrame(() => editorRef.current?.placeCaretAtEnd())
  }, [syncHistoryAvailability, updateDraft])

  const redoEditor = useCallback(() => {
    const result = redoEditorChange(editorHistoryRef.current, draftRef.current.body)
    if (!result) return
    editorHistoryRef.current = result.history
    syncHistoryAvailability()
    updateDraft((current) => ({ ...current, body: result.body }))
    requestAnimationFrame(() => editorRef.current?.placeCaretAtEnd())
  }, [syncHistoryAvailability, updateDraft])

  const applyEditorFormat = (format: MarkdownFormat) => editorRef.current?.format(format)
  const activeAssets = useMemo(() => {
    const assetIds = new Set(draft.assetIds ?? [])
    return assets
      .filter((asset) => assetIds.has(asset.id) && asset.state !== 'deleted')
      .reverse().sort((left, right) => right.createdAt - left.createdAt)
  }, [assets, draft.assetIds])
  const liveClassification = useMemo(
    () => classifyContent(draft.body, undefined, activeAssets.length > 0),
    [activeAssets.length, draft.body],
  )
  const recentUserTags = useMemo(
    () => tagsByMostRecentUse(notes, sessionTags),
    [notes, sessionTags],
  )
  const filterTags = useMemo(() => {
    const counts = new Map<string, number>()
    const mostRecentUse = new Map<string, number>()
    for (const note of notes) {
      for (const tag of new Set([...note.userTags, ...inferredTagsForNote(note)])) {
        counts.set(tag, (counts.get(tag) ?? 0) + 1)
        mostRecentUse.set(tag, Math.max(mostRecentUse.get(tag) ?? 0, note.modifiedAt))
      }
    }
    const tags = [...counts.keys()]
    const byFrequency = [...tags].sort((left, right) =>
      (counts.get(right) ?? 0) - (counts.get(left) ?? 0) || left.localeCompare(right),
    )
    const byRecency = [...tags].sort((left, right) =>
      (mostRecentUse.get(right) ?? 0) - (mostRecentUse.get(left) ?? 0) || left.localeCompare(right),
    )
    const balanced: string[] = []
    const seen = new Set<string>()
    for (let index = 0; index < tags.length; index += 1) {
      for (const tag of [byRecency[index], byFrequency[index]]) {
        if (!tag || seen.has(tag)) continue
        seen.add(tag)
        balanced.push(tag)
      }
    }
    return balanced
  }, [notes])
  const suggestedTags = useMemo(
    () => [...new Set([
      ...recentUserTags,
      ...DEFAULT_TAGS,
      ...liveClassification.suggestedTags,
      ...draft.userTags,
    ])],
    [draft.userTags, liveClassification.suggestedTags, recentUserTags],
  )

  const closeTagging = useCallback((restoreSelection = true) => {
    setTagToolbarOpen(false)
    setTagInputOpen(false)
    setTagInput('')
    if (restoreSelection) restoreEditorSelectionAfterTransientUi()
  }, [restoreEditorSelectionAfterTransientUi])

  const toggleTagEditing = useCallback(() => {
    if (tagToolbarOpen) {
      closeTagging()
      return
    }
    setTagToolbarOpen(true)
    setTagInputOpen(true)
  }, [closeTagging, tagToolbarOpen])

  const deferredQuery = useDeferredValue(searchQuery)
  const smartSearchQuery = parseTagSearchQuery(searchQuery, filterTags).text.trim()
  const parsedSearchQuery = useMemo(
    () => parseTagSearchQuery(deferredQuery, filterTags),
    [deferredQuery, filterTags],
  )
  const searchQueryTags = parsedSearchQuery.tags
  const searchTagPrefix = tagSearchPrefix(searchQuery)
  const searchTagSuggestions = useMemo(
    () => searchTagPrefix === null
      ? []
      : filterTags.filter((tag) => tag.startsWith(searchTagPrefix)).slice(0, 7),
    [filterTags, searchTagPrefix],
  )
  const visibleSearchTagSuggestions = searchTagSuggestionsOpen ? searchTagSuggestions : []
  const activeSearchTagSuggestion = searchTagSuggestionIndex === null
    ? null
    : visibleSearchTagSuggestions[searchTagSuggestionIndex] ?? null
  const effectiveSearchFilters = useMemo<SearchFilters>(
    () => searchQueryTags.length > 0 ? { ...searchFilters, tags: searchQueryTags } : searchFilters,
    [searchFilters, searchQueryTags],
  )
  const effectiveSearchQuery = parsedSearchQuery.text
  useEffect(() => {
    smartSearchSequenceRef.current++
    setSmartSearchResult(null)
    setSmartSearchError(null)
    setSmartSearchBusy(false)
  }, [smartSearchQuery])
  const searchResults = useMemo(
    () => searchIndex.search(effectiveSearchQuery, effectiveSearchFilters),
    [effectiveSearchFilters, effectiveSearchQuery, notes, searchIndex, searchIndexRevision],
  )
  const quickAccessWidgetNotes = useMemo(() => quickAccessWidgetPayload(notes), [notes])
  useEffect(() => {
    void updateNativeQuickAccessWidgetPayload(quickAccessWidgetNotes)
  }, [quickAccessWidgetNotes])
  const renderedSearchResults = searchResults
  const browseStartup = browseStartupPresentation(
    browseInventoryPhase,
    renderedSearchResults.length,
  )
  // The welcome: only for a scope that has never held a note (docs/onboarding), once the inventory is ready and no account restore is running.
  const welcome = useWelcome({
    scope: authSession?.user.id ?? 'local-review',
    inventoryReady: browseInventoryPhase === 'ready',
    noteCount: notes.length,
    restoring: hasRemoteAccount && accountRestorePhase !== 'ready',
  })
  // After the first note a guest sees one dismissible line that says sign-in is optional (docs/onboarding, DEC-25).
  const signInStrip = useSignInStrip({
    scope: authSession?.user.id ?? 'local-review',
    guest: !authSession,
    inventoryReady: browseInventoryPhase === 'ready',
    noteCount: notes.length,
    welcomeShowing: welcome.show,
  })
  // The welcome's composer saves through the same path as the editor, so the note is synced, searchable and counted like any other.
  const saveWelcomeNote = useCallback(async (body: string) => {
    const note = await commitSnapshot({ ...createBlankDraft(), body })
    if (!note) throw new Error('The note was not saved.')
    void notify.success(onboardingContent.welcome.saved)
  }, [commitSnapshot])
  const searchWindowKey = `${effectiveSearchQuery}\u0000${effectiveSearchFilters.status ?? ''}\u0000${effectiveSearchFilters.tag ?? ''}\u0000${effectiveSearchFilters.tags?.join(',') ?? ''}\u0000${effectiveSearchFilters.property ?? ''}\u0000${effectiveSearchFilters.hasAlarm ? 'alarm' : ''}\u0000${effectiveSearchFilters.quickAccess ? 'quick-access' : ''}`
  useLayoutEffect(() => {
    const browseScopeAction = browseViewportActionForScopeChange(
      browseScopeKeyRef.current,
      searchWindowKey,
      searchRestoreViewportRef.current !== null,
    )
    browseScopeKeyRef.current = searchWindowKey
    if (browseScopeAction !== 'reset') return
    searchFiltersViewportRef.current = null
    pendingBrowseViewportRef.current = null
    setSearchNavigationIndex(null)
    searchNavigationDirectionRef.current = null
    if (mode === 'search' && !browseCovered) window.scrollTo(0, 0)
  }, [browseCovered, mode, searchWindowKey])
  const visibleSearchCount =
    searchWindow.key === searchWindowKey ? searchWindow.count : NOTE_BATCH_SIZE
  const activeSearchIndex =
    clampSearchNavigationIndex(searchNavigationIndex, renderedSearchResults.length)
  const activeSearchResult = activeSearchIndex === null ? null : renderedSearchResults[activeSearchIndex]
  const activeSearchResultDomId = activeSearchResult ? `search-result-${activeSearchResult.id}` : undefined
  const renderedSearchCount = Math.max(visibleSearchCount, (activeSearchIndex ?? -1) + 1)
  const visibleSearchResults = useMemo(
    () => renderedSearchResults.slice(0, renderedSearchCount),
    [renderedSearchCount, renderedSearchResults],
  )
  const hasVisibleSearchResults = visibleSearchResults.length > 0
  const hasMoreSearchResults = renderedSearchCount < renderedSearchResults.length
  const hasActiveSearchFilters = hasNonDefaultSearchFilters(effectiveSearchFilters)
  const smartSearchActive = smartSearchEnabled && smartSearchSelected && !hasActiveSearchFilters
  useEffect(() => {
    if (!hasActiveSearchFilters) return
    setSmartSearchSelected(false)
    smartSearchSequenceRef.current++
    setSmartSearchResult(null)
    setSmartSearchError(null)
    setSmartSearchBusy(false)
  }, [hasActiveSearchFilters])
  const hasSearchNarrowing = Boolean(searchQuery.trim()) || hasActiveSearchFilters
  const searchEmptyCause = searchEmptyCauseFor(searchQuery, effectiveSearchFilters)
  // Sharing from other apps works in the installed app (PWA or Capacitor) on Android, not in an Android browser tab.
  // While the welcome shows it is the whole page: no Contexts strip and no "0 notes" summary above it.
  const welcomeVisible = welcome.show && searchEmptyCause === 'empty' && browseStartup.showEmptyState
  const activeSearchFilterCount = [
    (effectiveSearchFilters.status ?? 'all') !== 'all',
    Boolean(effectiveSearchFilters.property),
    Boolean(effectiveSearchFilters.hasAlarm),
    Boolean(effectiveSearchFilters.quickAccess),
    Boolean(effectiveSearchFilters.tag) || Boolean(effectiveSearchFilters.tags?.length),
  ].filter(Boolean).length
  const activeSearchFilterScope = useMemo(
    () => searchFilterScopePresentation(effectiveSearchFilters),
    [effectiveSearchFilters],
  )
  const showMoreSearchResults = useCallback(() => {
    setSearchWindow((current) => ({
      key: searchWindowKey,
      count:
        (current.key === searchWindowKey ? current.count : NOTE_BATCH_SIZE) + NOTE_BATCH_SIZE,
    }))
  }, [searchWindowKey])

  const captureSearchReturnState = (): SearchReturnState => ({
    query: searchQuery,
    filters: { ...searchFilters },
    filtersOpen: searchFiltersOpen,
    navigationIndex: searchNavigationIndex,
    activeResultId: activeSearchResult?.id ?? null,
    window: { ...searchWindow },
    viewport: captureBrowseViewport(),
  })

  const applySearchFilter = (update: (current: SearchFilters) => SearchFilters) => {
    setSearchFilters(update)
    setSearchTagSuggestionsOpen(false)
    setSearchTagSuggestionIndex(null)
    if (!searchFiltersOpen && shouldRestoreBrowseInputFocus(studio.runtime.formFactor)) searchRef.current?.focus({ preventScroll: true })
  }

  const toggleSearchTagFilter = (tag: string) => {
    if (searchQueryTags.includes(tag) || searchFilters.tag === tag) {
      if (searchQueryTags.includes(tag)) {
        setSearchQuery((current) => removeTagFromSearchQuery(current, tag))
      }
      applySearchFilter((current) => ({
        ...current,
        tag: current.tag === tag ? undefined : current.tag,
      }))
      return
    }
    applySearchFilter((current) => ({ ...current, tag }))
  }

  const selectSearchTagSuggestion = (tag: string) => {
    setSearchQuery((current) => completeTagSearchQuery(current, tag))
    setSearchTagSuggestionsOpen(false)
    setSearchTagSuggestionIndex(null)
    searchRef.current?.focus({ preventScroll: true })
  }

  const changeSearchLayout = useCallback((layout: SearchLayout) => {
    setSearchLayout(layout)
    writeSearchLayout(layout)
  }, [])

  const closeAccountPanel = useCallback(() => {
    setSettingsSectionMenuOpen(false)
    setAccountPanelOpen(false)
  }, [])

  const openAccountPanel = useCallback(() => {
    accountPanelScrollRef.current ??= window.scrollY
    setAccountPanelOpen(true)
  }, [])

  const openAdminDashboard = useCallback(() => {
    suppressAccountPanelFocusRestoreRef.current = true
    const currentState = window.history.state
    const state = currentState && typeof currentState === 'object' ? currentState : {}
    window.history.pushState({ ...state, minimanyAdminRoute: true }, '', adminPath())
    setAccountPanelOpen(false)
    setAdminRouteActive(true)
    setComparisonRouteActive(false)
  }, [])

  const openSearchComparison = useCallback(() => {
    if (!supabaseConfig?.isDev || !developerAuthorized) return
    const currentState = window.history.state
    const state = currentState && typeof currentState === 'object' ? currentState : {}
    window.history.pushState({ ...state, minimanyAdminRoute: true }, '', '/admin/experiments/search')
    setAccountPanelOpen(false)
    setAdminRouteActive(true)
    setComparisonRouteActive(true)
  }, [developerAuthorized])

  const scrollToSettingsSection = useCallback((sectionId: string, drawAttention = false) => {
    if (studioActive) {
      setStudioSettingsSection(studioCategoryForSection(sectionId))
      return
    }
    const section = document.getElementById(`settings-${studioCategoryForSection(sectionId)}`)
    if (!section) return
    section.scrollIntoView({ block: 'start' })
    if (!drawAttention || typeof section.animate !== 'function') return
    section.getAnimations().forEach((animation) => animation.cancel())
    section.animate(
      [
        {
          backgroundColor: 'rgba(16, 219, 229, 0.14)',
          boxShadow: '0 0 0 2px rgba(16, 219, 229, 0.32)',
        },
        {
          backgroundColor: 'rgba(16, 219, 229, 0)',
          boxShadow: '0 0 0 2px rgba(16, 219, 229, 0)',
        },
      ],
      {
        duration: APP_SETTINGS.interactions.settingsSectionAttentionMs,
        easing: 'ease-out',
      },
    )
  }, [studioActive])

  useLayoutEffect(() => {
    if (!studioActive || !accountPanelOpen) return
    const scrollRegion = document.querySelector('.account-panel__scroll-region')
    if (scrollRegion) scrollRegion.scrollTop = 0
  }, [studioActive, accountPanelOpen, studioSettingsSection])

  const openGoogleKeepImport = useCallback(() => {
    openAccountPanel()
    requestAnimationFrame(() => {
      scrollToSettingsSection('export-import-section')
      setGoogleKeepImportOpen(true)
    })
  }, [openAccountPanel, scrollToSettingsSection])

  const openNoteExports = useCallback(() => {
    openAccountPanel()
    requestAnimationFrame(() => {
      scrollToSettingsSection('export-import-section')
      requestAnimationFrame(() => {
        const exports = document.getElementById('settings-data-exports')
        if (exports instanceof HTMLDetailsElement) exports.open = true
        exports?.scrollIntoView({ block: 'start' })
      })
    })
  }, [openAccountPanel, scrollToSettingsSection])

  const queueSelectedNoteExport = useCallback((options: NoteExportOptions) => {
    const pending = noteExportDialog
    if (!pending || pending.scope !== operationScope) return
    setNoteExportDialog(null)
    dismissSearchSelection()
    setExportBrowseNotice('status')
    setExportRequestPending(true)
    setExportRequestError(null)
    const toastId = notify.loading('Export request is in progress…', {
      description: 'You can keep using miniMany. The ZIP will appear in Recent exports.',
      action: { label: 'View', onClick: openNoteExports },
    })
    void noteExports.queue(pending.notes, options).then(async (jobId) => {
      setExportRequestPending(false)
      const id = await toastId
      exportToastIdsRef.current.set(jobId, id)
      void notify.loading('Export requested', {
        id,
        description: 'Preparing in the background. You can keep using miniMany.',
        action: { label: 'View', onClick: openNoteExports },
      })
    }).catch(async (error) => {
      const message = error instanceof Error ? error.message : 'Could not queue the export'
      setExportRequestPending(false)
      setExportRequestError(message)
      void notify.error('Export request failed', {
        id: await toastId,
        description: message,
      })
    })
  }, [dismissSearchSelection, noteExportDialog, noteExports, openNoteExports, operationScope])

  const exportJobStatesRef = useRef<Map<string, string> | null>(null)
  useEffect(() => {
    if (!noteExports.loaded) return
    const next = new Map(noteExports.jobs.map((job) => [job.job_id, job.state]))
    const previous = exportJobStatesRef.current
    exportJobStatesRef.current = next
    for (const job of noteExports.jobs) {
      const prior = previous?.get(job.job_id)
      const trackedToastId = exportToastIdsRef.current.get(job.job_id)
      if (job.state === 'ready' && ((prior && prior !== 'ready') || trackedToastId !== undefined)) {
        void notify.success(`Export ready · ${job.note_count} note${job.note_count === 1 ? '' : 's'}`, {
          id: trackedToastId,
          action: {
            label: 'Download',
            onClick: () => void noteExports.download(job.job_id).catch((error) => {
              void notify.error(error instanceof Error ? error.message : 'Could not download the export')
            }),
          },
        })
        exportToastIdsRef.current.delete(job.job_id)
      } else if (job.state === 'failed' && ((prior && prior !== 'failed') || trackedToastId !== undefined)) {
        void notify.error('An export needs attention in Recent exports', { id: trackedToastId })
        exportToastIdsRef.current.delete(job.job_id)
      }
    }
  }, [noteExports.download, noteExports.jobs, noteExports.loaded])

  const clearSearch = useCallback(() => {
    setSearchQuery('')
    setSearchFilters({ status: 'all' })
    setSearchFiltersOpen(false)
    setSearchTagsExpanded(false)
    setSearchTagSuggestionsOpen(false)
    setSearchTagSuggestionIndex(null)
    setSearchNavigationIndex(null)
  }, [])

  const closeSearchFilters = useCallback(() => {
    setSearchFiltersOpen(false)
    setSearchTagsExpanded(false)
  }, [])

  const openSearchFilters = useCallback(() => {
    searchFiltersViewportRef.current = captureBrowseViewport()
    setSearchFiltersOpen(true)
    setSearchTagsExpanded(false)
  }, [])

  useLayoutEffect(() => {
    if (searchFiltersOpen) return
    const viewport = searchFiltersViewportRef.current
    if (!viewport) return
    searchFiltersViewportRef.current = null
    restoreBrowseViewportWhenReady(viewport, APP_SETTINGS.interactions.browseViewportRestoreFrameBudget)
    requestAnimationFrame(() => filterToggleRef.current?.focus({ preventScroll: true }))
  }, [searchFiltersOpen])

  const clearSearchFromButton = useCallback(() => {
    clearSearch()
    searchRef.current?.focus({ preventScroll: true })
  }, [clearSearch])

  const clearSearchQuery = useCallback(() => {
    setSearchQuery('')
    setSearchTagSuggestionsOpen(false)
    setSearchTagSuggestionIndex(null)
    setSearchNavigationIndex(null)
  }, [])

  const resetSearchFilters = useCallback(() => {
    setSearchFilters({ status: 'all' })
    setSearchTagsExpanded(false)
    setSearchTagSuggestionsOpen(false)
    setSearchTagSuggestionIndex(null)
    setSearchNavigationIndex(null)
    setSearchQuery((current) => parseTagSearchQuery(current, filterTags).text)
  }, [filterTags])

  const openPhoneSearchSurface = useCallback(() => {
    setContextLabBrowseOverride(true)
    setMobileSearchSurfaceOpen(openMobileSearchSurface)
    requestAnimationFrame(() => searchRef.current?.focus({ preventScroll: true }))
  }, [])

  const runJevSearch = async () => {
    const owner = authSessionRef.current?.user.id
    const query = smartSearchQuery
    if (!smartSearchEnabled || !owner || query.length < 2 || hasActiveSearchFilters || smartSearchBusy) return
    const flow = createDevFlow(owner, 'jev_search')
    flow.event('search_submitted', 'started')
    const sequence = ++smartSearchSequenceRef.current
    setSmartSearchBusy(true)
    setSmartSearchError(null)
    try {
      const records = contextsEnabled ? await readManualContexts(owner) : []
      const { runSmartSearch } = await import('./intelligence/smartPilot/client')
      const result = await runSmartSearch(owner, query, Array.from(notesRef.current.values()), records, undefined, flow)
      if (sequence !== smartSearchSequenceRef.current || authSessionRef.current?.user.id !== owner) {
        flow.event('presentation', 'skipped', 'search_superseded'); return
      }
      setSmartSearchResult({ query, result, contexts: records })
      flow.event('presentation', 'ok', result.notes.length + result.contexts.length ? 'results_shown' : 'no_match',
        { resultCount: result.notes.length + result.contexts.length })
    } catch (error) {
      flow.event('presentation', 'failed', devFlowReason(error))
      if (sequence === smartSearchSequenceRef.current) setSmartSearchError(error instanceof Error ? error.message : 'Smart Search was unavailable.')
    } finally {
      if (sequence === smartSearchSequenceRef.current) setSmartSearchBusy(false)
    }
  }

  const handleSearchKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.nativeEvent.isComposing) return
    if (visibleSearchTagSuggestions.length > 0 && (event.key === 'ArrowDown' || event.key === 'ArrowUp')) {
      event.preventDefault()
      setSearchTagSuggestionIndex((current) => {
        const direction = event.key === 'ArrowDown' ? 1 : -1
        const currentIndex = current ?? (direction > 0 ? -1 : 0)
        return (currentIndex + direction + visibleSearchTagSuggestions.length) % visibleSearchTagSuggestions.length
      })
      return
    }
    if (event.key === 'Enter' && activeSearchTagSuggestion) {
      event.preventDefault()
      selectSearchTagSuggestion(activeSearchTagSuggestion)
      return
    }
    if (event.key === 'Enter' && smartSearchActive && smartSearchQuery.length >= 2) {
      event.preventDefault()
      void runJevSearch()
      return
    }
    if (event.key === 'Escape' && visibleSearchTagSuggestions.length > 0) {
      event.preventDefault()
      setSearchTagSuggestionsOpen(false)
      setSearchTagSuggestionIndex(null)
      return
    }
    if (!smartSearchActive && (event.key === 'ArrowDown' || event.key === 'ArrowUp')) {
      if (renderedSearchResults.length === 0) return
      event.preventDefault()
      const direction = event.key === 'ArrowDown' ? 1 : -1
      searchNavigationDirectionRef.current = direction
      setSearchNavigationIndex((current) =>
        moveSearchNavigationIndex(current, renderedSearchResults.length, direction),
      )
      return
    }
    if (event.key === 'Enter' && activeSearchResult) {
      event.preventDefault()
      const note = notesById.get(activeSearchResult.id)
      if (note) {
        void openNote(note, activeSearchResult.matchTerms[0], captureSearchReturnState())
      }
      return
    }
    if (event.key === 'Escape') {
      event.preventDefault()
      if (getSearchEscapeAction(
        searchQuery,
        hasActiveSearchFilters,
        isDesktopBrowseRuntime(),
      ) === 'refocus-search') {
        searchRef.current?.focus({ preventScroll: true })
        return
      }
      if (getSearchEscapeAction(searchQuery, hasActiveSearchFilters) === 'clear-search') {
        clearSearch()
        return
      }
      switchMode('capture')
    }
  }

  const restorePendingBrowseViewport = useCallback(() => {
    const viewport = searchRestoreViewportRef.current ?? pendingBrowseViewportRef.current
    if (!viewport) return
    searchRestoreViewportRef.current = null
    pendingBrowseViewportRef.current = null
    restoreBrowseViewportWhenReady(
      viewport,
      APP_SETTINGS.interactions.browseViewportRestoreFrameBudget,
    )
    if (shouldRestoreBrowseInputFocus(studio.runtime.formFactor)) {
      searchRef.current?.focus({ preventScroll: true })
    }
  }, [studio.runtime.formFactor])

  useLayoutEffect(() => {
    if (mode === 'capture' && searchResultOpenScrollRef.current) {
      searchResultOpenScrollRef.current = false
      window.scrollTo(0, 0)
      return
    }
    if (mode !== 'search' || browseCovered) return
    if (!studioActive) restorePendingBrowseViewport()
  }, [browseCovered, mode, notes, restorePendingBrowseViewport, studioActive])

  useEffect(() => {
    if (mode !== 'search' || browseCovered) return
    if (activeSearchResultDomId && skipActiveSearchScrollRef.current) {
      skipActiveSearchScrollRef.current = false
      return
    }
    const frame = requestAnimationFrame(() => {
      const navigationDirection = searchNavigationDirectionRef.current
      searchNavigationDirectionRef.current = null
      const result = activeSearchResultDomId
        ? document.getElementById(activeSearchResultDomId)
        : null
      const controls = searchControlsRef.current
      const summary = searchSummaryRef.current
      if (controls && summary) {
        scrollSearchNavigationIntoView(result, controls, summary, navigationDirection)
      }
    })
    return () => cancelAnimationFrame(frame)
  }, [activeSearchResultDomId, browseCovered, mode])

  useLayoutEffect(() => {
    if (!browseSurfaceMounted || !searchMasonryRef.current) return
    return installSearchResultPaintWindow(
      searchMasonryRef.current,
      APP_SETTINGS.interactions.searchResultPaintWindowMarginPercent,
    )
  }, [browseSurfaceMounted, hasVisibleSearchResults])

  useLayoutEffect(() => {
    if (!browseSurfaceMounted || !searchMasonryRef.current) return
    return installMeasuredMasonryLayout(searchMasonryRef.current)
  }, [browseSurfaceMounted, hasVisibleSearchResults])

  // Inline desktop surfaces restore after the masonry pass. The touch shell retains its
  // existing modal lifecycle and restoration ordering.
  useLayoutEffect(() => {
    if (studioActive && mode === 'search' && !browseCovered) restorePendingBrowseViewport()
  }, [browseCovered, mode, notes, restorePendingBrowseViewport, studioActive])

  const activeNote = notesById.get(draft.noteId)
  useEffect(() => {
    const routeNoteId = readNoteRoute()
    document.title = adminRouteActive
      ? comparisonRouteActive ? 'Search comparison · miniMany' : 'Admin · miniMany'
      : mode === 'capture' && activeNote && routeNoteId === activeNote.id
        ? `${activeNote.manualTitle ?? activeNote.derivedTitle} · miniMany`
        : 'miniMany'
  }, [activeNote, adminRouteActive, comparisonRouteActive, mode])
  const activeUploadStatus = deriveNoteUploadStatus(
    activeNote ?? null,
    activeAssets,
    noteSyncAttempts.get(draft.noteId) ?? 0,
    uploadStatusClock,
  )
  const liveUrls = useMemo(
    () => mergeUrlMetadata(attachmentUrlsForDraft(draft), activeNote?.urls, {
      now: Date.now(), legacyAddedAt: activeNote?.createdAt ?? draft.createdAt,
    }),
    [activeNote?.urls, activeNote?.createdAt, draft.body, draft.createdAt, draft.suppressedUrlKeys],
  )
  // A note whose first block is its only link shows that link as a card above the editor, so the
  // dock and rail do not list it a second time (docs/link-notes/PLAN.md, phase 2).
  const leadingCard = useMemo(() => leadingLinkCard(draft.body, liveUrls), [draft.body, liveUrls])
  // The note's leading link and its unpacked screenshot are one preview when they are about the same thing (links/mergeUnpacked.ts).
  const mergedUnpacked = Boolean(leadingCard && cleanupWorkspace && cleanupViews.get(draft.noteId) !== 'original' && sameSubject(leadingCard, cleanupCards.get(draft.noteId)))
  // The large preview shown as a sheet (for a link that is not the note's card), named by its link key.
  const [previewSheetKey, setPreviewSheetKey] = useState<string | null>(null)
  const previewSheetUrl = previewSheetKey ? liveUrls.find((url) => linkMetadataUrlKey(url) === previewSheetKey) ?? null : null
  // Everything generated about one link, full screen: named by the link and the generation to open on.
  const [answersView, setAnswersView] = useState<{ key: string; selected: string } | null>(null)
  const answersViewUrl = answersView ? liveUrls.find((url) => linkMetadataUrlKey(url) === answersView.key) ?? null : null
  const expandGenerations = (url: UrlEntity) => (selected: string) => setAnswersView({ key: linkMetadataUrlKey(url), selected })
  const gistUiFor = (url: UrlEntity) => !gistsEnabled() ? undefined : {
    status: gistController.statusOf(url),
    statusDetail: gistController.detailOf(url),
    blockedReason: (() => { const known = gistController.readabilityOf(url); return known.state === 'blocked' ? known.reason : null })(),
    want: (trigger: GistTrigger, refresh?: boolean) => void gistController.request(url, trigger, { refresh }),
    hide: () => void gistController.hide(url),
  }
  // The one display switch (Pill, Row, Preview) behind the card, the preview sheet, rows and pills. Preview is the card above the
  // note when the link can lead it (alone in the note's first block), kept as the person's choice so it holds with or without a
  // picture; for any other link it is the sheet.
  const chooseLinkDisplay = (url: UrlEntity, choice: LinkDisplayChoice) => {
    const key = linkMetadataUrlKey(url)
    if (choice !== 'preview') {
      setPreviewSheetKey(null)
      if (url.display !== choice) void saveLinkDisplay(draft.noteId, key, choice)
      return
    }
    const asCard = leadingLinkCard(draft.body, liveUrls.map((candidate) => linkMetadataUrlKey(candidate) === key ? { ...candidate, display: 'preview' as const } : candidate))
    if (asCard) {
      setPreviewSheetKey(null)
      if (url.display !== 'preview') void saveLinkDisplay(draft.noteId, key, 'preview')
    } else setPreviewSheetKey(key)
  }
  // Links live in the note (card, row or chip); the images are the only attachments. Where they sit depends on who leads the note:
  // above the text for an image-led note, and for a text-led note a band on a phone or a column beside the text on a desktop
  // (apps/app/DESIGN.md, "Images in a note"). The weight is read when the note opens, never while it is being typed.
  const editorImageAttachmentCount = Math.max(draft.assetIds?.length ?? 0, activeAssets.length)
  // A note shared before the label was left out holds only its screenshot's file label ("Screenshot (22 Oct …)"), which is not the
  // person's words: it is cleared once, so the note shows no made-up title or text.
  useEffect(() => {
    if (mode === 'capture' && editorImageAttachmentCount > 0 && draft.body.trim() && isSharedFileLabel(draft.body)) updateDraft((current) => ({ ...current, body: '' }))
  }, [draft.noteId, draft.body, editorImageAttachmentCount, mode, updateDraft])
  const imageStage = useImageStageLayout(draft.noteId, mode, draft.body)
  // Clean up's clean view takes the images' place, above the text and in the desktop column alike.
  // An unpacked note opens on its unpacked view at once (its card is known from the start), never flashing its screenshots first;
  // the person's own choice on this device wins after that.
  const cleanupView = cleanupViews.get(draft.noteId) ?? (cleanupCards.has(draft.noteId) ? 'clean' : undefined)
  const cleanView = cleanupShownEnabled && cleanupView === 'clean'
  // An unpacked shared message, still exactly as shared, folds under its card (screenshotCleanup/CleanupPanel.tsx, decision 23).
  const sharedTextFolded = cleanView && sharedTextFolds && sharedTextShown !== draft.noteId
  const imageAside = editorImageAttachmentCount > 0 && studio.runtime.formFactor === 'desktop' && imageStage.textLed && !cleanView
  const editorLinkTitle = leadingCard ? null : linkOnlyEditorTitle(
    draft.body, liveUrls, activeNote, (draft.assetIds?.length ?? 0) > 0,
  )
  const editorFallbackTitleForUrl = useCallback(
    (url: UrlEntity) => authoredUrlLabelInMarkdown(draft.body, url) ?? undefined,
    [draft.body],
  )
  const localModeActive = !authRestoring && (authProvider.id === 'local' || !authSession)
  const canActOnDraft = Boolean(activeNote || hasDraftContent(draft))
  const canCopyDraft = draft.body.trim().length > 0
  const recentNotes = useMemo(() => {
    const now = Date.now()
    return notes
      .filter(
        (note) =>
          note.id !== draft.noteId &&
          !['archived', 'expired', 'trash'].includes(note.status) &&
          !(note.status === 'snoozed' && note.snoozedUntil && note.snoozedUntil > now),
      )
      .slice(0, 8)
  }, [draft.noteId, notes])
  const handoffNotes = useMemo(
    () => notes
      .filter((note) => note.originDeviceId !== deviceId && note.status !== 'trash')
      .slice(0, 3),
    [deviceId, notes],
  )
  const nextManualRetryRevealAt = useMemo(() => assets.reduce<number | null>((earliest, asset) => {
    if (asset.state !== 'error' || asset.attempts >= IMAGE_CONFIG.manualRetryMinAttempts) {
      return earliest
    }
    const revealAt = asset.modifiedAt + IMAGE_CONFIG.manualRetryDelayMs
    if (revealAt <= uploadStatusClock) return earliest
    return earliest === null ? revealAt : Math.min(earliest, revealAt)
  }, null), [assets, uploadStatusClock])

  useEffect(() => {
    if (nextManualRetryRevealAt === null) return
    const timer = window.setTimeout(
      () => setUploadStatusClock(Date.now()),
      Math.max(0, nextManualRetryRevealAt - Date.now()) + 10,
    )
    return () => window.clearTimeout(timer)
  }, [nextManualRetryRevealAt])

  useEffect(() => {
    const target = searchLoadMoreRef.current
    if (mode !== 'search' || !hasMoreSearchResults || !target) return
    const observer = new IntersectionObserver(
      (entries) => {
        if (!entries[0]?.isIntersecting) return
        startTransition(() => {
          showMoreSearchResults()
        })
      },
      { rootMargin: '600px 0px' },
    )
    observer.observe(target)
    return () => observer.disconnect()
  }, [hasMoreSearchResults, mode, showMoreSearchResults, visibleSearchCount])

  const refreshNoteSyncAttempts = useCallback(async () => {
    const operations = await repository.listAllOutbox()
    const next = new Map<string, number>()
    for (const operation of operations) {
      if (operation.entityType === 'note') next.set(operation.entityId, operation.attemptCount)
    }
    setNoteSyncAttempts((current) => {
      if (
        current.size === next.size
        && [...current].every(([noteId, attempts]) => next.get(noteId) === attempts)
      ) return current
      return next
    })
  }, [])

  const runSync = useCallback((request: SyncRequest): Promise<SyncCycleResult> => {
    if (!syncCoordinator || !loaded || !isOnline || !authSession) {
      return Promise.resolve(emptySyncCycleResult())
    }
    if (syncRunRef.current) return syncRunRef.current

    const task = measureDiagnosticActivity('sync:cycle', async () => {
      // Quiet by default: a cycle that ends in time is never shown; only one running far longer than expected is.
      const cancelProgress = scheduleStallNotice(() => {
        setBackgroundSyncState('syncing')
      })
      try {
        const summary = await syncCoordinator.syncOnce(authSession.user.id, {
          push: request.push,
          pullNotes: request.domains.includes('notes'),
          pullSettings: request.domains.includes('settings'),
        })
        searchIndex.addBatch(acceptedSynchronizedNoteUpdates(notesRef.current, summary.changedNotes))
        const currentDraft = draftRef.current
        const previouslyRenderedNote = notesRef.current.get(currentDraft.noteId) ?? null
        const synchronizedActiveNote = summary.changedNotes.find(
          (note) => note.id === currentDraft.noteId,
        )
        const replacement = synchronizedActiveNote
          ? synchronizedDraftReplacement(
              currentDraft,
              previouslyRenderedNote,
              synchronizedActiveNote,
            )
          : null
        if (replacement) {
          draftRef.current = replacement
          resetEditorHistory()
          setDraft(replacement)
          setSaveState('local')
          void repository.saveActiveDraft(replacement).catch(() => setSaveState('error'))
        }
        const mergedNotes = mergeSynchronizedNotes([...notesRef.current.values()], summary.changedNotes)
        setNotes((current) => mergeSynchronizedNotes(current, summary.changedNotes))
        await updateNativeQuickAccessWidget(mergedNotes, true)
        if (quickAccessRefreshPendingRef.current && request.domains.includes('notes')) {
          quickAccessRefreshPendingRef.current = false
          recordDiagnosticMilestone('quick-access:refresh-complete', { outcome: 'synced' })
        }
        for (const settings of summary.changedSettings) {
          if (settings.recordType === 'global') {
            setGlobalSettings(settings)
          } else if (settings.deviceId === deviceId) {
            setDeviceSettings(settings)
            writeBootstrapStartMode(settings.startMode)
            writeBootstrapUiSize(settings.uiSize)
            applyUiSize(settings.uiSize)
            if (document.activeElement !== deviceNameInputRef.current) {
              setDeviceNameDraft(displayDeviceName(settings))
            }
          }
        }
        if (request.push || request.domains.includes('assets')) {
          await imageQueue.wake(authSession.user.id)
        }
        const appliedAssets: NoteAsset[] = []
        let assetPullComplete = !request.domains.includes('assets')
        if (assetTransport && request.domains.includes('assets')) {
          assetPullComplete = false
          if (!assetPullCursorReadyRef.current) {
            const storedCursor = await repository.getMeta('asset-sync-cursor')
            assetPullCursorRef.current = typeof storedCursor === 'string' ? storedCursor : null
            assetPullCursorReadyRef.current = true
          }
          for (let page = 0; page < APP_SETTINGS.sync.maxPullPages; page += 1) {
            const pulledAssets = await assetTransport.pull(assetPullCursorRef.current)
            appliedAssets.push(...await Promise.all(
              pulledAssets.assets.map((asset) => repository.applyRemoteAsset(asset)),
            ))
            assetPullCursorRef.current = pulledAssets.cursor
            await repository.setMeta('asset-sync-cursor', pulledAssets.cursor)
            if (pulledAssets.assets.length < APP_SETTINGS.sync.pullPageSize) {
              assetPullComplete = true
              break
            }
          }
          setAssets((current) => {
            const byId = new Map(current.map((asset) => [asset.id, asset]))
            for (const asset of appliedAssets) byId.set(asset.id, asset)
            return [...byId.values()]
          })
        }
        if (contextSyncOwnerRef.current === authSession.user.id
          && (request.push || request.domains.includes('contexts') || summary.changedNotes.length)) {
          await syncAuthorizedContexts(authSession.user.id, () => contextSyncOwnerRef.current)
        }
        const latestAccountLimitBlock = summary.accountLimitBlocks.at(-1)
        const nextError = !summary.settingsSyncAvailable
          ? 'Settings are safe on this device. Apply the settings migration to enable cross-device sync.'
          : latestAccountLimitBlock
            ? `Your account has reached its ${latestAccountLimitBlock.limit.toLocaleString()}-note limit. The blocked note remains saved on this device.`
            : null
        if (summary.pushed > 0 || summary.accountLimitBlocks.length > 0) {
          setAccountAccessRefreshKey((current) => current + 1)
        }
        setSyncError(nextError)
        setBackgroundSyncState(nextError ? 'error' : 'idle')
        if (!accountRestoreReadyRef.current) {
          if (nextError) setAccountRestorePhase('error')
          const evidence = applyRestoreCycleEvidence(restoreDomainsVerifiedRef.current, {
            requestedDomains: request.domains,
            notePullComplete: summary.notePullComplete,
            settingsPullComplete: summary.settingsSyncAvailable,
            assetPullComplete,
          })
          restoreDomainsVerifiedRef.current = evidence.verifiedDomains
          if (evidence.retryDomains.length > 0 && summary.settingsSyncAvailable) {
            requestSyncRef.current({ push: false, domains: evidence.retryDomains }, 0)
          }
          if (evidence.allDomainsVerified) {
            const continuity = await repository.inspectLocalDataContinuity()
            if (continuity.safeForServerRestoreCutover) {
              const [remoteInventory, localNotes, localAssets] = await Promise.all([
                syncCoordinator.getRemoteInventory(authSession.user.id),
                repository.listNotes(),
                repository.listAssets(),
              ])
              let inventory = reconcileRestoreInventory(
                {
                  noteIds: localNotes.map((note) => note.id),
                  assetIds: localAssets.map((asset) => asset.id),
                },
                remoteInventory,
              )
              if (
                !inventory.matches
                && inventory.assetIdsMatch
                && inventory.missingAssetObjectCount === 0
              ) {
                const repairableNoteIds = synchronizedTrashNotesMissingFromRemote(
                  localNotes,
                  remoteInventory.noteIds,
                )
                if (repairableNoteIds?.length) {
                  const deletedNoteIds = await repository.deleteSynchronizedTrashNotes(
                    repairableNoteIds,
                  )
                  if (deletedNoteIds.length === repairableNoteIds.length) {
                    const deletedIds = new Set(deletedNoteIds)
                    const repairedNotes = localNotes.filter((note) => !deletedIds.has(note.id))
                    searchIndex.load(repairedNotes)
                    setNotes(repairedNotes)
                    inventory = reconcileRestoreInventory(
                      {
                        noteIds: repairedNotes.map((note) => note.id),
                        assetIds: localAssets.map((asset) => asset.id),
                      },
                      remoteInventory,
                    )
                    recordDiagnosticMilestone('restore:stale-trash-repaired', {
                      deletedNoteCount: deletedNoteIds.length,
                    })
                  }
                }
              }
              recordDiagnosticMilestone('restore:inventory-checked', {
                matches: inventory.matches,
                noteIdsMatch: inventory.noteIdsMatch,
                assetIdsMatch: inventory.assetIdsMatch,
                missingAssetObjectCount: inventory.missingAssetObjectCount,
                localNoteCount: inventory.localNoteCount,
                remoteNoteCount: inventory.remoteNoteCount,
                localAssetCount: inventory.localAssetCount,
                remoteAssetCount: inventory.remoteAssetCount,
              })
              if (inventory.missingAssetObjectCount > 0) {
                const message = 'Account restore stopped because one or more image files are missing on the server.'
                setSyncError(message)
                setBackgroundSyncState('error')
                setAccountRestorePhase('error')
              } else if (!inventory.matches) {
                if (
                  restoreInventoryRetryCountRef.current
                  < APP_SETTINGS.sync.restoreInventoryMaxRetries
                ) {
                  restoreInventoryRetryCountRef.current += 1
                  await Promise.all([
                    syncCoordinator.resetNoteRestoreCursor(authSession.user.id),
                    repository.deleteMeta('asset-sync-cursor'),
                  ])
                  assetPullCursorRef.current = null
                  assetPullCursorReadyRef.current = true
                  restoreDomainsVerifiedRef.current.delete('notes')
                  restoreDomainsVerifiedRef.current.delete('assets')
                  setAccountRestorePhase('restoring')
                  requestSyncRef.current({ push: false, domains: ['notes', 'assets'] }, 0)
                } else {
                  const message = 'Account restore could not reconcile all notes and images with the server.'
                  setSyncError(message)
                  setBackgroundSyncState('error')
                  setAccountRestorePhase('error')
                }
              } else {
                accountRestoreReadyRef.current = true
                setAccountRestorePhase('ready')
                recordDiagnosticMilestone('restore:complete', {
                  notes: inventory.localNoteCount,
                  assets: continuity.assets.total,
                  remoteOnlyAssets: continuity.assets.remoteOnly,
                })
              }
            } else {
              setAccountRestorePhase('restoring')
            }
          }
        }
        const removedNoteIds = summary.changedNotes
          .filter(isGoogleKeepRollbackTombstone)
          .map((note) => note.id)
        const result = {
          noteIds: summary.changedNotes
            .filter((note) => !isGoogleKeepRollbackTombstone(note))
            .map((note) => note.id),
          removedNoteIds,
          settingKeys: summary.changedSettings.map((settings) => settings.key),
          assetIds: appliedAssets.map((asset) => asset.id),
        }
        const recoveredFailure = syncFailureRef.current
        syncFailureRef.current = null
        recordDiagnosticMilestone('sync:cycle-complete', {
          push: request.push,
          domains: request.domains.join(','),
          pushed: summary.pushed,
          pulled: summary.pulled,
          changed: result.noteIds.length + result.removedNoteIds.length
            + result.settingKeys.length + result.assetIds.length,
          recoveredFailures: recoveredFailure?.consecutiveFailures ?? 0,
        })
        return result
      } catch (error) {
        await updateNativeQuickAccessWidget(notesRef.current.values(), false)
        if (quickAccessRefreshPendingRef.current && request.domains.includes('notes')) {
          quickAccessRefreshPendingRef.current = false
          recordDiagnosticMilestone('quick-access:refresh-complete', { outcome: 'failed-restored' })
        }
        const message = error instanceof Error
          ? error.message
          : 'Background synchronization failed.'
        const failure = nextSyncFailureState(syncFailureRef.current, message)
        syncFailureRef.current = failure
        const exposed = shouldExposeSyncFailure(failure)
        recordDiagnosticMilestone('sync:cycle-failed', {
          push: request.push,
          domains: request.domains.join(','),
          consecutiveFailures: failure.consecutiveFailures,
          failureAgeMs: failure.lastFailureAt - failure.firstFailureAt,
          exposed,
          errorType: error instanceof Error ? error.name : typeof error,
        })
        setBackgroundSyncState(exposed ? 'error' : 'idle')
        if (exposed) {
          setSyncError(message)
          if (!accountRestoreReadyRef.current) setAccountRestorePhase('error')
        } else if (!accountRestoreReadyRef.current) {
          setAccountRestorePhase('restoring')
        }
        throw error
      } finally {
        await refreshNoteSyncAttempts().catch(() => undefined)
        cancelProgress()
        syncRunRef.current = null
      }
    })
    syncRunRef.current = task
    return task
  }, [
    applyVerifiedAccessProfile,
    authSession,
    deviceId,
    isOnline,
    loadAccessProfile,
    loaded,
    refreshNoteSyncAttempts,
    resetEditorHistory,
    searchIndex,
  ])

  const retryNoteUpload = useCallback(async (noteId: string, noteAssets: NoteAsset[]) => {
    const now = Date.now()
    const retriedAssets = noteAssets
      .filter((asset) => asset.state === 'error')
      .map((asset): NoteAsset => ({
        ...asset,
        state: asset.errorStage === 'processing' ? 'local' : 'ready_to_upload',
        errorStage: null,
        nextAttemptAt: now,
        lastError: null,
        modifiedAt: now,
      }))
    try {
      await Promise.all([
        repository.retryNoteOperation(noteId, now),
        ...retriedAssets.map((asset) => repository.putAsset(asset)),
      ])
      const retriedAssetsById = new Map(retriedAssets.map((asset) => [asset.id, asset]))
      if (retriedAssetsById.size > 0) {
        setAssets((current) => current.map((asset) => retriedAssetsById.get(asset.id) ?? asset))
      }
      setNoteSyncAttempts((current) => {
        const next = new Map(current)
        next.set(noteId, 0)
        return next
      })
      requestSyncRef.current({ push: true, domains: ['assets'] }, 0)
      await imageQueue.wake(authSessionRef.current?.user.id ?? null)
    } catch {
      setSaveState('error')
      void notify.error('The upload could not be retried yet.')
    }
  }, [])

  useEffect(() => {
    if (loaded) void refreshNoteSyncAttempts()
  }, [loaded, refreshNoteSyncAttempts])

  useEffect(() => {
    if (!loaded) return
    const wake = () => {
      if (document.visibilityState === 'visible') {
        void imageQueue.wake(authSession?.user.id ?? null)
      }
    }
    wake()
    window.addEventListener('online', wake)
    document.addEventListener('visibilitychange', wake)
    return () => {
      window.removeEventListener('online', wake)
      document.removeEventListener('visibilitychange', wake)
    }
  }, [authSession?.user.id, loaded])

  const remoteOnlyAssetKey = useMemo(() => remotePreviewCacheKey(assets), [assets])

  useEffect(() => {
    if (!assetTransport || !authSession) {
      setRemoteAssetUrls(new Map())
      return
    }
    if (!isOnline || !remoteOnlyAssetKey) return
    const remoteOnlyAssets = assets.filter(remoteAssetNeedsPreviewCache)
    let cancelled = false
    let running = false
    let retryAttempt = 0
    let retryTimer: number | null = null

    const scheduleRetry = (failedAssetCount: number) => {
      if (cancelled || retryTimer !== null) return
      retryAttempt += 1
      const delayMs = remotePreviewRetryDelay(retryAttempt)
      recordDiagnosticMilestone('image:preview-cache-retry', {
        attempt: retryAttempt,
        delayMs,
        failedAssetCount,
      })
      retryTimer = window.setTimeout(() => {
        retryTimer = null
        void refresh()
      }, delayMs)
    }

    const refresh = async () => {
      if (cancelled || running || !navigator.onLine) return
      running = true
      try {
        const urls = await assetTransport.getPreviewUrls(remoteOnlyAssets)
        if (cancelled) return
        setRemoteAssetUrls((current) => new Map([...current, ...urls]))
        const result = await cacheRemoteAssetPreviews({
          assets: remoteOnlyAssets,
          urls,
          store: repository,
        })
        if (cancelled) return
        if (result.cachedAssets.length > 0) {
          setAssets((current) => {
            const byId = new Map(current.map((asset) => [asset.id, asset]))
            for (const asset of result.cachedAssets) byId.set(asset.id, asset)
            return [...byId.values()]
          })
          recordDiagnosticMilestone('image:preview-cache-written', {
            count: result.cachedAssets.length,
          })
        }
        if (result.failedAssetIds.length > 0) {
          scheduleRetry(result.failedAssetIds.length)
        } else {
          retryAttempt = 0
        }
      } catch (error) {
        if (!cancelled) {
          recordDiagnosticMilestone('image:preview-cache-failed', {
            errorType: error instanceof Error ? error.name : typeof error,
          })
          scheduleRetry(remoteOnlyAssets.length)
        }
      } finally {
        running = false
      }
    }

    const wakeVisibleCache = () => {
      if (document.visibilityState !== 'visible') return
      if (retryTimer !== null) window.clearTimeout(retryTimer)
      retryTimer = null
      void refresh()
    }
    void refresh()
    document.addEventListener('visibilitychange', wakeVisibleCache)
    return () => {
      cancelled = true
      document.removeEventListener('visibilitychange', wakeVisibleCache)
      if (retryTimer !== null) window.clearTimeout(retryTimer)
    }
  }, [authSession?.user.id, isOnline, remoteOnlyAssetKey])

  const applyPeerSyncResult = useCallback(async (result: SyncCycleResult) => {
    const removedNoteIds = Array.isArray(result.removedNoteIds) ? result.removedNoteIds : []
    const [peerNotes, peerSettings, peerAssets] = await Promise.all([
      Promise.all(result.noteIds.map((noteId) => repository.getNote(noteId))),
      Promise.all(result.settingKeys.map((key) => repository.getSetting(key))),
      Promise.all(result.assetIds.map((assetId) => repository.getAsset(assetId))),
    ])
    const synchronizedNotes = peerNotes.filter((note): note is Note => Boolean(note))
    if (removedNoteIds.length > 0) {
      for (const noteId of removedNoteIds) searchIndex.remove(noteId)
      const removed = new Set(removedNoteIds)
      setNotes((current) => current.filter((note) => !removed.has(note.id)))
    }
    if (synchronizedNotes.length > 0) {
      searchIndex.addBatch(acceptedSynchronizedNoteUpdates(notesRef.current, synchronizedNotes))
      const currentDraft = draftRef.current
      const activeNote = synchronizedNotes.find((note) => note.id === currentDraft.noteId)
      const replacement = activeNote
        ? synchronizedDraftReplacement(
            currentDraft,
            notesRef.current.get(currentDraft.noteId) ?? null,
            activeNote,
          )
        : null
      if (replacement) {
        draftRef.current = replacement
        resetEditorHistory()
        setDraft(replacement)
        setSaveState('local')
        void repository.saveActiveDraft(replacement).catch(() => setSaveState('error'))
      }
      setNotes((current) => mergeSynchronizedNotes(current, synchronizedNotes))
    }
    for (const settings of peerSettings) {
      if (!settings) continue
      if (settings.recordType === 'global') setGlobalSettings(settings)
      else if (settings.deviceId === deviceId) {
        setDeviceSettings(settings)
        writeBootstrapStartMode(settings.startMode)
        writeBootstrapUiSize(settings.uiSize)
        applyUiSize(settings.uiSize)
      }
    }
    const synchronizedAssets = peerAssets.filter((asset): asset is NoteAsset => Boolean(asset))
    if (synchronizedAssets.length > 0) {
      setAssets((current) => {
        const byId = new Map(current.map((asset) => [asset.id, asset]))
        for (const asset of synchronizedAssets) byId.set(asset.id, asset)
        return [...byId.values()]
      })
    }
    syncFailureRef.current = null
    setSyncError(null)
    setBackgroundSyncState('idle')
    await refreshNoteSyncAttempts()
  }, [deviceId, refreshNoteSyncAttempts, resetEditorHistory, searchIndex])

  useEffect(() => {
    if (!syncCoordinator || !loaded || !authSession || !supabaseConfig) {
      requestSyncRef.current = () => undefined
      return
    }
    let runtime: SyncRuntime | null = null
    let disposeRealtime: (() => Promise<void>) | null = null
    let cancelled = false
    let leadershipGeneration = 0
    const accountKey = `${supabaseConfig.environment}:${authSession.user.id}`
    const coordinator = new SyncTabCoordinator({
      accountKey,
      onLeadershipChange: (leader) => {
        recordDiagnosticMilestone('sync:leadership', { leader })
        leadershipGeneration += 1
        const generation = leadershipGeneration
        if (!leader) {
          runtime?.dispose()
          runtime = null
          syncRuntimeRef.current = null
          if (disposeRealtime) void disposeRealtime()
          disposeRealtime = null
          requestSyncRef.current = (request, delayMs) => coordinator.request(request, delayMs)
          return
        }
        runtime = new SyncRuntime({
          execute: runSync,
          nextAttempt: async () => {
            const [outboxAt, assetAt] = await Promise.all([
              repository.getNextOutboxAttemptAt(),
              repository.getNextAssetAttemptAt(),
            ])
            if (outboxAt === null && assetAt === null) return null
            if (assetAt !== null && (outboxAt === null || assetAt < outboxAt)) {
              return { at: assetAt, request: { push: false, domains: ['assets'] } }
            }
            return { at: outboxAt!, request: { push: true, domains: ['assets'] } }
          },
          onResult: (result) => coordinator.publishResult(result),
          isOnline: () => navigator.onLine,
          isVisible: () => document.visibilityState === 'visible',
          // Neon has no change hints: poll at one calmer interval instead of the 15 s "hints are down" rate, which would keep the database awake.
          ...(supabaseConfig.backend === 'neon' ? { fixedSafetyPollMs: APP_SETTINGS.sync.noHintSafetyPollMs } : {}),
        })
        syncRuntimeRef.current = runtime
        requestSyncRef.current = (request, delayMs) => runtime?.request(request, delayMs)
        runtime.start()
        void subscribeToSyncInvalidations(
          authSession.user.id,
          supabaseConfig.isDev,
          (domain: SyncDomain) => runtime?.request(
            { push: false, domains: [domain] },
            APP_SETTINGS.sync.realtimeInvalidationDebounceMs,
          ),
          (status) => {
            recordDiagnosticMilestone('sync:realtime-status', { status })
            runtime?.setRealtimeConnected(status === 'connected')
          },
        ).then((dispose) => {
          if (cancelled || generation !== leadershipGeneration || !runtime) void dispose()
          else disposeRealtime = dispose
        }).catch(() => {
          if (generation === leadershipGeneration) runtime?.setRealtimeConnected(false)
        })
      },
      onRequest: (request, delayMs) => runtime?.request(request, delayMs),
      onPeerResult: (result) => { void applyPeerSyncResult(result) },
    })
    syncTabCoordinatorRef.current = coordinator
    requestSyncRef.current = (request, delayMs) => coordinator.request(request, delayMs)
    coordinator.start()
    coordinator.request({
      push: true,
      domains: ['notes', 'settings', 'assets', 'contexts'],
    }, 0)
    return () => {
      cancelled = true
      coordinator.dispose()
      runtime?.dispose()
      if (disposeRealtime) void disposeRealtime()
      syncRuntimeRef.current = null
      syncTabCoordinatorRef.current = null
      requestSyncRef.current = () => undefined
    }
  }, [applyPeerSyncResult, authSession?.user.id, loaded, runSync])

  useEffect(() => {
    if (loaded && authSession) {
      requestSyncRef.current({ push: true, domains: ['notes', 'contexts'] }, 0)
    }
  }, [loaded, authSession?.user.id])

  useEffect(() => {
    if (loaded && contextsEnabled) requestSyncRef.current({ push: true, domains: ['contexts'] }, 0)
  }, [loaded, contextsEnabled, contextsOwner])

  useEffect(() => {
    if (
      quickAccessRefreshRequest === 0
      || handledQuickAccessRefreshRequestRef.current === quickAccessRefreshRequest
      || authRestoring
      || !loaded
    ) return
    if (!authSession || !isOnline) {
      handledQuickAccessRefreshRequestRef.current = quickAccessRefreshRequest
      recordDiagnosticMilestone('quick-access:refresh-requested', { online: false })
      void updateNativeQuickAccessWidget(notesRef.current.values(), true).then(() => {
        recordDiagnosticMilestone('quick-access:refresh-complete', { outcome: 'offline-restored' })
      })
      return
    }
    if (!syncCoordinator) return
    handledQuickAccessRefreshRequestRef.current = quickAccessRefreshRequest
    quickAccessRefreshPendingRef.current = true
    recordDiagnosticMilestone('quick-access:refresh-requested', { online: true })
    requestSyncRef.current({ push: true, domains: ['notes'] }, 0)
  }, [authRestoring, authSession, isOnline, loaded, quickAccessRefreshRequest])

  useEffect(() => {
    if (!syncCoordinator || !loaded || !authSession) return
    const requestCatchUp = (event: Event) => {
      if (navigator.onLine && document.visibilityState === 'visible') {
        requestSyncRef.current({ push: true, domains: ['notes', 'settings', 'assets', 'contexts'], retryNow: event.type === 'online' }, 0)
      }
    }
    const contextChanged = (event: Event) => {
      if ((event as CustomEvent).detail === authSession.user.id) requestSyncRef.current({ push: true, domains: ['notes', 'contexts'] })
    }
    const demoNotesChanged = (event: Event) => {
      const owner = (event as CustomEvent).detail
      if (owner !== authSession.user.id) return
      void repository.listNotes().then(rows => {
        if (authSessionRef.current?.user.id !== owner) return
        setNotes(rows); searchIndex.load(rows)
        requestSyncRef.current({ push: true, domains: ['notes', 'contexts'] }, 0)
      }).catch(() => { void notify.error('Reopen your notes to refresh the archived sample.') })
    }
    window.addEventListener('context-demo-notes-changed', demoNotesChanged)
    window.addEventListener('minimany:manual-context-change', contextChanged)
    window.addEventListener('online', requestCatchUp)
    window.addEventListener('focus', requestCatchUp)
    window.addEventListener('pageshow', requestCatchUp)
    document.addEventListener('visibilitychange', requestCatchUp)
    return () => {
      window.removeEventListener('minimany:manual-context-change', contextChanged)
      window.removeEventListener('context-demo-notes-changed', demoNotesChanged)
      window.removeEventListener('online', requestCatchUp)
      window.removeEventListener('focus', requestCatchUp)
      window.removeEventListener('pageshow', requestCatchUp)
      document.removeEventListener('visibilitychange', requestCatchUp)
    }
  }, [authSession?.user.id, loaded])

  const switchMode = (nextMode: AppMode) => {
    if (nextMode === mode) return
    if (mode === 'capture' && nextMode === 'search') {
      if (searchReturnRef.current) void returnToSearchResultContext()
      else { browseFromNoteRef.current = true; void leaveCapture() }
      return
    }
    void flushDraft().catch(() => undefined)
    startTransition(() => setMode(nextMode))
  }

  const armBackGuard = useCallback(() => {
    if (backGuardArmedRef.current) return
    const currentState = window.history.state
    const routedNoteId = readNoteRoute()
    if (currentState?.[HISTORY_GUARD_KEY] === true && !routedNoteId) {
      backGuardArmedRef.current = true
      return
    }
    const state = currentState && typeof currentState === 'object' ? currentState : {}
    const baseState = { ...state }
    delete baseState[HISTORY_GUARD_KEY]
    pushGuardedRouteEntry(baseState, { ...baseState, [HISTORY_GUARD_KEY]: true })
    backGuardArmedRef.current = true
  }, [])

  useEffect(() => {
    if (oauthCallbackPendingRef.current) return
    if (!backGuardInitializedRef.current) {
      backGuardInitializedRef.current = true
      armBackGuard()
      return
    }
    if (suppressBackGuardRearmRef.current) {
      suppressBackGuardRearmRef.current = false
      return
    }
    armBackGuard()
  }, [
    armBackGuard,
    authSession?.user.id,
    draft.updatedAt,
    mode,
    oauthCallbackHandled,
    searchFilters.property,
    searchFilters.hasAlarm,
    searchFilters.status,
    searchFilters.tag,
    searchQuery,
  ])

  useEffect(() => {
    let restoringContextStep = false
    const handleBrowserBack = () => {
      if (restoringContextStep) { restoringContextStep = false; return }
      if (dismissContextStep('steps')) {
        // Back is undone with a step forward; when there is none, the next Back must not be swallowed waiting for it.
        restoringContextStep = true
        window.setTimeout(() => { restoringContextStep = false }, APP_SETTINGS.interactions.backTransitionWaitMs)
        window.history.forward()
        return
      }
      const routeIsAdmin = readAdminRoute()
      setComparisonRouteActive(window.location.pathname === '/admin/experiments/search')
      if (routeIsAdmin !== adminRouteActiveRef.current) {
        setAdminRouteActive(routeIsAdmin)
        if (routeIsAdmin) setAccountPanelOpen(false)
        backGuardArmedRef.current = true
        return
      }
      if (routeIsAdmin || window.location.pathname === '/access/accept') return
      const routeContext = readContextRoute()
      if (contextSyncOwnerRef.current && readContextCollectionRoute() && modeRef.current === 'search') {
        setContextCollectionOpen(true); setContextLabSelectedContextId(null); setContextLabBrowseOverride(false); return
      }
      if (contextSyncOwnerRef.current && routeContext && modeRef.current === 'search') {
        setContextLabSelectedContextId(routeContext); setContextLabBrowseOverride(false); return
      }
      if (libraryEnabled() && readLinksRoute() && modeRef.current === 'search') { setLinksOpen(true); return }
      if (linksVisible && !readLinksRoute()) {
        // Back consumed the Links screen's guard entry; re-arm the root as for a Context.
        backGuardArmedRef.current = false
        setLinksOpen(false); armBackGuard(); return
      }
      if (contextLabVisible && !routeContext) {
        // Back consumed the Context's guard entry. Re-arm the root before another
        // Context replaces its URL, otherwise the ref stays armed at history index 0.
        backGuardArmedRef.current = false
        setContextCollectionOpen(false); setContextLabSelectedContextId(null); setContextLabBrowseOverride(true); armBackGuard(); return
      }
      if (!backGuardArmedRef.current) return
      backGuardArmedRef.current = false
      const contextDialog = document.querySelector<HTMLDialogElement>('dialog.mobile-sidebar-dialog[open], dialog.note-preview-dialog[open]')
        ?? document.querySelector<HTMLDialogElement>('dialog.context-canvas-sheet[open], dialog.context-add-dialog[open], dialog.context-refresh-dialog[open], dialog.note-selection-dialog[open], dialog.scenario-import-dialog[open]')
      if (contextDialog) {
        contextDialog.dispatchEvent(new Event('cancel', { cancelable: true }))
        armBackGuard()
        return
      }
      // A full-screen picture, code or the image viewer closes alone; the note behind stays open (navigation/contextBack.ts).
      if (dismissContextStep('overlays') || activeImagePreview) {
        setActiveImagePreview(null)
        armBackGuard()
        return
      }
      // A note opening or closing finishes before Back acts on it, but only briefly: one stuck on a step that never settles (a
      // frame while the app was in the background, a native share handoff) must not leave Back doing nothing.
      if (composerTransitionRef.current) {
        if (Date.now() - composerTransitionSinceRef.current < APP_SETTINGS.interactions.backTransitionWaitMs) {
          armBackGuard()
          return
        }
        recordDiagnosticMilestone('back:transition-stuck', { ms: Date.now() - composerTransitionSinceRef.current })
        composerTransitionRef.current = false
      }
      if (accountPanelOpen) {
        if (settingsSectionMenuOpen) {
          setSettingsSectionMenuOpen(false)
          armBackGuard()
          return
        }
        closeAccountPanel()
        armBackGuard()
        return
      }

      const currentMode = modeRef.current
      const currentQuery = searchQueryRef.current
      const currentFilters = searchFiltersRef.current
      const hasSearchReturn = Boolean(searchReturnRef.current)
      const hasResettableSearch =
        getSearchEscapeAction(
          currentQuery,
          hasNonDefaultSearchFilters(currentFilters),
        ) === 'clear-search'
      const backAction = getAppBackAction(currentMode, searchFiltersOpen, hasResettableSearch, browseFromNoteRef.current)
      if (backAction === 'close-filters') {
        armBackGuard()
        closeSearchFilters()
        return
      }
      if (backAction === 'stay') {
        // The phone never leaves the app from its main page by Back; a browser still can, with the next Back.
        if (getNativePlatform(window) === 'android') armBackGuard()
        else suppressBackGuardRearmRef.current = true
        return
      }
      const currentDraft = draftRef.current
      const hasActiveCapture =
        hasDraftContent(currentDraft) || notesRef.current.has(currentDraft.noteId)
      const keepGuard = shouldKeepBackGuardAfterEscape(
        currentMode,
        hasSearchReturn,
        hasResettableSearch,
        hasActiveCapture,
      )
      if (keepGuard) armBackGuard()
      else suppressBackGuardRearmRef.current = true

      if (backAction === 'close-note') {
        if (hasSearchReturn) void returnToSearchResultContext()
        else void finishDraft()
        return
      }
      if (backAction === 'clear-search') {
        clearSearch()
        return
      }
      void flushDraft().catch(() => undefined)
      startTransition(() => setMode('capture'))
    }

    window.addEventListener('popstate', handleBrowserBack)
    return () => window.removeEventListener('popstate', handleBrowserBack)
  }, [
    contextLabVisible,
    linksVisible,
    accountPanelOpen,
    activeImagePreview,
    armBackGuard,
    clearSearch,
    closeAccountPanel,
    settingsSectionMenuOpen,
    closeSearchFilters,
    finishDraft,
    flushDraft,
    returnToSearchResultContext,
    searchFiltersOpen,
  ])

  useEffect(() => {
    const handleDesktopShortcutCapture = (event: globalThis.KeyboardEvent) => {
      if (document.querySelector('dialog.context-add-dialog[open], dialog.note-selection-dialog[open], dialog.scenario-import-dialog[open]')) {
        // Native dialog owns Escape and text input; app commands must not navigate
        // or alter selection behind this form.
        if (event.key === 'Escape') event.stopPropagation()
        else if (!event.isComposing && getDesktopShortcut(event)) { event.preventDefault(); event.stopPropagation() }
        return
      }
      // The dialog can open while editor focus is retained. Handle Escape before
      // the editor's own completion handler, and don't run app commands behind help.
      if (keyboardShortcutsOpen && !event.isComposing) {
        if (event.key === 'Escape' || getDesktopShortcut(event)) {
          event.preventDefault()
          event.stopPropagation()
          if (event.key === 'Escape') setKeyboardShortcutsOpen(false)
        }
        return
      }
      if (event.isComposing || event.defaultPrevented || !isDesktopPointerRuntime()) return
      const shortcut = getDesktopShortcut(event)
      if (!shortcut) return
      if (shortcut === 'focus-search' && mode !== 'search') return
      if (shortcut === 'suppress-legacy-underline' && mode !== 'capture') return

      // Claim physical shortcuts before contenteditable/browser defaults.
      // Stopping propagation also
      // prevents the editor fallback from executing the same command twice.
      event.preventDefault()
      event.stopPropagation()

      if (shortcut === 'suppress-legacy-underline') return
      if (shortcut === 'show-shortcuts') {
        setKeyboardShortcutsOpen(true)
        return
      }
      if (shortcut === 'focus-search') {
        if (contextLabVisible || linksVisible) {
          setContextLabBrowseOverride(true); setLinksOpen(false)
          requestAnimationFrame(() => searchRef.current?.focus({ preventScroll: true }))
        } else searchRef.current?.focus({ preventScroll: true })
        return
      }
      if (shortcut === 'new-note') {
        const wasEditing = mode === 'capture'
        queueMicrotask(() => {
          void beginNewNote(
            false,
            false,
            mode === 'search' && !browseCovered ? captureSearchReturnState() : null,
          ).then((started) => {
            if (started && wasEditing) void notify.success('Note saved')
          })
        })
        return
      }
      if (mode !== 'capture') return
      if (shortcut === 'toggle-bold' || shortcut === 'toggle-italic') {
        applyEditorFormat(shortcut === 'toggle-bold' ? 'bold' : 'italic')
        return
      }
      if (shortcut === 'edit-link') {
        applyEditorFormat('link')
        return
      }
      if (shortcut === 'toggle-underline') {
        applyEditorFormat('underline')
        return
      }
      if (shortcut === 'toggle-heading') {
        applyEditorFormat('heading')
        return
      }
      if (shortcut === 'delete-line') {
        editorRef.current?.deleteCurrentLine()
        return
      }
      if (shortcut === 'delete-note') {
        if (canActOnDraft) void changeActiveStatus('trash')
        return
      }
      if (shortcut === 'copy-markdown') {
        void copyMarkdown(draft.body, draft.noteId)
        return
      }
      if (shortcut === 'toggle-quick-access') {
        if (canActOnDraft) void toggleCurrentNoteQuickAccess()
        return
      }
      if (shortcut === 'toggle-tags') {
        toggleTagEditing()
        return
      }
      if (shortcut === 'open-reminder') setReminderEditorOpen(true)
    }

    window.addEventListener('keydown', handleDesktopShortcutCapture, true)
    return () => window.removeEventListener('keydown', handleDesktopShortcutCapture, true)
  }, [applyEditorFormat, beginNewNote, canActOnDraft, captureSearchReturnState, changeActiveStatus, contextLabVisible, linksVisible, browseCovered, copyMarkdown, draft.body, draft.noteId, keyboardShortcutsOpen, mode, toggleCurrentNoteQuickAccess, toggleTagEditing])

  useEffect(() => {
    const handleGlobalCaptureShortcut = (event: globalThis.KeyboardEvent) => {
      if (invitationVisible) return
      if (document.querySelector('dialog.sign-out-dialog[open]')) return
      if (event.isComposing) return
      if (keyboardShortcutsOpen && event.key === 'Escape') {
        event.preventDefault()
        event.stopPropagation()
        leaveShortcuts()
        return
      }
      if (markdownNotesImportOpen && event.key === 'Escape') {
        event.preventDefault()
        event.stopPropagation()
        setMarkdownNotesImportOpen(false)
        return
      }
      if (event.defaultPrevented) return
      if (reminderEditorOpen && event.key === 'Escape') {
        event.preventDefault()
        closeReminderEditor()
        return
      }
      if (activeImagePreview && event.key === 'Escape') {
        event.preventDefault()
        setActiveImagePreview(null)
        clearDismissedModalFocus()
        return
      }
      if (accountPanelOpen && event.key === 'Escape') {
        event.preventDefault()
        if (settingsSectionMenuOpen) {
          setSettingsSectionMenuOpen(false)
          return
        }
        suppressAccountPanelFocusRestoreRef.current = true
        closeAccountPanel()
        clearDismissedModalFocus()
        return
      }
      if (mode === 'search' && !browseCovered && event.key === 'Escape' && isDesktopBrowseRuntime()) {
        event.preventDefault()
        searchRef.current?.focus({ preventScroll: true })
        return
      }
      if (mode === 'capture' && event.key === 'Escape') {
        event.preventDefault()
        if (searchReturnRef.current) void returnToSearchResultContext()
        else void finishDraft()
      }
    }
    window.addEventListener('keydown', handleGlobalCaptureShortcut)
    return () => window.removeEventListener('keydown', handleGlobalCaptureShortcut)
  }, [accountPanelOpen, activeImagePreview, clearDismissedModalFocus, closeAccountPanel, closeReminderEditor, browseCovered, finishDraft, invitationVisible, keyboardShortcutsOpen, markdownNotesImportOpen, mode, reminderEditorOpen, returnToSearchResultContext, settingsSectionMenuOpen])

  const handleAuthentication = async (options?: SignInOptions) => {
    if (authProvider.id === 'local') return
    setAuthBusy(true)
    setAuthError(null)
    try {
      if (getNativePlatform(window) === 'android') {
        await flushDraft()
        const session = await authProvider.signIn(options)
        if (session && acceptsWorkspaceSession(session)) setAuthSession(session)
        return
      }
      const session = await startDurableOAuthNavigation({
        persistDraft: flushDraft,
        beginNavigation: beginOAuthHistoryNavigation,
        signIn: () => authProvider.signIn(options),
        cancelNavigation: cancelOAuthHistoryNavigation,
      })
      if (session && acceptsWorkspaceSession(session)) setAuthSession(session)
    } catch (error) {
      cancelOAuthHistoryNavigation()
      setAuthError(error instanceof Error ? error.message : 'Sign-in failed.')
    } finally {
      setAuthBusy(false)
    }
  }

  const refreshNotesFromBrand = () => {
    if (!authSession || !syncCoordinator || !loaded || !isOnline) return
    setBackgroundSyncState('syncing')
    requestSyncRef.current({ push: true, domains: ['notes'], retryNow: true }, 0)
  }

  const requestPullToRefreshSync = useCallback(() => {
    if (!authSession || !syncCoordinator || !loaded || !isOnline) return
    setBackgroundSyncState('syncing')
    requestSyncRef.current({ push: true, domains: ['notes', 'settings', 'assets', 'contexts'], retryNow: true }, 0)
  }, [authSession, isOnline, loaded])

  useEffect(() => {
    const surface = appShellRef.current
    if (!surface || !androidMobileShell || mode !== 'search' || accountPanelOpen || searchFiltersOpen || browseCovered || mobileSidebarOpen) return
    let gesture: PullToRefreshGesture | null = null
    const onTouchStart = (event: TouchEvent) => {
      if (event.touches.length !== 1 || window.scrollY > 0) return
      const touch = event.touches[0]
      if (!touch) return
      gesture = { startX: touch.clientX, startY: touch.clientY, currentX: touch.clientX, currentY: touch.clientY }
    }
    const onTouchMove = (event: TouchEvent) => {
      const touch = event.touches[0]
      if (!gesture || !touch) return
      gesture.currentX = touch.clientX
      gesture.currentY = touch.clientY
    }
    const onTouchEnd = () => {
      if (gesture && window.scrollY <= 0 && isPullToRefreshGesture(gesture, APP_SETTINGS.interactions.pullToRefreshDistancePx)) {
        requestPullToRefreshSync()
      }
      gesture = null
    }
    const onTouchCancel = () => { gesture = null }
    surface.addEventListener('touchstart', onTouchStart, { passive: true })
    surface.addEventListener('touchmove', onTouchMove, { passive: true })
    surface.addEventListener('touchend', onTouchEnd, { passive: true })
    surface.addEventListener('touchcancel', onTouchCancel, { passive: true })
    return () => {
      surface.removeEventListener('touchstart', onTouchStart)
      surface.removeEventListener('touchmove', onTouchMove)
      surface.removeEventListener('touchend', onTouchEnd)
      surface.removeEventListener('touchcancel', onTouchCancel)
    }
  }, [accountPanelOpen, androidMobileShell, browseCovered, mobileSidebarOpen, mode, requestPullToRefreshSync, searchFiltersOpen])

  const [signOutOpen, setSignOutOpen] = useState(false)
  useEffect(() => {
    if (!loaded) return
    return registerWorkspaceDraftReader(() => hasDraftContent(draftRef.current) ? draftRef.current : null)
  }, [loaded])

  // Screens that cannot own the sign-in (a guest's Context) ask for it; the latest handler always answers.
  const handleAuthenticationRef = useRef(handleAuthentication)
  handleAuthenticationRef.current = handleAuthentication
  useEffect(() => onSignInRequested(() => { if (!authSession) void handleAuthenticationRef.current() }), [authSession])

  const handleSignOut = async () => {
    setSignOutOpen(true)
  }

  // Account deletion: the dialog asks the server, which erases the account at once. Afterwards this copy is removed and
  // the person is signed out; a browser that cannot coordinate removal keeps the copy hidden instead.
  const [deleteAccountOpen, setDeleteAccountOpen] = useState(false)
  // The notes download in Settings → Data & storage: working, done or failed, in words (see exportNotes).
  const [notesExport, setNotesExport] = useState<NotesExportStatus>({ phase: 'idle', title: '', detail: '' })
  // Without an account: erase what this device holds (data/guestDataRemoval.ts).
  const [deleteDeviceDataOpen, setDeleteDeviceDataOpen] = useState(false)
  const [deleteAccountResumed, setDeleteAccountResumed] = useState(false)
  useEffect(() => {
    // Back from signing in again for a deletion: reopen the dialog at its last step.
    if (authSession && takeDeletionResume()) { setDeleteAccountResumed(true); setDeleteAccountOpen(true) }
  }, [authSession?.user.id])
  const finishAccountDeletion = (removeLocalCopy: boolean) => {
    if (removeLocalCopy) {
      try { authorizeWorkspaceRemoval() } catch { /* The account is gone; the copy stays hidden if this browser cannot remove it. */ }
    }
    // The next lifecycle shows "your account has been deleted" while it finishes, then continues into the guest workspace.
    markAccountDeleted()
    markWorkspaceSignedOut()
  }


  const hasPendingSyncWork = useMemo(() => {
    const globalSettingsPending = globalSettings.localSyncState !== 'synced'
      && !(
        globalSettings.localSyncState === 'local'
        && globalSettings.modifiedAt === 0
      )
    return notes.some((note) => note.localSyncState !== 'synced')
      || globalSettingsPending
      || deviceSettings.localSyncState !== 'synced'
      || assets.some(assetRequiresSynchronization)
  }, [assets, deviceSettings.localSyncState, globalSettings, notes])

  useEffect(() => {
    if (!authSession || !syncCoordinator || !hasPendingSyncWork) {
      setPendingSyncVisible(false)
      return
    }
    return scheduleStallNotice(() => setPendingSyncVisible(true))
  }, [authSession, hasPendingSyncWork])

  const [restoreStalled, setRestoreStalled] = useState(false)
  useEffect(() => {
    setRestoreStalled(false)
    if (accountRestorePhase !== 'restoring') return
    return scheduleStallNotice(() => setRestoreStalled(true))
  }, [accountRestorePhase])

  const continuityStatus = getContinuityStatus({
    authenticated: hasRemoteAccount,
    online: isOnline,
    state: backgroundSyncState,
    syncAvailable: Boolean(syncCoordinator),
    syncError,
    restorePhase: accountRestorePhase,
    pendingWorkVisible: pendingSyncVisible,
    restoreStalled,
  })
  const composerContinuityStatus = getComposerContinuityStatus(
    continuityStatus,
    accountRestorePhase,
  )
  const initialBrowseProgress = mode === 'search' && browseStartup.headerProgress
  const headerProgressLabel = useMinimumProgressLabel(
    initialBrowseProgress ? 'Loading notes' : backgroundSyncState === 'syncing' ? 'Syncing notes' : undefined,
    APP_SETTINGS.sync.progressIndicatorMinimumMs,
  )
  const headerProgressActive = Boolean(headerProgressLabel)
  const noteRouteBlocked = Boolean(
    requestedNoteId && noteRoutePhase !== 'none' && noteRoutePhase !== 'open',
  )
  const noteRouteStatus = noteRoutePhase === 'sign-in'
    ? {
        glyph: '↗',
        title: 'Sign in to open this note',
        message: 'This private bookmark is available only to the miniMany account that owns it.',
      }
    : noteRoutePhase === 'offline'
      ? {
          glyph: '⌁',
          title: 'This note is not on this device',
          message: 'Reconnect to retrieve the server copy. Notes already stored here remain available offline.',
        }
      : noteRoutePhase === 'not-found'
        ? {
            glyph: '?',
            title: 'Note unavailable',
            message: 'The note does not exist, was permanently removed, or belongs to another account.',
          }
        : noteRoutePhase === 'error'
          ? {
              glyph: '!',
              title: 'The note could not be opened',
              message: 'Your local notes are safe. Check the connection or account and try again.',
            }
          : {
              glyph: '…',
              title: 'Opening note',
              message: 'Checking this device first, then the authenticated Supabase copy.',
            }

  const exportNotes = async (format: 'json' | 'markdown' | 'recovery') => {
    const timestamp = new Date().toISOString().slice(0, 10)
    if (format === 'recovery') {
      setRecoveryExportBusy(true)
      try {
        await commitQueueRef.current
        const currentDraft = draftRef.current
        if (shouldPersistActiveDraft(currentDraft, notesRef.current.get(currentDraft.noteId))) {
          await repository.saveActiveDraft(currentDraft)
        }
        const [source, continuity, recoverable] = await Promise.all([
          repository.readLocalRecoveryExportSource(),
          repository.inspectLocalDataContinuity(),
          readRecoverableContexts(authSession?.user.id),
        ])
        const recovery = await buildLocalRecoveryExport({ ...source, ...recoverable }, continuity, new Date(), {
          environment: supabaseConfig?.environment ?? null,
          accountFingerprint: authSession
            ? await fingerprintRecoveryAccount(authSession.user.id)
            : null,
        })
        const blob = new Blob([JSON.stringify(recovery)], { type: 'application/json' })
        downloadBlob(blob, `miniMany-local-recovery-${timestamp}.json`)
        setLocalContinuity(continuity)
        void notify.success('Local recovery copy downloaded.')
      } catch {
        void notify.error('The recovery copy could not be created. No local data was changed.')
      } finally {
        setRecoveryExportBusy(false)
      }
      return
    }
    // The notes download: always says it is working, and says how it ended (a download that fails or is blocked is never silent).
    setNotesExport({ phase: 'working', title: 'Preparing your download…', detail: 'Reading your saved data. A large library can take a few seconds.' })
    try {
      const exportableNotes = (await repository.listNotes()).filter((note) => note.status !== 'trash')
      // Contexts are part of the person's data: their names, notes and every act of theirs in them (data/contextsExport.ts).
      let contextsExport: ContextsExport | null = null
      let contextsNote = ''
      try { contextsExport = await readContextsExport(exportableNotes, authSession?.user.id) }
      catch { contextsNote = ' Some saved experimental data could not be read and is missing from this file.' }
      const contextsMarkdown = contextsExport ? contextsAsMarkdown(contextsExport) : ''
      const contents =
        format === 'json'
          ? JSON.stringify({ exportedAt: new Date().toISOString(), notes: exportableNotes, ...(contextsExport ? { contexts: contextsExport } : {}) }, null, 2)
          : [...exportableNotes.map(portableMarkdownNote), ...(contextsMarkdown ? [contextsMarkdown] : [])]
              .join('\n---\n\n')
      const blob = new Blob([contents], { type: format === 'json' ? 'application/json' : 'text/markdown' })
      const filename = `miniMany-export-${timestamp}.${format === 'json' ? 'json' : 'md'}`
      downloadBlob(blob, filename)
      const contextCount = contextsExport?.contexts.length ?? 0
      const summary = `${exportableNotes.length} ${exportableNotes.length === 1 ? 'note' : 'notes'}${contextsEnabled ? ` and ${contextCount} ${contextCount === 1 ? 'Context' : 'Contexts'}` : ''}, ${readableSize(blob.size)}.`
      setNotesExport({ phase: contextsNote ? 'failed' : 'done', title: contextsNote ? `Downloaded ${filename}, with some data missing` : `Downloaded ${filename}`, detail: `${summary} Look in your Downloads folder.${contextsNote}` })
    } catch {
      setNotesExport({ phase: 'failed', title: 'The download could not be created', detail: 'Nothing was changed in your notes. Please try again.' })
    }
  }

  const importLocalRecovery = async (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]
    event.target.value = ''
    if (!file) return
    if (!authSession || !supabaseConfig) {
      void notify.error('Sign in with the same miniMany account before importing a recovery copy.')
      return
    }
    if (accountRestorePhase !== 'ready') {
      void notify.error('Wait for account restore to finish before importing local-only data.')
      return
    }
    setRecoveryImportBusy(true)
    try {
      const currentDraft = draftRef.current
      if (shouldPersistActiveDraft(currentDraft, notesRef.current.get(currentDraft.noteId))) {
        await repository.saveActiveDraft(currentDraft)
      }
      const contents = await file.text()
      if (JSON.parse(contents)?.format === 'minimany-workspace-recovery') {
        const { importWorkspaceBackup } = await import('./data/workspaceBackup')
        await importWorkspaceBackup(contents, file.size)
        window.location.reload()
        return
      }
      const recovery = parseLocalRecoveryExport(contents, file.size)
      await verifyRecoveryBinding(
        recovery,
        supabaseConfig.environment,
        authSession.user.id,
      )
      const plan = buildLocalRecoveryImportPlan(recovery)
      const result = await repository.importLocalRecovery(plan)
      // Contexts and the person's acts in them come back additively; one that already exists is left as it is.
      const recoveredContexts = await restoreRecoveredContexts(recovery, authSession.user.id)
      const [restoredNotes, restoredAssets, restoredDraft, restoredGlobalSettings, continuity] =
        await Promise.all([
          repository.listNotes(),
          repository.listAssets(),
          repository.getActiveDraft(),
          repository.getGlobalSettings(),
          repository.inspectLocalDataContinuity(),
        ])
      for (const note of restoredNotes) searchIndex.upsert(note)
      setNotes(restoredNotes)
      setAssets(restoredAssets)
      if (restoredDraft) {
        draftRef.current = restoredDraft
        setDraft(restoredDraft)
      }
      if (restoredGlobalSettings) setGlobalSettings(restoredGlobalSettings)
      setLocalContinuity(continuity)
      setAccountRestorePhase('restoring')
      accountRestoreReadyRef.current = false
      restoreDomainsVerifiedRef.current.clear()
      requestSyncRef.current({ push: true, domains: ['notes', 'settings', 'assets', 'contexts'] }, 0)
      void notify.success(
        `Recovered ${result.importedNotes} note(s), ${result.importedAssets} image(s), ${recoveredContexts.contexts} saved collection(s), and ${result.importedDraft ? 'one draft' : 'no new draft'}.`,
      )
    } catch (error) {
      void notify.error(error instanceof Error ? error.message : 'The recovery copy could not be imported.')
    } finally {
      setRecoveryImportBusy(false)
    }
  }

  const imageStageNode = (placement: 'stage' | 'aside') => (
    <NoteImageStage
      placement={placement}
      look={imageStage.look}
      onLook={imageStage.setLook}
      assets={activeAssets}
      expectedCount={draft.assetIds?.length ?? 0}
      remoteUrls={remoteAssetUrls}
      highlightedAssetId={revealedImageId}
      highlightAnimationMs={APP_SETTINGS.interactions.imagePasteRevealAnimationMs}
      onRemove={removeImage}
      onPreviewOpen={setActiveImagePreview}
      onPreviewVisible={recordImagePreviewVisible}
      loadFullImage={loadFullImage}
      reading={unpackReading === draft.noteId}
    />
  )

  const renderSearchResult = (result: NoteSearchResult) => {
    const note = notesById.get(result.id)
    if (!note) return null
    return (
      <SearchResultCard
        key={result.id}
        id={`search-result-${result.id}`}
        active={result.id === activeSearchResult?.id}
        copied={copiedNoteId === note.id}
        note={note}
        result={result}
        layout={searchLayout}
        presentation={DEFAULT_BROWSE_PRESENTATION.layouts[searchLayout]}
        linkMetadataStatus={(url) => linkMetadataStatusForUrl(note.id, url)}
        assets={assetsByNote.get(note.id)?.filter((asset) => note.assetIds?.includes(asset.id))}
        cover={cleanupWorkspace && unpackPreferences.value.cover && cleanupViews.get(note.id) !== 'original' ? cleanupCovers.get(note.id) : undefined}
        unpacked={cleanupWorkspace && cleanupViews.get(note.id) !== 'original' ? cleanupCards.get(note.id) : undefined}
        loadFullImage={loadFullImage}
        expectedCount={note.assetIds?.length ?? 0}
        remoteAssetUrls={remoteAssetUrls}
        onCopy={copyNote}
        onBump={(selected) => void runNoteCardTransition(() => bumpNoteToTop(selected))}
        onOpen={(selected, matchTerm) => {
          void openNote(selected, matchTerm, captureSearchReturnState())
        }}
        onStatusChange={(selected, status) => {
          if (status !== 'trash') {
            void runNoteCardTransition(() => changeNoteStatus(selected, status))
            return
          }
          void animateSearchNoteRemoval([selected], () => changeNoteStatus(selected, status))
            .catch(() => notify.error('Could not move the note to trash'))
        }}
        selected={selectedSearchNoteIds.has(note.id)}
        selectionMode={searchSelectionMode}
        onSelectionStart={startSearchSelection}
        onSelectionToggle={toggleSearchSelection}
      />
    )
  }

  const studioBrowseTitle = effectiveSearchFilters.quickAccess ? 'Quick Access'
    : effectiveSearchFilters.hasAlarm ? 'Reminders'
    : effectiveSearchFilters.status === 'archived' ? 'Archive'
    : effectiveSearchFilters.status === 'trash' ? 'Trash' : 'Here’s everything'

  function changeStudio(preference: StudioPreference) {
    if (!studio.change(preference)) void notify.error('Appearance changed for this session; device preference storage is unavailable.')
  }

  async function navigateStudio(destination: StudioDestination | 'tag' | 'context' | 'contexts' | 'search', value?: string, focusBrowseSearch = studioActive) {
    if ((destination === 'contexts' || destination === 'context') && !contextSyncOwnerRef.current) return
    if (studioNavigationBusy || composerTransitionRef.current) return
    composerTransitionRef.current = true; composerTransitionSinceRef.current = Date.now()
    setStudioNavigationBusy(true)
    try {
      // A sidebar action must never leave a note after a failed local save.
      if (mode === 'capture') {
        await flushDraft()
        await repository.clearActiveDraft()
        clearActiveNoteRoute()
        closeOnDemandTools()
        searchReturnRef.current = null
        setMode('search')
      }
      if (accountPanelOpen) suppressAccountPanelFocusRestoreRef.current = true
      closeAccountPanel()
      if ((destination === 'contexts' || destination === 'context' || destination === 'links') && !browseCovered && mode === 'search') pendingBrowseViewportRef.current = captureBrowseViewport()
      setContextLabBrowseOverride(destination !== 'context' && destination !== 'contexts')
      setContextCollectionOpen(destination === 'contexts')
      setLinksOpen(destination === 'links' && libraryEnabled())
      setContextLabSelectedContextId(destination === 'context' ? value ?? null : null)
      if (destination === 'contexts') { replaceContextCollectionRoute(); window.scrollTo(0, 0); return }
      if (destination === 'links') { replaceLinksRoute(); window.scrollTo(0, 0); return }
      if (destination === 'context') return
      if (destination === 'search') { requestAnimationFrame(() => searchRef.current?.focus({ preventScroll: true })); return }
      if (destination === 'tag') {
        toggleSearchTagFilter(value!)
        if (focusBrowseSearch) requestAnimationFrame(() => searchRef.current?.focus({ preventScroll: true }))
        return
      }
      if (destination === 'all') { setSearchQuery(''); setSearchFilters({ status: 'all' }) }
      else applySearchFilter(current => studioShortcutFilters(current, destination))
      setSearchNavigationIndex(null)
      window.scrollTo({ top: 0 })
      // Studio's sidebar stays mounted, so Browse entry must explicitly hand
      // keyboard ownership back to the search combobox after the view commits.
      if (focusBrowseSearch) requestAnimationFrame(() => searchRef.current?.focus({ preventScroll: true }))
    } catch { void notify.error('The note could not be saved. You are still in the editor.') }
    finally { composerTransitionRef.current = false; setStudioNavigationBusy(false) }
  }

  // Toasts outlive the render that made them, so their actions reach the current navigation through a ref.
  const navigateStudioRef = useRef(navigateStudio)
  navigateStudioRef.current = navigateStudio
  /** "Added to context", with Undo, and a way to the Context itself when it is a single one. */
  const notifyContextAdded = (contextId: string | null, noteId: string, count: number, undo: () => Promise<void>, afterUndo?: () => void, title?: string) => {
    void notify(count === 1 ? (title ? `Added to ${title}` : 'Added to context') : `Added to ${count} contexts`, {
      cancel: { label: 'Undo', onClick: () => {
        void undo().then(() => { afterUndo?.(); void notify.success('Context addition undone') })
          .catch(() => notify.error('Could not undo this context addition'))
      } },
      ...(contextId ? { action: { label: 'Go to context', onClick: () => { setContextFocus({ contextId, noteId }); void navigateStudioRef.current('context', contextId, false) } } } : {}),
    })
  }

  const activeReminderElapsed = activeNote?.reminder
    ? isReminderElapsed(activeNote.reminder)
    : false

  const openActiveContextPicker = () => {
    const owner = contextSyncOwnerRef.current
    if (!owner) return
    void flushDraft().then(note => {
      if (contextSyncOwnerRef.current !== owner) return
      if (!note) { void notify('Add some content before choosing a context.'); return }
      setAddToContextNotes({ scope: authSession?.user.id ?? 'local-review', notes: [note], returnToNote: true })
    }).catch(() => notify.error('The note could not be saved. It was not added to a context.'))
  }

  const attachSuggestedContext = async () => {
    const suggestion = smartSuggestion, owner = authSession?.user.id
    if (!suggestion || !owner || smartSuggestionBusy || !smartSuggestionsEnabled) return
    suggestion.flow.event('owner_choice', 'started', 'attach')
    const target = suggestion.records.find(record => record.id === suggestion.ids[0])
    if (!target) { suggestion.flow.event('owner_choice', 'failed', 'target_missing'); setSmartSuggestion(null); return }
    setSmartSuggestionBusy(true)
    try {
      const saved = await addNotesToContext({ ownerId: owner, contextId: target.id, noteIds: [suggestion.note.id], existingTitle: target.title,
        existingSourceIds: target.links.map(link => link.noteId) })
      suggestion.flow.event('owner_choice', 'ok', 'attached')
      setSmartSuggestion(null)
      notifyContextAdded(target.id, suggestion.note.id, 1, () => undoContextAddition(saved.receipt))
    } catch (error) { suggestion.flow.event('owner_choice', 'failed', error instanceof PlanLimitError ? 'plan_limit' : 'attach_failed'); void notify.error(error instanceof PlanLimitError ? error.message : 'Could not attach the note. It remains in your library.') }
    finally { setSmartSuggestionBusy(false) }
  }

  // The foot of the note's ⋯ menu: when it was last edited, in words ("Edited 12m ago", "Edited just now").
  const editedLabel = (timestamp: number) => {
    const ago = formatRelativeTime(timestamp)
    return ago === 'now' ? 'Edited just now' : /\d[smhd]$/.test(ago) ? `Edited ${ago} ago` : `Edited ${ago}`
  }
  // Shown only when the note has tags (or Contexts), or while they are being edited; adding one starts from the ⋯ menu.
  const noteDetailsShown = Boolean(activeNote) && (draft.userTags.length > 0 || tagToolbarOpen || contextsEnabled)
  const noteDetails = noteDetailsShown ? (
    <div
      className="composer__note-details"
      aria-hidden={!activeNote}
    >
      {activeNote ? (
        <>
          <div className="composer__tag-rail">
            {/* One entry point for the note's Contexts, first; tags are quieter: their chips, then an icon to add one. */}
            {contextsEnabled && <Suspense fallback={null}><NoteContextLinks
              owner={authSession?.user.id ?? 'local-review'} noteId={draft.noteId}
              onAdd={openActiveContextPicker} addDisabled={!canActOnDraft}
              onOpenContext={contextId => { void navigateStudio('context', contextId, false) }}
              onRemoved={contextId => { const owner = authSession?.user.id; if (owner) void import('./intelligence/contextSync/client').then(({ syncContexts }) => syncContexts(owner, contextId)).catch(() => undefined) }}
            /></Suspense>}
            {studioActive ? <>
              {draft.userTags.map(tag => (
                <button type="button" className="btn btn-ghost studio-note-tag" key={tag}
                  title={tag} aria-label={`Edit tags: ${tag}`} onClick={toggleTagEditing}>
                  <span><TagLabel tag={tag} /></span>
                </button>
              ))}
            </> : draft.userTags.length > 0 ? (
            <button
              type="button"
              className="btn btn-ghost composer__tag-summary"
              aria-label={`Edit tags: ${draft.userTags.join(', ')}`}
              onClick={toggleTagEditing}
            >
              <span>{`# ${draft.userTags.slice(0, 3).join(', ')}${draft.userTags.length > 3 ? ` (+${draft.userTags.length - 3})` : ''}`}</span>
            </button>
            ) : null}
          </div>
          {tagToolbarOpen ? (
            <TagAutocomplete
              inputRef={tagInputRef}
              value={tagInput}
              tags={suggestedTags}
              selectedTags={draft.userTags}
              onValueChange={setTagInput}
              onAddTag={addCustomTag}
              onRemoveTag={toggleTag}
              onClose={() => closeTagging()}
            />
          ) : null}
        </>
      ) : null}
    </div>
  ) : null

  const settingsSectionMenuItems: SettingsSectionMenuItem[] = matchingSettingsCategories('', contextsEnabled, devToolsAllowed)
    .map(category => ({ id: category.id, label: category.title, current: studioActive && studioSettingsSection === category.id }))

  async function openInvitationContexts() {
    const owner = authSessionRef.current?.user.id
    if (!owner || contextSyncOwnerRef.current !== owner) throw Error('This preview is unavailable.')
    // Pull the account's existing Contexts first; entering again never overwrites them.
    await syncAuthorizedContexts(owner, () => contextSyncOwnerRef.current)
    if (contextSyncOwnerRef.current !== owner) throw Error('This preview is unavailable.')
    const savedNotes = await repository.listNotes()
    if (contextSyncOwnerRef.current !== owner) throw Error('This preview is unavailable.')
    setNotes(savedNotes); searchIndex.load(savedNotes)
    setContextStripVisible(true)
    requestSyncRef.current({ push: true, domains: ['notes', 'contexts'] }, 0)
    window.history.replaceState(null, '', '/')
    setInvitationRouteActive(false)
    await navigateStudio('contexts', undefined, false)
  }

  return (
    <>
    {deleteDeviceDataOpen && activeWorkspace() && <Suspense fallback={null}><DeleteDeviceDataDialog scope={activeWorkspace()!.scope} onCancel={() => setDeleteDeviceDataOpen(false)}
      onOpenExport={() => { setDeleteDeviceDataOpen(false); setAccountPanelOpen(true); scrollToSettingsSection('export-import-section', true) }} /></Suspense>}
    {deleteAccountOpen && <Suspense fallback={null}><DeleteAccountDialog email={authSession?.user.email ?? null} resumed={deleteAccountResumed}
      onCancel={() => { setDeleteAccountOpen(false); setDeleteAccountResumed(false) }}
      onReauthenticate={() => handleAuthentication({ reauthenticate: true })}
      onOpenExport={() => { setDeleteAccountOpen(false); setDeleteAccountResumed(false); setAccountPanelOpen(true); scrollToSettingsSection('export-import-section', true) }}
      onDeleted={finishAccountDeletion} /></Suspense>}
    {signOutOpen && <Suspense fallback={null}><SignOutDialog email={authSession?.user.email ?? null} repository={repository} flushDraft={flushDraft} onCancel={() => setSignOutOpen(false)} onSync={async () => { await flushDraft(); await contextsAccount.synchronizeForSignOut({ runSharedSync: runSync }) }} /></Suspense>}
    {invitationRouteActive && !contextsLive && <main className="invitation-welcome"><h1>This preview is unavailable</h1><a href="/">Open miniMany</a></main>}
    {invitationRouteActive && contextsLive && <Suspense fallback={<main className="invitation-welcome" role="status">Opening your invitation…</main>}><FeatureInvitation token={invitationToken} email={authSession?.user.email || null} restoring={authRestoring || Boolean(authSession && accountRestorePhase !== 'ready')} busy={authBusy} authError={authError || undefined} onSignIn={() => void handleAuthentication()} onSignOut={() => void handleSignOut()} onAccepted={async () => {
      // A retained invitation cannot enable the experiment or grant developer access.
      await openInvitationContexts()
    }} /></Suspense>}
    {adminRouteActive ? (
      developerAuthorized ? (
        <Suspense fallback={<main className="admin-page" role="status">Loading developer view…</main>}>
          {comparisonRouteActive && supabaseConfig?.isDev && authSession?.user.id ? <SearchComparison key={authSession.user.id} owner={authSession.user.id}
            notes={notes} searchIndex={searchIndex} onOpenNote={note => { setAdminRouteActive(false); setComparisonRouteActive(false); void openNote(note) }} />
            : <AdminWorkspace key={authSession?.user.id} enabled={deviceSettings.performanceDiagnosticsEnabled} onEnabledChange={changePerformanceDiagnostics}
              cleanup={{ notes, assetsByNote, remoteUrls: remoteAssetUrls, workspace: activeWorkspace()?.databaseName ?? null, owner: authSession?.user.id ?? null, loadFullImage }} />}
        </Suspense>
      ) : <main className="admin-page"><div className="back-bar"><BackButton href="/" label="miniMany" /><h1>miniMany admin</h1></div>
        <p role="status">{authRestoring || developerAccess === 'checking' ? 'Checking admin access…' : authSession ? 'This account does not have admin access.' : 'Sign in with your administrator account.'}</p>
        {!authSession && !authRestoring && <button className="btn" disabled={authBusy} onClick={() => void handleAuthentication()}>Sign in with Google</button>}
        {authError && <p role="alert">{authError}</p>}
      </main>
    ) : null}
    {appUpdatePromptOpen ? createPortal(
      <div className="settings-layer app-update-layer visual-viewport-frame">
        <div className="settings-layer__backdrop" aria-hidden="true" />
        <section
          className="account-panel app-update-dialog"
          aria-label="App update"
          aria-modal="true"
          role="dialog"
        >
          <span className="eyebrow">New version</span>
          <h2>{appUpdate.phase === 'error' ? 'Update needs another try' : 'Update miniMany'}</h2>
          <p role="status" aria-live="polite">
            {appUpdate.phase === 'applying'
              ? 'Saving this device and loading the latest app code…'
              : appUpdate.phase === 'error'
                ? 'The update could not finish. Check the connection, then try again.'
                : 'A newer version is available. Updating refreshes app code while keeping local notes and images.'}
          </p>
          <div className="app-update-dialog__actions">
            {appUpdate.phase !== 'applying' ? (
              <button
                ref={appUpdateButtonRef}
                className="primary-action"
                type="button"
                onClick={() => void applyLatestApp()}
              >
                {appUpdate.phase === 'error' ? 'Try update again' : 'Update now'}
              </button>
            ) : null}
            {appUpdate.phase !== 'applying' ? (
              <button
                className="btn btn-secondary"
                type="button"
                onClick={remindAboutAppUpdateLater}
              >
                Remind me later
              </button>
            ) : null}
          </div>
          <span className="app-update-dialog__safety">
            Reminders pause for {APP_UPDATE_REMINDER_HOURS} hours. Offline data is not cleared.
          </span>
        </section>
      </div>,
      document.body,
    ) : null}
    <div
      ref={appShellRef}
      className="app-shell"
      hidden={adminRouteActive || invitationVisible}
      inert={(!studioActive && (mode === 'capture' || accountPanelOpen)) || appUpdatePromptOpen}
      data-studio-surface={accountPanelOpen ? 'settings' : mode === 'capture' ? 'editor' : 'browse'}
    >
      {studioActive ? <DesktopSidebar
        filters={effectiveSearchFilters}
        editing={mode === 'capture'}
        settingsOpen={accountPanelOpen}
        contextOpen={contextLabVisible}
        linksOpen={linksVisible}
        linksEnabled={libraryEnabled()}
        busy={studioNavigationBusy || composerClosing}
        name={authSession?.user.name ?? 'Local workspace'}
        status={continuityStatus?.label}
        saveStatus={mode === 'capture' ? saveState === 'error' ? 'Save needs attention' : !hasDraftContent(draft) ? 'Ready to write' : activeNote && draftMatchesNote(draft, activeNote) ? 'Saved locally' : 'Saving…' : undefined}
        progressLabel={headerProgressLabel}
        activity={<><GoogleKeepGlobalStatus controller={googleKeepImports} onOpen={openGoogleKeepImport} />{headerProgressActive ? <span role="status">{headerProgressLabel}…</span> : null}</>}
        onNewNote={() => { if (accountPanelOpen) { suppressAccountPanelFocusRestoreRef.current = true; closeAccountPanel() }; void beginNewNote(mode === 'capture', false, mode === 'search' && !browseCovered ? captureSearchReturnState() : null) }}
        tags={filterTags}
        settingsRef={accountButtonRef}
        onRefresh={refreshNotesFromBrand}
        onSettings={accountPanelOpen ? closeAccountPanel : openAccountPanel}
        onNavigate={(destination) => { void navigateStudio(destination) }}
        onTag={(tag) => { void navigateStudio('tag', tag) }}
        contexts={contextsEnabled ? <Suspense fallback={null}><ContextStrip sidebar title="Contexts" selectedId={contextLabVisible ? contextLabSelectedContextId : null}
          key={`${authSession?.user.id ?? 'local-review'}:${contextLabVisible}`} storageScope={authSession?.user.id ?? 'local-review'}
          onOpenContext={(contextId) => { void navigateStudio('context', contextId) }}
          onOpenCollection={() => { void navigateStudio('contexts') }}
        /></Suspense> : null}
      /> : null}
      {!browseCovered ? <header className={`app-header${headerProgressActive ? ' app-header--syncing' : ''}`}>
        <StudioBrand
          buttonRef={workspaceNavigationDrawer ? mobileSidebarTriggerRef : undefined}
          onRefresh={workspaceNavigationDrawer ? () => setMobileSidebarOpen(true) : refreshNotesFromBrand}
          actionLabel={workspaceNavigationDrawer ? 'Open navigation' : undefined}
          progressLabel={headerProgressLabel}
        />
        <div className="header-actions">
          <GoogleKeepGlobalStatus controller={googleKeepImports} onOpen={openGoogleKeepImport} />
          {continuityStatus ? (
            <span
              className={`continuity-state continuity-state--${continuityStatus.tone}`}
              role="status"
              aria-live="polite"
            >
              <Icon name={continuityStatus.icon} />
              {continuityStatus.label}
            </span>
          ) : null}
          <button
            ref={studioActive ? undefined : accountButtonRef}
            className="account-button"
            type="button"
            onPointerDown={() => {
              accountPanelScrollRef.current = window.scrollY
            }}
            onClick={openAccountPanel}
            aria-expanded={accountPanelOpen}
            disabled={authBusy || authRestoring}
            title="Settings, account, and export"
          >
            <AccountAvatar userId={authSession?.user.id} name={authSession?.user.name} src={authSession?.user.avatarUrl}/>
            <span className="account-button__label">
              {authSession?.user.name ?? (
                authProvider.id === 'local'
                  ? 'Local'
                  : authRestoring
                    ? 'Restoring…'
                    : authBusy
                      ? 'Connecting'
                      : 'Sign in'
              )}
            </span>
          </button>
        </div>
      </header> : null}
      {workspaceNavigationDrawer ? <MobileSidebarDialog
        open={mobileSidebarOpen}
        onClose={() => {
          setMobileSidebarOpen(false)
          requestAnimationFrame(() => mobileSidebarTriggerRef.current?.focus({ preventScroll: true }))
        }}
        filters={effectiveSearchFilters}
        editing={false}
        settingsOpen={accountPanelOpen}
        contextOpen={contextLabVisible}
        linksOpen={linksVisible}
        linksEnabled={libraryEnabled()}
        busy={studioNavigationBusy || composerClosing}
        name={authSession?.user.name ?? 'Local workspace'}
        status={continuityStatus?.label}
        progressLabel={headerProgressLabel}
        activity={<><GoogleKeepGlobalStatus controller={googleKeepImports} onOpen={openGoogleKeepImport} />{headerProgressActive ? <span role="status">{headerProgressLabel}…</span> : null}</>}
        onNewNote={() => {
          setMobileSidebarOpen(false)
          void beginNewNote(false, false, mode === 'search' && !browseCovered ? captureSearchReturnState() : null)
        }}
        tags={filterTags}
        settingsRef={null}
        onRefresh={refreshNotesFromBrand}
        onSettings={() => { setMobileSidebarOpen(false); openAccountPanel() }}
        onNavigate={(destination) => { setMobileSidebarOpen(false); void navigateStudio(destination, undefined, false) }}
        onTag={(tag) => { setMobileSidebarOpen(false); void navigateStudio('tag', tag, false) }}
        contexts={contextsEnabled ? <Suspense fallback={null}><ContextStrip sidebar title="Contexts" selectedId={contextLabVisible ? contextLabSelectedContextId : null}
          key={`${authSession?.user.id ?? 'local-review'}:mobile:${contextLabVisible}`} storageScope={authSession?.user.id ?? 'local-review'}
          onOpenContext={(contextId) => { setMobileSidebarOpen(false); void navigateStudio('context', contextId, false) }}
          onOpenCollection={() => { setMobileSidebarOpen(false); void navigateStudio('contexts', undefined, false) }}
        /></Suspense> : null}
      /> : null}

      <main className={
        mode === 'capture' && !retainBrowseSurface
          ? 'main-content main-content--capture main-content--editing'
          : 'main-content'
      }>
        {authError ? <div className="inline-alert" role="status">{authError}</div> : null}
        {contextLabOpen ? <div hidden={!contextLabVisible}><Suspense fallback={<p role="status">Opening context…</p>}>
          <ContextLab studio={studioActive} weekStartsOn={globalSettings.weekStartsOn}
            key={`${authSession?.user.id ?? 'local-review'}:${contextLabSelectedContextId}`}
            storageScope={authSession?.user.id ?? 'local-review'}
            initialContextId={contextLabSelectedContextId}
            focusNoteId={contextFocus && contextFocus.contextId === contextLabSelectedContextId ? contextFocus.noteId : null}
            onCanonicalContext={(id) => {
              if (contextCollectionOpen && !contextLabSelectedContextId) window.history.pushState(window.history.state, '', contextPath(id))
              else replaceContextRoute(id)
              setContextLabSelectedContextId(id)
            }}
            onCollection={contextCollectionOpen && contextLabSelectedContextId ? () => window.history.back() : undefined}
            notes={notes}
            active={contextLabVisible && !accountPanelOpen && !adminRouteActive}
            onBrowse={() => {
              setContextCollectionOpen(false)
              setContextLabBrowseOverride(true)
              setContextLabSelectedContextId(null)
            }}
            onOpenNote={(id) => {
              const note = notesRef.current.get(id)
              if (note) { searchReturnRef.current = null; void openNote(note) }
            }}
            onCapture={async () => {
              const opened = await beginNewNote()
              return opened ? draftRef.current.noteId : null
            }}
            onDeleteContext={async (context, expectedRevision, trashExclusiveNotes, visibleContexts) => {
              // A guest's Contexts live on this device under the guest scope: deleting one needs no account, and there is nothing to sync.
              const owner = authSession?.user.id ?? 'local-review'
              const trashed = await deleteManualContext(owner, context.id, expectedRevision, trashExclusiveNotes, deviceId, context, visibleContexts)
              for (const note of trashed) searchIndex.upsert(note)
              if (trashed.length) setNotes(current => trashed.reduce(updateNoteCollection, current))
              if (authSession) void import('./intelligence/contextSync/client').then(({ syncContexts }) => syncContexts(owner, context.id)).catch(() => {
                void notify('Context deleted on this device · sync pending')
              })
              await navigateStudio('contexts', undefined, false)
              void notify.success(trashed.length ? `Context deleted · ${trashed.length} ${trashed.length === 1 ? 'note' : 'notes'} moved to Trash` : 'Context deleted')
            }}
          />
        </Suspense></div> : null}
        {libraryEnabled() && linksOpen ? <Suspense fallback={<p role="status">Opening links…</p>}>
          <LinksLibrary
            owner={authSession?.user.id ?? 'local-review'}
            notes={notes}
            loadAllNotes={loadAllNotes}
            unpacked={cleanupCards}
            active={linksVisible && !accountPanelOpen && !adminRouteActive}
            gistFor={gistUiFor}
            answersFor={gistsEnabled() ? (url) => ({
              summaryStatus: gistController.statusOf(url),
              summaryDetail: gistController.detailOf(url),
              onSummarise: () => void gistController.request(url, 'manual', { refresh: true }),
              ask: {
                status: gistController.askStatusOf(url),
                statusDetail: gistController.askDetailOf(url),
                readability: gistController.readabilityOf(url),
                check: () => void gistController.check(url),
                ask: (question) => gistController.ask(url, question),
                clearStatus: () => gistController.clearAskStatus(url),
                remove: (id) => void gistController.removeAnswer(url, id),
              },
            }) : undefined}
            onOpenNote={(id) => {
              void (async () => {
                const note = notesRef.current.get(id) ?? await repository.getNote(id)
                if (note) { searchReturnRef.current = null; void openNote(note) }
              })()
            }}
            onChangeLinks={saveLinkAttributes}
            onBack={() => { setLinksOpen(false); clearNoteRoute() }}
          />
        </Suspense> : null}
        {keyboardShortcutsOpen ? createPortal(
          <dialog
            ref={shortcutsDialogRef}
            className="modal modal-bottom sm:modal-middle studio-sheet keyboard-shortcuts-modal"
            open
            aria-labelledby="keyboard-shortcuts-title"
            onCancel={(event) => {
              event.preventDefault()
              leaveShortcuts()
            }}
            onClick={(event) => {
              if (event.target === event.currentTarget) {
                leaveShortcuts()
              }
            }}
          >
            <section className="modal-box sheet-panel" aria-describedby="keyboard-shortcuts-description">
              <header className="sheet-header">
                <h2 id="keyboard-shortcuts-title" className="sheet-title"><Icon name="keyboard" />Keyboard shortcuts</h2>
                <button
                  className="btn btn-ghost btn-square sheet-close"
                  type="button"
                  onClick={leaveShortcuts}
                  aria-label="Close keyboard shortcuts"
                ><Icon name="close" /></button>
              </header>
              <div className="sheet-body">
                <p id="keyboard-shortcuts-description" className="sheet-note">Available with a desktop keyboard.</p>
                <section aria-labelledby="shortcut-general-title">
                  <h3 id="shortcut-general-title" className="sheet-heading mb-3">General</h3>
                  <dl className="grid grid-cols-[1fr_auto] items-center gap-x-5 gap-y-3">
                    <dt>New note</dt><dd><kbd className="kbd kbd-sm">Ctrl</kbd> + <kbd className="kbd kbd-sm">Shift</kbd> + <kbd className="kbd kbd-sm">N</kbd></dd>
                    <dt>Focus search <span className="opacity-60">(Browse)</span></dt><dd><kbd className="kbd kbd-sm">Ctrl</kbd> + <kbd className="kbd kbd-sm">Shift</kbd> + <kbd className="kbd kbd-sm">S</kbd></dd>
                    <dt className="flex items-center gap-2"><Icon className="h-4 w-4 text-primary" name="keyboard" />Show shortcuts</dt><dd><kbd className="kbd kbd-sm">Ctrl/⌘</kbd> + <kbd className="kbd kbd-sm">/</kbd></dd>
                  </dl>
                </section>
                <section aria-labelledby="shortcut-formatting-title">
                  <h3 id="shortcut-formatting-title" className="sheet-heading mb-3">Note formatting</h3>
                  <dl className="grid grid-cols-[1fr_auto] items-center gap-x-5 gap-y-3">
                    <dt>Cycle Regular → H1 → H2</dt><dd><kbd className="kbd kbd-sm">Ctrl</kbd> + <kbd className="kbd kbd-sm">Shift</kbd> + <kbd className="kbd kbd-sm">H</kbd></dd>
                    <dt>Bold</dt><dd><kbd className="kbd kbd-sm">Ctrl/⌘</kbd> + <kbd className="kbd kbd-sm">B</kbd><div className="mt-1"><kbd className="kbd kbd-sm">Ctrl</kbd> + <kbd className="kbd kbd-sm">Shift</kbd> + <kbd className="kbd kbd-sm">B</kbd></div></dd>
                    <dt>Italic</dt><dd><kbd className="kbd kbd-sm">Ctrl/⌘</kbd> + <kbd className="kbd kbd-sm">I</kbd><div className="mt-1"><kbd className="kbd kbd-sm">Ctrl</kbd> + <kbd className="kbd kbd-sm">Shift</kbd> + <kbd className="kbd kbd-sm">I</kbd></div></dd>
                    <dt>Underline</dt><dd><kbd className="kbd kbd-sm">Ctrl</kbd> + <kbd className="kbd kbd-sm">Shift</kbd> + <kbd className="kbd kbd-sm">U</kbd></dd>
                    <dt>Add or edit link</dt><dd><kbd className="kbd kbd-sm">Ctrl</kbd> + <kbd className="kbd kbd-sm">Shift</kbd> + <kbd className="kbd kbd-sm">L</kbd></dd>
                    <dt>Soft line break <span className="opacity-60">(same block)</span></dt><dd><kbd className="kbd kbd-sm">Shift</kbd> + <kbd className="kbd kbd-sm">Enter</kbd></dd>
                  </dl>
                </section>
                <section aria-labelledby="shortcut-note-actions-title">
                  <h3 id="shortcut-note-actions-title" className="sheet-heading mb-3">Note actions</h3>
                  <dl className="grid grid-cols-[1fr_auto] items-center gap-x-5 gap-y-3">
                    <dt className="flex items-center gap-2"><Icon className="h-4 w-4 text-error" name="remove-line" />Delete line</dt><dd><kbd className="kbd kbd-sm">Alt</kbd> + <kbd className="kbd kbd-sm">.</kbd></dd>
                    <dt className="flex items-center gap-2"><Icon className="h-4 w-4 text-error" name="trash" />Delete note</dt><dd><kbd className="kbd kbd-sm">Ctrl</kbd> + <kbd className="kbd kbd-sm">Shift</kbd> + <kbd className="kbd kbd-sm">D</kbd></dd>
                    <dt className="flex items-center gap-2"><Icon className="h-4 w-4 text-primary" name="copy" />Copy as Markdown</dt><dd><kbd className="kbd kbd-sm">Ctrl</kbd> + <kbd className="kbd kbd-sm">Shift</kbd> + <kbd className="kbd kbd-sm">C</kbd></dd>
                    <dt className="flex items-center gap-2"><Icon className="h-4 w-4 text-warning" name="flash" />Toggle Quick Access</dt><dd><kbd className="kbd kbd-sm">Ctrl</kbd> + <kbd className="kbd kbd-sm">Shift</kbd> + <kbd className="kbd kbd-sm">Q</kbd></dd>
                    <dt>Toggle tag editing</dt><dd><kbd className="kbd kbd-sm">Ctrl</kbd> + <kbd className="kbd kbd-sm">Shift</kbd> + <kbd className="kbd kbd-sm">T</kbd></dd>
                    <dt className="flex items-center gap-2"><Icon className="h-4 w-4 text-primary" name="bell" />Set or edit reminder</dt><dd><kbd className="kbd kbd-sm">Ctrl</kbd> + <kbd className="kbd kbd-sm">Shift</kbd> + <kbd className="kbd kbd-sm">R</kbd></dd>
                  </dl>
                </section>
              </div>
            </section>
          </dialog>,
          document.body,
        ) : null}
        {accountPanelOpen ? renderWorkspaceSurface(
          <div className="settings-layer visual-viewport-frame">
            <div className="settings-layer__backdrop" aria-hidden="true" />
          <section
            className="card account-panel account-panel--dialog"
            aria-label="Settings, account, and data"
            role={studioActive ? 'region' : 'dialog'}
            aria-modal={studioActive ? undefined : true}
          >
            <div className="account-panel__heading">
              <div className="account-panel__title">
                <h2>Settings</h2>
                <SettingsSectionMenu
                  open={settingsSectionMenuOpen}
                  items={settingsSectionMenuItems}
                  onOpenChange={setSettingsSectionMenuOpen}
                  onSelect={(id) => {
                    setSettingsSectionMenuOpen(false)
                    if (studioActive) setStudioSettingsSection(id as StudioSettingsCategory)
                    else scrollToSettingsSection(id)
                  }}
                />
              </div>
              <button
                ref={accountPanelCloseRef}
                className="btn btn-ghost btn-square"
                type="button"
                onClick={closeAccountPanel}
                aria-label="Close account panel"
              ><Icon name="close" /></button>
            </div>
            <div className="account-panel__scroll-region">
            <div className="settings-panel__layout">
              {studioActive ? <StudioSettingsNavigation selected={studioSettingsSection} onSelect={setStudioSettingsSection} contextsAllowed={contextsEnabled} devAllowed={devToolsAllowed} onAdmin={developerAuthorized ? openAdminDashboard : undefined}/> : (
              <nav className="settings-panel__nav" aria-label="Settings sections">
                <ul className="menu menu-vertical">
                {settingsSectionMenuItems.map(item => <li key={item.id}><button type="button" onClick={() => scrollToSettingsSection(item.id, item.id === 'account' && localModeActive)}>{item.label}</button></li>)}
                {developerAuthorized ? (
                  <li className="settings-panel__nav-admin"><button type="button" onClick={openAdminDashboard}>Admin dashboard</button></li>
                ) : null}
                </ul>
              </nav>
              )}
              <SettingsCategoryPanels
                desktop={studioActive}
                selected={studioSettingsSection}
                contextsAllowed={contextsEnabled}
                devAllowed={devToolsAllowed}
                accountAttention={localModeActive}
                content={{
                  appearance: <>
                    <div className="settings-list">
                      <div className="studio-appearance">
                        <h4 className="studio-settings-group-title">Space &amp; reading</h4>
                        <div className="settings-row studio-interface-size" role="group" aria-labelledby="interface-size-label">
                          <span id="interface-size-label" className="settings-row__label">
                            <strong>Interface size</strong>
                            <span>{studioActive ? 'Scale text and controls together.' : 'Phones default to Medium; tablets default to Large.'}</span>
                          </span>
                          <div className="settings-options settings-options--three settings-row__control">
                            {(['small', 'medium', 'large'] as const).map((uiSize) => (
                              <button
                                key={uiSize}
                                type="button"
                                className={`btn ${deviceSettings.uiSize === uiSize ? 'is-selected' : ''}`}
                                aria-pressed={deviceSettings.uiSize === uiSize}
                                onClick={() => changeUiSize(uiSize)}
                              >
                                {uiSize.charAt(0).toUpperCase() + uiSize.slice(1)}
                              </button>
                            ))}
                          </div>
                        </div>
                        <h4 className="studio-settings-group-title">Color &amp; contrast</h4>
                        <StudioAppearanceChoices palette={studio.preference.palette} appearance={studio.preference.appearance} onChange={appearance => changeStudio({ ...studio.preference, appearance })} />
                      </div>
                    </div>
                  </>,
                  capture: <>
                    <div className="settings-list">
                      <div className="settings-row" role="group" aria-labelledby="auto-expire-label">
                        <span id="auto-expire-label" className="settings-row__label">
                          <strong>Note auto-expire duration</strong>
                          <span>Applies to newly created expiry timers.</span>
                        </span>
                        <div className="settings-options settings-options--four settings-row__control">
                          {AUTO_EXPIRE_OPTIONS.map((days) => (
                            <button
                              key={days}
                              type="button"
                              className={`btn ${globalSettings.autoExpireDays === days ? 'is-selected' : ''}`}
                              aria-pressed={globalSettings.autoExpireDays === days}
                              onClick={() => changeAutoExpire(days)}
                            >
                              {days}d
                            </button>
                          ))}
                        </div>
                      </div>
                      <div className="settings-row" role="group" aria-labelledby="start-mode-label">
                        <span id="start-mode-label" className="settings-row__label">
                          <strong>App start mode</strong>
                          <span>Used the next time the app opens.</span>
                        </span>
                        <div className="settings-options settings-row__control">
                          {(['browse', 'capture'] as const).map((startMode) => (
                            <button
                              key={startMode}
                              type="button"
                              className={`btn ${deviceSettings.startMode === startMode ? 'is-selected' : ''}`}
                              aria-pressed={deviceSettings.startMode === startMode}
                              onClick={() => changeStartMode(startMode)}
                            >
                              {startMode === 'capture' ? 'Capture' : 'Browse'}
                            </button>
                          ))}
                        </div>
                      </div>
                    </div>
                  </>,
                  reminders: <>
                    <div className="settings-list">
                      <div className="settings-row settings-row--wrap" role="group" aria-labelledby="reminder-quick-label">
                        <span id="reminder-quick-label" className="settings-row__label">
                          <strong>Reminder quick choices</strong>
                          <span>Presets shown before date and time selection.</span>
                        </span>
                        <div className="settings-compact-inputs settings-row__control">
                          <label><span>First · min</span><input className="input input-sm" type="number" min="5" max="720" value={globalSettings.reminderQuickOptions.firstMinutes} onChange={(event) => changeReminderQuickOption('firstMinutes', Number(event.target.value))} /></label>
                          <label><span>Second · min</span><input className="input input-sm" type="number" min="5" max="1440" value={globalSettings.reminderQuickOptions.secondMinutes} onChange={(event) => changeReminderQuickOption('secondMinutes', Number(event.target.value))} /></label>
                          <label><span>Tomorrow · hour</span><input className="input input-sm" type="number" min="0" max="23" value={globalSettings.reminderQuickOptions.tomorrowHour} onChange={(event) => changeReminderQuickOption('tomorrowHour', Number(event.target.value))} /></label>
                        </div>
                      </div>
                      <div className="settings-row" role="group" aria-labelledby="week-start-label">
                        <span id="week-start-label" className="settings-row__label">
                          <strong>Week starts on</strong>
                          <span>Orders reminder calendars and weekday choices.</span>
                        </span>
                        <div className="settings-options settings-row__control">
                          {(['monday', 'sunday'] as const).map((weekStartsOn) => (
                            <button
                              key={weekStartsOn}
                              type="button"
                              className={`btn ${globalSettings.weekStartsOn === weekStartsOn ? 'is-selected' : ''}`}
                              aria-pressed={globalSettings.weekStartsOn === weekStartsOn}
                              onClick={() => changeWeekStartsOn(weekStartsOn)}
                            >
                              {weekStartsOn === 'monday' ? 'Monday' : 'Sunday'}
                            </button>
                          ))}
                        </div>
                      </div>
                    </div>
                  </>,
                  units: <>
                    <div className="settings-list">
                      <div className="settings-row settings-row--wrap" role="group" aria-labelledby="units-label">
                        <span id="units-label" className="settings-row__label">
                          <strong>Measuring units</strong>
                          <span>Recipes and other measurements are shown in these units; a converted amount keeps the original beside it. Auto follows this device’s region and time zone ({resolveMeasurementSystem('auto') === 'us' ? 'US customary' : 'metric'} here).</span>
                        </span>
                        <div className="settings-options settings-row__control">
                          {MEASUREMENT_PREFERENCES.map((preference) => (
                            <button
                              key={preference}
                              type="button"
                              className={`btn ${normalizeMeasurementPreference(globalSettings.measurementSystem) === preference ? 'is-selected' : ''}`}
                              aria-pressed={normalizeMeasurementPreference(globalSettings.measurementSystem) === preference}
                              onClick={() => changeMeasurementPreference(preference)}
                            >
                              {preference === 'auto' ? 'Auto' : preference === 'metric' ? 'Metric' : 'US customary'}
                            </button>
                          ))}
                        </div>
                      </div>
                    </div>
                  </>,
                  contexts: <>

                    <div className="settings-contexts-control">
                      <p className="settings-row__label"><span>Contexts gather the notes for one thing you are working on, and sync with your account.</span></p>
                      <button type="button" className="btn btn-link settings-contexts-browse-link" onClick={() => {
                        setContextLabBrowseOverride(true)
                        setContextLabSelectedContextId(null)
                        setContextStripVisible(true)
                        suppressAccountPanelFocusRestoreRef.current = true
                        closeAccountPanel()
                        if(mode === 'capture') void finishDraft()
                      }}>Show contexts in Browse</button>
                    </div>

                  </>,
                  account: <>

                    <div className="settings-account__row">
                      <div className="settings-account__identity">
                        <strong>{authSession?.user.name ?? 'Local workspace'}</strong>
                        {hasRemoteAccount && authSession && <p className="settings-account__email">{authSession.user.email ?? 'Email unavailable for this account'}</p>}
                        <p>
                          {authProvider.id === 'local'
                            ? 'Google sign-in becomes available when the Supabase environment variables are configured. Local notes remain fully functional.'
                            : authRestoring
                              ? 'Restoring your Google session and synchronized notes…'
                              : authSession && accountRestorePhase === 'restoring'
                                ? 'Signed in with Google. Verifying synchronized notes and images before reporting this device as restored…'
                                : authSession && accountRestorePhase === 'error'
                                  ? 'Signed in with Google, but account restore is incomplete. Local data remains available while synchronization is retried.'
                                  : authSession
                                    ? `Signed in with Google through the ${supabaseConfig?.environment ?? 'configured'} workspace.`
                                    : 'Connect Google to synchronize notes between devices. Local notes remain available if sign-in fails.'}
                        </p>
                      </div>
                      <div className="account-panel__actions account-panel__actions--auth">
                        {authProvider.id !== 'local' && !authSession && !authRestoring ? (
                          <button type="button" className="btn btn-primary primary-action account-panel__auth-action" onClick={() => void handleAuthentication()} disabled={authBusy}>
                            {authBusy ? 'Connecting…' : 'Sign in with Google'}
                          </button>
                        ) : null}
                        {authProvider.id !== 'local' && authSession ? (
                          <button type="button" className="btn account-panel__auth-action" onClick={() => void handleSignOut()} disabled={authBusy}>
                            {authBusy ? 'Signing out…' : 'Sign out'}
                          </button>
                        ) : null}

                      </div>
                    </div>

                    {syncError ? <p className="account-panel__sync-error" role="status">{syncError}</p> : null}

                    {hasRemoteAccount ? (
                      <div className="settings-account-access" aria-label="Account type and note usage">
                        {accountAccess ? (
                          <>
                            <div className="settings-account-access__heading">
                              <strong>Account capacity</strong>
                              <span className={`badge badge-soft ${accountAccess.capacities.notes !== null && accountAccess.usage.notes >= accountAccess.capacities.notes ? 'badge-warning' : 'badge-info'}`}>
                                {accountAccess.displayName}
                              </span>
                            </div>
                            <p>
                              {accountAccess.usage.notes.toLocaleString()} of{' '}
                              {accountAccess.capacities.notes === null
                                ? 'unlimited'
                                : accountAccess.capacities.notes.toLocaleString()}{' '}
                              synchronized notes used
                            </p>
                            {accountAccess.capacities.notes !== null ? (
                              <progress
                                className={`progress ${accountAccess.usage.notes >= accountAccess.capacities.notes ? 'progress-warning' : 'progress-info'}`}
                                value={Math.min(accountAccess.usage.notes, accountAccess.capacities.notes)}
                                max={accountAccess.capacities.notes}
                                aria-label={`${accountAccess.usage.notes} of ${accountAccess.capacities.notes} synchronized notes used`}
                              />
                            ) : null}
                            {!accountCanAddNote(accountAccess) ? (
                              <div role="alert" className="alert alert-soft alert-warning settings-account-access__warning">
                                New notes cannot synchronize until you move a note to trash or change account type. Existing notes remain available.
                              </div>
                            ) : null}
                            <AccountPlanUsage contextsEnabled={contextsEnabled} access={accountAccess} />
                          </>
                        ) : (
                          <p>{accountAccessError ? 'Account capacity is temporarily unavailable.' : 'Checking account capacity…'}</p>
                        )}
                      </div>
                    ) : null}

                    <div className="settings-sync-status" role="status">
                      <span><Icon name="check" />{storagePersistent === true ? 'Persistent offline storage granted' : 'Offline browser storage active'}</span>
                      {localContinuity?.hasUnsynchronizedWork ? (
                        <span className="settings-sync-status__pending"><Icon name="upload-error" />Changes are waiting to sync or be backed up</span>
                      ) : localContinuity ? (
                        <span><Icon name="check" />No unsynchronized local work detected</span>
                      ) : null}
                    </div>

                    <div className="settings-list"><label className="settings-row settings-row--input">
                      <span className="settings-row__label">
                        <strong>Device name</strong>
                        <span>Detected as {deviceSettings.detectedName}. Renaming does not change its identity.</span>
                      </span>
                      <input
                        ref={deviceNameInputRef}
                        className="input input-sm settings-row__control settings-row__text-input"
                        value={deviceNameDraft}
                        maxLength={64}
                        autoComplete="off"
                        spellCheck={false}
                        onChange={(event) => setDeviceNameDraft(event.target.value)}
                        onBlur={() => {
                          if(!deviceNameDraft.trim()) setDeviceNameDraft(deviceSettings.detectedName)
                        }}
                        aria-describedby="device-name-hint"
                      />
                      <span id="device-name-hint" className="sr-only">Renaming this device does not change its unique identity.</span>
                    </label></div>

                    {!authSession && !authRestoring ? (
                      <div className="settings-account-danger">
                        <span className="settings-row__label">
                          <strong>Data on this device</strong>
                          <span>Without an account, your notes and images are stored only on this device. To erase them, use Delete data on this device in Data &amp; storage.</span>
                        </span>
                        <button type="button" className="btn btn-error btn-outline" onClick={() => scrollToSettingsSection('export-import-section', true)}>Go to Data &amp; storage</button>
                      </div>
                    ) : null}
                    {authProvider.id !== 'local' && authSession ? (
                      <div className="settings-account-danger">
                        <span className="settings-row__label">
                          <strong>Delete account</strong>
                          <span>Permanently erase your account and everything in it, on every device. This cannot be undone.</span>
                        </span>
                        <button type="button" className="btn btn-error btn-outline" onClick={() => setDeleteAccountOpen(true)} disabled={authBusy}>Delete account…</button>
                      </div>
                    ) : null}
                  </>,
                  data: <DataStorageSettings
                    continuity={localContinuity}
                    storagePersistent={storagePersistent}
                    recoveryExportBusy={recoveryExportBusy}
                    recoveryImportBusy={recoveryImportBusy}
                    recoveryImportEnabled={Boolean(authSession && accountRestorePhase === 'ready')}
                    recoveryInputRef={recoveryImportInputRef}
                    onExport={(kind) => void exportNotes(kind)}
                    notesExport={notesExport}
                    onImportRecovery={(event) => void importLocalRecovery(event)}
                    googleKeepController={googleKeepImports}
                    googleKeepEnabled={Boolean(authSession && accountRestorePhase === 'ready' && isOnline)}
                    googleKeepOpen={googleKeepImportOpen}
                    onGoogleKeepOpenChange={setGoogleKeepImportOpen}
                    noteExportController={noteExports}
                    onDeleteDeviceData={!authSession && !authRestoring ? () => setDeleteDeviceDataOpen(true) : undefined}
                  />,
                  actions: <>

                    <p className="actions-log__intro">Merge and tag actions from this device. Undo remains available after dismissing a message.</p>

                    <ActionsLog operations={visibleOperations} busy={operationBusy} onUndo={operation => void undoOperation(operation)} />

                  </>,
                  dev: <div className="settings-dev">
                    <section className="settings-dev-group" aria-labelledby="settings-dev-contexts">
                      <h4 id="settings-dev-contexts">Experiments</h4>
                      <ul className="list settings-dev-list"><li className="list-row settings-dev-tool">
                        <span className="settings-dev-tool__icon" aria-hidden="true"><Icon name="context"/></span>
                        <span className="list-col-grow settings-dev-tool__copy"><strong>Contexts</strong><span>Enable Contexts for this account on this device. Developer access is required, including in production. Turning this off keeps saved data.</span></span>
                        <label className="settings-dev-tool__toggle"><span>Enable Contexts</span><input type="checkbox" className="toggle toggle-sm" checked={contextsRequested} onChange={event => changeContextsPreference(event.target.checked)}/></label>
                      </li>
                      <li className="list-row settings-dev-tool">
                        <span className="settings-dev-tool__icon" aria-hidden="true"><Icon name="spark"/></span>
                        <span className="list-col-grow settings-dev-tool__copy"><strong>Unpack</strong><span>Screenshots you share or add become the post, recipe or ticket they show, for this account on this device. The screenshots are always kept. Rate runs in Admin → Clean-up.</span></span>
                        <label className="settings-dev-tool__toggle"><span>Unpack screenshots</span><input type="checkbox" className="toggle toggle-sm" checked={cleanupRequested} onChange={event => changeCleanupPreference(event.target.checked)}/></label>
                      </li>
                      {/* What Unpack does along the way: chosen here, never in the note. Amounts follow Settings → Units. */}
                      {cleanupRequested ? ([
                        ['cover', 'Use the photo as the note\'s picture', 'In Browse, the dish or photo instead of the screenshots'],
                        ['addRest', 'Add the rest of a post you pasted', 'Your own words stay as you typed them'],
                        ['quickAccess', 'Show bookings in Quick Access', 'From the day before to the day after'],
                        ['remind', 'Offer a reminder for bookings', 'The day before at 09:00, set only on a tap'],
                      ] as const).map(([name, title, detail]) => (
                        <li key={name} className="list-row settings-dev-tool settings-dev-tool--nested">
                          <span className="list-col-grow settings-dev-tool__copy"><strong>{title}</strong><span>{detail}</span></span>
                          <label className="settings-dev-tool__toggle"><span className="sr-only">{title}</span><input type="checkbox" className="toggle toggle-sm" checked={unpackPreferences.value[name]} onChange={event => changeUnpackPreference(name, event.target.checked)}/></label>
                        </li>
                      )) : null}</ul>
                    </section>
                    {contextsEnabled && !import.meta.env.DEV && <p>Context diagnostics are available in each Context’s actions → Sync &amp; recovery or Dev history &amp; feedback. Interface tools run in the local desktop app.</p>}
                    <section className="settings-dev-group" aria-labelledby="settings-dev-smart-tools">
                      <h4 id="settings-dev-smart-tools">Jev owner pilot</h4>
                      <ul className="list settings-dev-list">
                        <li className="list-row settings-dev-tool">
                          <span className="settings-dev-tool__icon" aria-hidden="true"><Icon name="search"/></span>
                          <span className="list-col-grow settings-dev-tool__copy"><strong>Smart Search</strong><span>{contextsEnabled ? 'Find related notes and Contexts on demand.' : 'Find related notes on demand.'} Local results remain available offline.</span></span>
                          <label className="settings-dev-tool__toggle"><span>Enable smart search</span><input type="checkbox" className="toggle toggle-sm" checked={smartPilotPreferences.search} onChange={event => changeSmartPilotPreference('search', event.target.checked)}/></label>
                        </li>
                        {supabaseConfig?.isDev && <li className="list-row settings-dev-tool">
                          <span className="settings-dev-tool__icon" aria-hidden="true"><Icon name="search"/></span>
                          <span className="list-col-grow settings-dev-tool__copy"><strong>Compare search methods</strong><span>Run the same note search through local, Jev, vector, and vector → Jev paths.</span></span>
                          <button type="button" className="btn" onClick={openSearchComparison}>Open comparison</button>
                        </li>}
                        {contextsEnabled && <li className="list-row settings-dev-tool">
                          <span className="settings-dev-tool__icon" aria-hidden="true"><Icon name="context"/></span>
                          <span className="list-col-grow settings-dev-tool__copy"><strong>Show suggested Context</strong><span>After saving a new note, suggest strong matches; you choose whether and where to attach it. The editor also shows current links.</span></span>
                          <label className="settings-dev-tool__toggle"><span>Show suggestions</span><input type="checkbox" className="toggle toggle-sm" disabled={!contextsEnabled} checked={smartSuggestionsEnabled} onChange={event => changeSmartPilotPreference('suggestions', event.target.checked)}/></label>
                        </li>}
                      </ul>
                    </section>
                    <section className="settings-dev-group" aria-labelledby="settings-dev-content-tools">
                      <h4 id="settings-dev-content-tools">Content tools</h4>
                      <ul className="list settings-dev-list">
                        <li className="list-row settings-dev-tool">
                          <span className="settings-dev-tool__icon" aria-hidden="true"><Icon name="document"/></span>
                          <div className="list-col-grow settings-dev-tool__copy"><strong>Markdown note creator</strong><span>Build one or more ordinary notes from pasted Markdown or a text file.</span></div>
                          <button type="button" className="btn" onClick={() => setMarkdownNotesImportOpen(true)}>Open note creator</button>
                        </li>
                      </ul>
                    </section>
                    {import.meta.env.DEV ? <section className="settings-dev-group" aria-labelledby="settings-dev-interface-tools">
                      <h4 id="settings-dev-interface-tools">Interface testing</h4>
                      <ul className="list settings-dev-list">
                        {PREVIEW_DEVICE_OVERRIDES_ENABLED ? <li className="list-row settings-dev-tool settings-preview-device" role="group" aria-labelledby="preview-device-label">
                          <span className="settings-dev-tool__icon" aria-hidden="true"><Icon name="view"/></span>
                          <span id="preview-device-label" className="list-col-grow settings-dev-tool__copy"><strong>Preview device</strong><span>{previewDeviceOverrideLabel(previewDeviceOverride)}. Pair with the matching browser viewport.</span></span>
                          <div className="settings-options settings-dev-tool__control">
                            {(['off', 'android-phone', 'android-tablet'] as const).map((profile) => <button key={profile} type="button" className={`btn ${previewDeviceOverride === profile ? 'is-selected' : ''}`} aria-pressed={previewDeviceOverride === profile} onClick={() => changePreviewDeviceOverride(profile)}>{profile === 'off' ? 'Off' : profile === 'android-phone' ? 'Phone' : 'Tablet'}</button>)}
                          </div>
                        </li> : null}
                      </ul>
                    </section> : null}
                  </div>,
                  about: <>
                    <div className="settings-list">
                      <div className="settings-row">
                        <span className="settings-row__label">
                          <strong>Keyboard shortcuts</strong>
                          <span>Available while editing a note on desktop.</span>
                        </span>
                        <button
                          className="btn btn-link settings-shortcuts-link settings-row__control"
                          type="button"
                          onClick={() => setKeyboardShortcutsOpen(true)}
                        >
                          <Icon name="keyboard" />
                          View shortcuts <span aria-hidden="true">→</span>
                        </button>
                      </div>
                    </div>

                    <details className="collapse collapse-arrow card card-border settings-advanced" open={studioActive || undefined}>
                      <summary className="collapse-title">
                        <span>App updates</span>
                        <span className="badge badge-soft settings-scope-badge">
                          {!APP_UPDATE_CHECKS_ENABLED
                            ? 'Development'
                            : appUpdate.phase === 'available'
                              ? 'Available'
                              : appUpdate.phase === 'checking'
                                ? 'Checking'
                                : appUpdate.phase === 'error'
                                  ? 'Retry'
                                  : 'Current'}
                        </span>
                      </summary>
                      <div className="collapse-content">
                        <div className="settings-update-status" role="status" aria-live="polite">
                          <span>Running version</span>
                          <code title={__MINIMANY_BUILD_ID__}>{__MINIMANY_BUILD_ID__.slice(0, 12)}</code>
                          <p>
                            {appUpdate.phase === 'available'
                              ? 'A newer version is ready to load.'
                              : appUpdate.phase === 'checking'
                                ? 'Checking for the latest deployment…'
                                : appUpdate.phase === 'error'
                                  ? isOnline
                                    ? 'The version check failed. Try again.'
                                    : 'Connect to the internet to check for updates.'
                                  : appUpdate.phase === 'current'
                                    ? APP_UPDATE_CHECKS_ENABLED
                                      ? 'This device is running the latest version.'
                                      : 'Automatic update checks run in deployed builds.'
                                    : 'Update checks run automatically when the app opens or resumes.'}
                          </p>
                        </div>
                        <div className="settings-update-actions">
                          <button
                            className="btn"
                            type="button"
                            onClick={() => void checkForLatestApp()}
                            disabled={!APP_UPDATE_CHECKS_ENABLED || appUpdate.phase === 'checking' || appUpdate.phase === 'applying'}
                          >
                            Check for updates
                          </button>
                          {appUpdate.phase === 'available' ? (
                            <button type="button" className="btn btn-primary primary-action" onClick={() => void applyLatestApp()}>
                              Update now
                            </button>
                          ) : null}
                        </div>
                      </div>
                    </details>

                  </>,
                }}
              />
            </div>
            </div>
          </section>

          </div>,
          studioActive,
        ) : null}

        {!adminRouteActive && !invitationVisible && mode === 'capture' ? (
          <>
            {noteRouteBlocked ? renderWorkspaceSurface(
              <div className="composer-layer visual-viewport-frame">
                <div className="composer-layer__backdrop" aria-hidden="true" />
                <section
                  className="composer note-route-status"
                  aria-labelledby="note-route-status-title"
                  aria-modal={studioActive ? undefined : true}
                  role={studioActive ? 'region' : 'dialog'}
                >
                  {noteRoutePhase !== 'resolving' ? <div className="note-route-status__back"><BackButton label="miniMany" onClick={() => void beginNewNote()} /></div> : null}
                  <div className="empty-state" aria-live="polite">
                    <span className="empty-state__glyph" aria-hidden="true">
                      {noteRouteStatus.glyph}
                    </span>
                    <h2 id="note-route-status-title">{noteRouteStatus.title}</h2>
                    <p>{noteRouteStatus.message}</p>
                    {noteRoutePhase === 'sign-in' || noteRoutePhase === 'offline' || noteRoutePhase === 'error' ? <div className="note-route-status__actions">
                      {noteRoutePhase === 'sign-in' ? (
                        <button type="button" className="btn btn-primary" onClick={() => void handleAuthentication()} disabled={authBusy}>
                          {authBusy ? 'Connecting…' : 'Sign in with Google'}
                        </button>
                      ) : (
                        <button
                          type="button"
                          className="btn btn-primary"
                          onClick={() => {
                            setIsOnline(navigator.onLine)
                            noteRouteResolutionKeyRef.current = null
                            setNoteRouteResolveAttempt((current) => current + 1)
                          }}
                        >
                          Try again
                        </button>
                      )}
                    </div> : null}
                  </div>
                </section>
              </div>,
              studioActive,
            ) : renderWorkspaceSurface(
              <div className="composer-layer visual-viewport-frame">
                <div className="composer-layer__backdrop" aria-hidden="true" />
            <section
              ref={composerRef}
              key={composerKey}
              className={`composer composer--editing ${composerInstantEntry ? 'composer--instant-entry' : ''} ${composerClosing ? 'composer--closing' : ''} ${imageDropActive ? 'composer--image-drop' : ''} ${tagToolbarOpen ? 'composer--tags-open' : ''} ${editorTyping ? 'composer--typing' : ''} ${sharedTextFolded ? 'composer--shared-folded' : ''}`.trim()}
              aria-label="Note composer"
              role={studioActive ? 'region' : 'dialog'}
              aria-modal={studioActive ? undefined : true}
              onDragEnter={(event) => {
                if (event.dataTransfer.types.includes('Files')) setImageDropActive(true)
              }}
              onDragOver={(event) => {
                if (!event.dataTransfer.types.includes('Files')) return
                event.preventDefault()
                event.dataTransfer.dropEffect = 'copy'
                setImageDropActive(true)
              }}
              onDragLeave={(event) => {
                if (!event.currentTarget.contains(event.relatedTarget as Node | null)) {
                  setImageDropActive(false)
                }
              }}
              onDrop={handleImageDrop}
            >
              {imageDropActive ? (
                <div className="composer__drop-overlay" aria-hidden="true">
                  <Icon name="image" />
                  <span>Add image to this note</span>
                </div>
              ) : null}
              <div className={[
                'composer__scroll-region',
                imageAside ? 'composer__scroll-region--has-attachments' : '',
                liveUrls.length > 0 ? 'composer__scroll-region--has-link-previews' : '',
              ].filter(Boolean).join(' ')} ref={composerScrollRegionRef}
                onPointerDownCapture={(event) => {
                  const target = event.target
                  if (!tagToolbarOpen || !(target instanceof Element)
                    || target.closest('.tag-editor, .composer__tag-rail')) return
                  // While a phone keyboard is up, a tap beside the tag editor only puts the keyboard away; the editor stays open
                  // (a second tap closes it). Closing Gboard used to close the tags with it.
                  if (document.documentElement.dataset.visualViewportKeyboard === 'open') {
                    event.preventDefault()
                    tagInputRef.current?.blur()
                    // Nor does the tap's click put the caret in the note, which would bring the keyboard straight back.
                    const swallow = new AbortController()
                    event.currentTarget.addEventListener('click', (click) => { click.preventDefault(); click.stopPropagation() }, { capture: true, once: true, signal: swallow.signal })
                    window.setTimeout(() => swallow.abort(), 800)
                    return
                  }
                  // Let a text tap place its new caret instead of restoring the
                  // pre-tagging selection. Bare canvas taps resume at the end.
                  closeTagging(false)
                  if (target === event.currentTarget || target.classList.contains('composer__editor-layout')) {
                    event.preventDefault()
                    editorRef.current?.placeCaretAtEnd()
                  }
                }}
              >
              <div className="composer__topline">
                <div className="composer__topline-main">
                  <div className={`composer__meta ${composerContinuityStatus ? 'composer__meta--continuity' : ''}`.trim()}>
                  {studioActive ? <BackButton
                    className="studio-editor-back"
                    onClick={() => {
                      if (searchReturnRef.current) void returnToSearchResultContext()
                      else void finishDraft()
                    }}
                  /> : null}
                  {/* A phone shows Quick Access and the reminder only when they are on (a state, tapped to change); a desktop keeps them at hand. */}
                  {desktopNote || activeNote?.quickAccess ? <button
                    className={`btn btn-ghost composer-header-action editor-mode-button editor-mode-button--quick-access${activeNote?.quickAccess ? ' editor-mode-button--quick-access-active' : ''}`}
                    type="button"
                    onClick={() => void toggleCurrentNoteQuickAccess()}
                    aria-pressed={Boolean(activeNote?.quickAccess)}
                    aria-label={activeNote?.quickAccess ? 'Remove note from Quick Access' : 'Add note to Quick Access'}
                    title={canActOnDraft
                      ? activeNote?.quickAccess ? 'Remove from Quick Access' : 'Add to Quick Access'
                      : 'Add content before assigning Quick Access'}
                    disabled={!canActOnDraft}
                  >
                    <Icon name="flash" />
                    {studioActive ? <span className="studio-editor-action-label">Quick Access</span> : null}
                  </button> : null}
                  {desktopNote || activeNote?.reminder ? <button
                    type="button"
                    className={activeNote?.reminder
                      ? `btn btn-ghost composer-header-action reminder-chip reminder-chip--active${activeReminderElapsed ? ' reminder-chip--elapsed' : ''}`
                      : 'btn btn-ghost composer-header-action reminder-chip'}
                    onClick={() => setReminderEditorOpen(true)}
                    aria-haspopup="dialog"
                    aria-expanded={reminderEditorOpen}
                    aria-pressed={Boolean(activeNote?.reminder)}
                    aria-label={activeNote?.reminder
                      ? `Change reminder. ${activeReminderElapsed ? 'Elapsed' : 'Next'} reminder ${reminderNaturalTime(activeNote.reminder)}`
                      : 'Set reminder'}
                    title={activeNote?.reminder ? `${activeReminderElapsed ? 'Elapsed' : 'Next'} reminder ${reminderNaturalTime(activeNote.reminder)}` : 'Set reminder'}
                    disabled={!canActOnDraft}
                  >
                    <Icon name="bell" />
                    {activeNote?.reminder ? <span>{reminderNaturalTime(activeNote.reminder)}</span> : studioActive ? <span className="studio-editor-action-label">Reminder</span> : null}
                  </button> : null}
                  {composerContinuityStatus ? (
                    <span
                      className={`continuity-state continuity-state--${composerContinuityStatus.tone}`}
                      role="status"
                      aria-live="polite"
                    >
                      <Icon name={composerContinuityStatus.icon} />
                      {composerContinuityStatus.label}
                    </span>
                  ) : null}
                  {saveState === 'error' ? (
                    <span className="save-state save-state--error" role="status" aria-live="polite">
                      <span /> Storage issue
                    </span>
                  ) : null}
                  {reminderEditorOpen ? (
                    <ReminderEditor
                      noteTitle={activeNote?.manualTitle ?? activeNote?.derivedTitle ?? 'This note'}
                      reminder={activeNote?.reminder ?? null}
                      quickOptions={globalSettings.reminderQuickOptions}
                      weekStartsOn={globalSettings.weekStartsOn}
                      onClose={() => {
                        closeReminderEditor()
                      }}
                      onSave={saveActiveReminder}
                    />
                  ) : null}
                  {nativeReminderSnoozeNoteId === activeNote?.id && activeNote?.reminder ? (
                    <NativeReminderSnoozeDialog
                      title={activeNote.reminder.title ?? activeNote.manualTitle ?? activeNote.derivedTitle}
                      onClose={() => setNativeReminderSnoozeNoteId(null)}
                      onSnooze={async (durationMs) => await snoozeNativeReminder(activeNote.id, durationMs)}
                    />
                  ) : null}
                  </div>
                  <div className="composer__actions">
                  <UploadStatusIndicator
                    status={activeUploadStatus}
                    prominent
                    onRetry={() => void retryNoteUpload(draft.noteId, activeAssets)}
                  />
                  <button
                    ref={noteMenuButtonRef}
                    className="btn btn-ghost composer-header-action editor-mode-button composer__more-action"
                    type="button"
                    onClick={() => setNoteMenuOpen(open => !open)}
                    aria-label="More for this note"
                    aria-haspopup="menu"
                    aria-expanded={noteMenuOpen}
                    title="More"
                  >
                    <Icon name="more" />
                  </button>
                  {noteMenuOpen ? (
                    <LinkActionsMenu
                      kind="Note actions"
                      title=""
                      anchor={noteMenuButtonRef.current}
                      onClose={() => setNoteMenuOpen(false)}
                      footer={activeNote ? `${editedLabel(activeNote.modifiedAt)} · Created ${new Date(activeNote.createdAt).toLocaleString(undefined, { weekday: 'short', day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}` : undefined}
                      model={{ groups: [
                        // What a desktop keeps in the bar, a phone finds here first.
                        ...(desktopNote ? [] : [[
                          { id: 'quick-access', icon: 'flash' as const, label: activeNote?.quickAccess ? 'Remove from Quick Access' : 'Add to Quick Access', disabled: !canActOnDraft, run: () => void toggleCurrentNoteQuickAccess() },
                          { id: 'reminder', icon: 'bell' as const, label: activeNote?.reminder ? `Reminder ${reminderNaturalTime(activeNote.reminder)}` : 'Remind me', disabled: !canActOnDraft, run: () => setReminderEditorOpen(true) },
                        ]]),
                        [
                          { id: 'tags', icon: 'tag' as const, label: draft.userTags.length ? 'Edit tags' : 'Add tags', disabled: !activeNote, run: toggleTagEditing },
                          ...unpackMenu,
                          { id: 'copy', icon: 'copy' as const, label: 'Copy as Markdown', disabled: !canCopyDraft, run: () => void copyMarkdown(draft.body, draft.noteId) },
                        ],
                        [
                          { id: 'archive', icon: 'archive' as const, label: 'Archive', disabled: !canActOnDraft, run: () => void changeActiveStatus('archived') },
                          { id: 'trash', icon: 'trash' as const, label: 'Move to trash', danger: true, disabled: !canActOnDraft, run: () => void changeActiveStatus('trash') },
                        ],
                      ] }}
                    />
                  ) : null}
                  </div>
                </div>
              </div>

              <input
                ref={imageInputRef}
                hidden
                type="file"
                accept={IMAGE_FILE_ACCEPT}
                multiple
                onChange={chooseImages}
              />
              {activeImagePreview ? (
                <ImageLightbox
                  {...activeImagePreview}
                  loadFullImage={loadFullImage}
                  assets={activeAssets}
                  remoteUrls={remoteAssetUrls}
                  onClose={() => setActiveImagePreview(null)}
                />
              ) : null}

              {smartSuggestionsEnabled && smartSuggestion?.placement === 'top' && smartSuggestion.note.id === draft.noteId ? <SmartContextSuggestionBar
                count={smartSuggestion.ids.length} busy={smartSuggestionBusy} preview={notePreview(smartSuggestion.note)}
                title={smartSuggestion.records.find(record => record.id === smartSuggestion.ids[0])?.title}
                onAdd={() => void attachSuggestedContext()}
                onChoose={() => { smartSuggestion.flow.event('owner_choice', 'ok', 'choose_contexts'); setAddToContextNotes({ scope: authSession!.user.id, notes: [smartSuggestion.note], suggestedContextIds: smartSuggestion.ids, returnToNote: true }); setSmartSuggestion(null) }}
                onDismiss={() => { smartSuggestion.flow.event('owner_choice', 'ok', 'dismissed'); setSmartSuggestion(null) }}
              /> : null}
              {operationNotices(draft.noteId)}
              <div className={imageAside
                ? 'composer__editor-layout composer__editor-layout--has-attachments'
                : 'composer__editor-layout'}>
                <div
                  className="composer__note-content"
                  onPointerDown={(event) => {
                    if (event.target !== event.currentTarget) return
                    event.preventDefault()
                  }}
                  onClick={(event) => {
                    if (event.target !== event.currentTarget) return
                    editorRef.current?.placeCaretAtEnd()
                  }}
                >
                  {editorLinkTitle ? (
                    <h1 className="note-editor__derived-title">{editorLinkTitle}</h1>
                  ) : null}
                  {leadingCard ? (
                    <LinkNoteCard
                      merged={mergedUnpacked ? { kindLabel: cleanupCards.get(draft.noteId)!.label } : undefined}
                      url={leadingCard}
                      contentType={classifyContent(leadingCard.originalUrl, [leadingCard]).contentType}
                      fallbackTitle={editorFallbackTitleForUrl(leadingCard)}
                      metadataStatus={linkMetadataStatusForUrl(draft.noteId, leadingCard)}
                      onRetryMetadata={(url) => retryLinkMetadata(draft.noteId, url)}
                      onRemove={removeEditorUrl}
                      onEdit={(url) => editorRef.current?.editLink(url.originalUrl)}
                      onRemoveLink={removeEditorUrlWithWords}
                      gist={gistUiFor(leadingCard)}
                      onDisplay={(choice) => chooseLinkDisplay(leadingCard, choice)}
                      onExpand={gistsEnabled() ? expandGenerations(leadingCard) : undefined}
                    />
                  ) : null}
                  {/* Unpack's line sits above the images it is about; its cleaned items take their place. */}
                  {cleanupShownEnabled && activeWorkspace() && (editorImageAttachmentCount > 0 || cleanupCards.has(draft.noteId) || cleanupAuto?.noteId === draft.noteId || mayBeBookingShare(draft.body)) ? <Suspense fallback={null}>
                    <CleanupPanel
                      noteId={draft.noteId}
                      assets={activeAssets}
                      assetOrder={draft.assetIds ?? []}
                      remoteUrls={remoteAssetUrls}
                      workspace={activeWorkspace()!.databaseName}
                      owner={authSession?.user.id ?? null}
                      auto={cleanupAuto?.noteId === draft.noteId ? cleanupAuto.assetIds : null}
                      autoText={cleanupAuto?.noteId === draft.noteId && Boolean(cleanupAuto.text)}
                      onFoldText={setSharedTextFolds}
                      onShowText={() => setSharedTextShown(draft.noteId)}
                      onAutoDone={() => setCleanupAuto(null)}
                      loadFullImage={loadFullImage}
                      noteText={draft.body}
                      onBodyChange={(body) => updateDraft((current) => ({ ...current, body }))}
                      onCover={(cover) => setCleanupCover(draft.noteId, cover)}
                      onCard={(card) => setCleanupCard(draft.noteId, card)}
                      merged={mergedUnpacked}
                      view={cleanupView}
                      onView={(view) => chooseCleanupView(draft.noteId, view)}
                      measurementSystem={resolveMeasurementSystem(globalSettings.measurementSystem)}
                      reminderAt={notesById.get(draft.noteId)?.reminder?.nextAt ?? null}
                      inQuickAccess={notesById.get(draft.noteId)?.quickAccess ?? false}
                      onRemind={(at, title) => saveActiveReminder(createReminder(at, { title }))}
                      onQuickAccess={async (title) => {
                        if (notesRef.current.get(draft.noteId)?.quickAccess) return true
                        await toggleCurrentNoteQuickAccess(title)
                        return notesRef.current.get(draft.noteId)?.quickAccess ?? false
                      }}
                      preferences={unpackPreferences.value}
                      onMenu={setUnpackMenu}
                      onReading={(reading) => setUnpackReading(reading ? draft.noteId : null)}
                      onOpenShot={setActiveImagePreview}
                      onAddPhoto={async (file) => (await addImageFiles([file]))[0] ?? null}
                      onLeaveQuickAccess={async () => {
                        if (!notesRef.current.get(draft.noteId)?.quickAccess) return true
                        await toggleCurrentNoteQuickAccess()
                        return !notesRef.current.get(draft.noteId)?.quickAccess
                      }}
                    />
                  </Suspense> : null}
                  {editorImageAttachmentCount > 0 && !imageAside && !cleanView ? imageStageNode('stage') : null}
                  {sharedTextFolded ? (
                    <button type="button" className="composer__add-note" onClick={() => { setSharedTextShown(draft.noteId); window.requestAnimationFrame(() => editorRef.current?.placeCaretAtEnd()) }}>
                      Add a note
                    </button>
                  ) : null}
                  <ShareTidyStrip
                    noteId={draft.noteId}
                    body={draft.body}
                    onBodyChange={(body) => updateDraft((current) => ({ ...current, body }))}
                  />

                  <MarkdownEditor
                    ref={editorRef}
                    initialFocus={editorInitialFocusForEntry({
                      formFactor: studio.runtime.formFactor,
                      isExistingNote: Boolean(activeNote || requestedNoteId),
                      isRoutedNoteOpen: Boolean(requestedNoteId && noteRoutePhase === 'open'),
                      readingScreenshots: cleanupEnabled && cleanupAuto?.noteId === draft.noteId,
                    })}
                    markdown={draft.body}
                    previewUrls={liveUrls}
                    collapseLeadingLinkTarget={leadingCard ? linkTargetKey(leadingCard.originalUrl) : null}
                    onRetryLinkMetadata={(url) => retryLinkMetadata(draft.noteId, url)}
                    onRemoveLink={removeEditorUrlWithWords}
                    onLinkDisplay={chooseLinkDisplay}
                    linkActions={(url) => ({
                      gist: gistUiFor(url),
                      onPreview: () => setPreviewSheetKey(linkMetadataUrlKey(url)),
                      onExpand: gistsEnabled() ? expandGenerations(url) : undefined,
                      onRemove: () => removeEditorUrl(url),
                    })}
                    onChange={updateEditorBody}
                    onEditorFocusChange={setNativeEditorFocused}
                    placeholder={editorImageAttachmentCount > 0 ? 'Add a note' : undefined}
                    onPasteImages={addImagesAndUnpack}
                    onPasteLinkAttachments={(urls, attach) => {
                      updateDraft((current) => applyPastedUrlAttachmentChoice(current, urls, attach))
                    }}
                    onInlineTags={applyInlineTags}
                    inlineTagSuggestions={suggestedTags}
                    onUndo={undoEditor}
                    onRedo={redoEditor}
                    onFormatStateChange={setEditorFormatState}
                    onNewNote={() => {
                      void beginNewNote().then((started) => {
                        if (started) void notify.success('Note saved')
                      })
                    }}
                    onDone={() => {
                      if (searchReturnRef.current) void returnToSearchResultContext()
                      else void finishDraft()
                    }}
                  />
                  {/* The note's tags (and Contexts) close the note, quietly; they are added from the ⋯ menu. */}
                  {noteDetails}
                  {previewSheetUrl ? (
                    <SheetDialog title="Link preview" icon="link" closeLabel="Close link preview" className="link-preview-sheet" onClose={() => setPreviewSheetKey(null)}>
                      <LinkNoteCard
                        url={previewSheetUrl}
                        contentType={classifyContent(previewSheetUrl.originalUrl, [previewSheetUrl]).contentType}
                        fallbackTitle={editorFallbackTitleForUrl(previewSheetUrl)}
                        metadataStatus={linkMetadataStatusForUrl(draft.noteId, previewSheetUrl)}
                        onRetryMetadata={(url) => retryLinkMetadata(draft.noteId, url)}
                        gist={gistUiFor(previewSheetUrl)}
                        onDisplay={(choice) => chooseLinkDisplay(previewSheetUrl, choice)}
                        onExpand={gistsEnabled() ? expandGenerations(previewSheetUrl) : undefined}
                        attention="now"
                      />
                    </SheetDialog>
                  ) : null}
                  {answersViewUrl && gistsEnabled() ? (
                    <LinkAnswersOverlay
                      key={answersView!.key}
                      url={answersViewUrl}
                      initial={answersView!.selected}
                      summaryStatus={gistController.statusOf(answersViewUrl)}
                      summaryDetail={gistController.detailOf(answersViewUrl)}
                      onSummarise={() => void gistController.request(answersViewUrl, 'manual', { refresh: true })}
                      ask={{
                        status: gistController.askStatusOf(answersViewUrl),
                        statusDetail: gistController.askDetailOf(answersViewUrl),
                        readability: gistController.readabilityOf(answersViewUrl),
                        check: () => void gistController.check(answersViewUrl),
                        ask: (question) => gistController.ask(answersViewUrl, question),
                        clearStatus: () => gistController.clearAskStatus(answersViewUrl),
                        remove: (id) => void gistController.removeAnswer(answersViewUrl, id),
                      }}
                      onClose={() => setAnswersView(null)}
                    />
                  ) : null}

                </div>

                {imageAside ? (
                  <aside className="composer__attachment-sidebar" aria-label="Images in this note">
                    {imageStageNode('aside')}
                  </aside>
                ) : null}
              </div>



              </div>

              {!studioActive ? <button
                className="composer__done-fab"
                type="button"
                // Keeps the editor's focus: a phone closing its keyboard on touch moves this button down before the tap ends, and the
                // tap then only closed the keyboard. The formatting bar's buttons do the same.
                onPointerDown={(event) => event.preventDefault()}
                onClick={() => {
                  if (searchReturnRef.current) void returnToSearchResultContext()
                  else void finishDraft()
                }}
                aria-label="Done"
                title="Done"
              >
                <Icon name="check" />
              </button> : null}

              <div className="format-toolbar" role="toolbar" aria-label="Formatting">
                <div className="format-toolbar__group" role="group" aria-label="History and images">
                  <button type="button" onPointerDown={(event) => event.preventDefault()} onClick={undoEditor} title="Undo" aria-label="Undo editing" disabled={!historyAvailability.canUndo}><Icon name="undo" /></button>
                  <button type="button" onPointerDown={(event) => event.preventDefault()} onClick={() => imageInputRef.current?.click()} title="Add image" aria-label="Add image"><Icon name="image" /></button>
                </div>
                <div className="format-toolbar__group" role="group" aria-label="Text style">
                  <button
                    type="button"
                    onPointerDown={(event) => event.preventDefault()}
                    onClick={() => applyEditorFormat('heading')}
                    title="Cycle text style: Regular, H1, H2"
                    aria-label={`Heading style: ${editorFormatState.headingLevel === 'regular' ? 'Regular text' : editorFormatState.headingLevel.toUpperCase()}`}
                    aria-pressed={editorFormatState.heading}
                  >{editorFormatState.headingLevel === 'regular' ? 'H' : editorFormatState.headingLevel.toUpperCase()}</button>
                  <button type="button" onPointerDown={(event) => event.preventDefault()} onClick={() => applyEditorFormat('bold')} title="Bold" aria-label="Bold" aria-pressed={editorFormatState.bold}><strong>B</strong></button>
                  <button type="button" onPointerDown={(event) => event.preventDefault()} onClick={() => applyEditorFormat('italic')} title="Italic" aria-label="Italic" aria-pressed={editorFormatState.italic}><em>I</em></button>
                  <button type="button" onPointerDown={(event) => event.preventDefault()} onClick={() => applyEditorFormat('underline')} title="Underline" aria-label="Underline" aria-pressed={editorFormatState.underline}><u>U</u></button>
                  <button type="button" onPointerDown={(event) => event.preventDefault()} onClick={() => applyEditorFormat('highlight')} title="Highlight" aria-label="Highlight" aria-pressed={editorFormatState.highlight}><span className="format-highlight">A</span></button>
                  <button type="button" onPointerDown={(event) => event.preventDefault()} onClick={() => applyEditorFormat('link')} title="Link" aria-label="Link" aria-pressed={editorFormatState.link}><Icon name="link" /></button>
                </div>
                <div className="format-toolbar__group" role="group" aria-label="Lists" onPointerDownCapture={() => editorRef.current?.captureListSelection()}>
                  <button type="button" onPointerDown={(event) => event.preventDefault()} onClick={() => applyEditorFormat('bullet-list')} title="Bullet list" aria-label="Bullet list" aria-pressed={editorFormatState.bulletList}><Icon name="list" /></button>
                  <button type="button" onPointerDown={(event) => event.preventDefault()} onClick={() => applyEditorFormat('ordered-list')} title="Numbered list" aria-label="Numbered list" aria-pressed={editorFormatState.orderedList}>1.</button>
                  <button type="button" onPointerDown={(event) => event.preventDefault()} onClick={() => applyEditorFormat('checklist')} title="Checklist" aria-label="Checklist" aria-pressed={editorFormatState.checklist}><Icon name="checklist" /></button>
                </div>
                <div className="format-toolbar__group" role="group" aria-label="Blocks">
                  <button type="button" onPointerDown={(event) => event.preventDefault()} onClick={() => applyEditorFormat('quote')} title="Quote" aria-label="Quote" aria-pressed={editorFormatState.quote}>❝</button>
                  <button type="button" onPointerDown={(event) => event.preventDefault()} onClick={() => applyEditorFormat('divider')} title="Divider" aria-label="Divider">―</button>
                  <button type="button" onPointerDown={(event) => event.preventDefault()} onClick={() => applyEditorFormat('code-block')} title="Code block" aria-label="Code block" aria-pressed={editorFormatState.codeBlock}>&lt;/&gt;</button>
                </div>
                <div className="format-toolbar__group" role="group" aria-label="Clear formatting">
                  <button className="btn btn-ghost format-toolbar__clear" type="button" onPointerDown={(event) => event.preventDefault()} onClick={() => applyEditorFormat('plain-text')} title="Clear Markdown" aria-label="Clear Markdown">T×</button>
                </div>
              </div>
            </section>
              </div>,
              studioActive,
            )}

            {!retainBrowseSurface && handoffNotes.length > 0 ? (
              <section className={canActOnDraft ? 'content-section content-section--compact' : 'content-section'}>
                <div className="section-heading">
                  <div><span className="eyebrow">Handoff</span><h2>From another device</h2></div>
                </div>
                <div className={canActOnDraft ? 'note-list note-list--compact' : 'note-list'}>
                  {handoffNotes.map((note) => (
                    <NoteCard
                      key={note.id}
                      note={note}
                      assets={assetsByNote.get(note.id)?.filter((asset) => note.assetIds?.includes(asset.id))}
                      expectedCount={note.assetIds?.length ?? 0}
                      remoteAssetUrls={remoteAssetUrls}
                      noteSyncAttemptCount={noteSyncAttempts.get(note.id) ?? 0}
                      uploadStatusNow={uploadStatusClock}
                      onRetryUpload={retryNoteUpload}
                      copied={copiedNoteId === note.id}
                      compact={canActOnDraft}
                      transitionScope="handoff"
                      onCopy={copyNote}
                      onBump={(selected) => void runNoteCardTransition(() => bumpNoteToTop(selected))}
                      onOpen={(selected) => void openNote(selected)}
                      onStatusChange={(selected, status) => void runNoteCardTransition(
                        () => changeNoteStatus(selected, status),
                      )}
                    />
                  ))}
                </div>
              </section>
            ) : null}

            {!retainBrowseSurface && (recentNotes.length > 0 || !canActOnDraft) ? <section className={canActOnDraft ? 'content-section content-section--compact' : 'content-section'}>
              <div className="section-heading">
                <div><span className="eyebrow">Inbox</span><h2>Recent notes</h2></div>
                <button type="button" onClick={() => switchMode('search')}>See all <span>↗</span></button>
              </div>
              {recentNotes.length > 0 ? (
                <div className={canActOnDraft ? 'note-list note-list--compact' : 'note-list'}>
                  {(canActOnDraft ? recentNotes.slice(0, 5) : recentNotes).map((note) => (
                    <NoteCard
                      key={note.id}
                      note={note}
                      assets={assetsByNote.get(note.id)?.filter((asset) => note.assetIds?.includes(asset.id))}
                      expectedCount={note.assetIds?.length ?? 0}
                      remoteAssetUrls={remoteAssetUrls}
                      noteSyncAttemptCount={noteSyncAttempts.get(note.id) ?? 0}
                      uploadStatusNow={uploadStatusClock}
                      onRetryUpload={retryNoteUpload}
                      copied={copiedNoteId === note.id}
                      compact={canActOnDraft}
                      transitionScope="recent"
                      onCopy={copyNote}
                      onBump={(selected) => void runNoteCardTransition(() => bumpNoteToTop(selected))}
                      onOpen={(selected) => void openNote(selected)}
                      onStatusChange={(selected, status) => void runNoteCardTransition(
                        () => changeNoteStatus(selected, status),
                      )}
                    />
                  ))}
                </div>
              ) : (
                <div className="empty-state">
                  <span className="empty-state__glyph">+</span>
                  <h3>Your next thought starts here.</h3>
                  <p>Type above. It will be available offline and searchable immediately.</p>
                </div>
              )}
            </section> : null}
          </>
        ) : null}
        {!adminRouteActive && browseSurfaceMounted ? (
          <section
            className="search-view"
            hidden={browseCovered}
            data-mobile-search-surface={mobileSearchSurfaceOpen ? 'open' : 'closed'}
          >
            <div className="search-heading">
              <h1>{studioActive ? studioBrowseTitle : 'Search your notes.'}</h1>
            </div>
            <div className={searchFiltersOpen ? 'search-workspace search-workspace--filters-open' : 'search-workspace'}>
              <div ref={searchControlsRef} className="search-controls">
                <div className="search-box">
                  <Icon name="search" />
                  <input
                    ref={searchRef}
                    type="search"
                    role={smartSearchActive && visibleSearchTagSuggestions.length === 0 ? 'searchbox' : 'combobox'}
                    value={searchQuery}
                    onChange={(event) => {
                      setSearchQuery(event.target.value)
                      smartSearchSequenceRef.current++
                      setSmartSearchResult(null)
                      setSmartSearchError(null)
                      setSmartSearchBusy(false)
                      setSearchTagSuggestionsOpen(true)
                      setSearchTagSuggestionIndex(null)
                    }}
                    onKeyDown={handleSearchKeyDown}
                    onFocus={() => setSearchTagSuggestionsOpen(true)}
                    onBlur={() => {
                      setSearchTagSuggestionsOpen(false)
                      setSearchTagSuggestionIndex(null)
                    }}
                    placeholder="Try a phrase, tag, context"
                    aria-label={contextsEnabled ? 'Search notes and contexts' : 'Search notes'}
                    aria-autocomplete={smartSearchActive && visibleSearchTagSuggestions.length === 0 ? undefined : 'list'}
                    aria-controls={visibleSearchTagSuggestions.length > 0 ? 'search-tag-suggestions' : smartSearchActive ? undefined : 'search-results-list'}
                    aria-expanded={smartSearchActive && visibleSearchTagSuggestions.length === 0 ? undefined : visibleSearchTagSuggestions.length > 0}
                    aria-activedescendant={smartSearchActive ? undefined : activeSearchTagSuggestion
                      ? `search-tag-suggestion-${activeSearchTagSuggestion}`
                      : activeSearchResultDomId}
                  />
                  {smartSearchEnabled ? <button
                    type="button"
                    className="btn btn-ghost search-box-action smart-search-toggle"
                    aria-label={`Smart Search ${smartSearchActive ? 'on' : 'off'}. ${smartSearchActive ? 'Switch to standard search' : 'Switch to Smart Search'}`}
                    aria-pressed={smartSearchActive}
                    title={hasActiveSearchFilters ? 'Clear filters to use Smart Search' : !isOnline ? 'Smart Search requires a connection' : smartSearchActive ? 'Smart Search on · show standard results' : contextsEnabled ? 'Smart Search off · find related notes and Contexts' : 'Smart Search off · find related notes'}
                    disabled={hasActiveSearchFilters || (!isOnline && !smartSearchActive)}
                    onPointerDown={(event) => event.preventDefault()}
                    onClick={() => {
                      if (smartSearchActive) {
                        smartSearchSequenceRef.current++
                        setSmartSearchSelected(false)
                        setSmartSearchBusy(false)
                      } else {
                        setSmartSearchSelected(true)
                        if (smartSearchQuery.length >= 2 && smartSearchResult?.query !== smartSearchQuery) void runJevSearch()
                      }
                    }}
                  ><Icon name="spark" /></button> : null}
                  <button
                    type="button"
                    className={`btn btn-ghost search-box-action quick-access-toggle${effectiveSearchFilters.quickAccess ? ' quick-access-toggle--active' : ''}`}
                    aria-pressed={Boolean(effectiveSearchFilters.quickAccess)}
                    aria-label={effectiveSearchFilters.quickAccess ? 'Show all notes' : 'Show only Quick Access notes'}
                    title={effectiveSearchFilters.quickAccess ? 'Show all notes' : 'Quick Access notes'}
                    onPointerDown={(event) => event.preventDefault()}
                    onClick={() => applySearchFilter((current) => ({
                      ...current,
                      quickAccess: !current.quickAccess || undefined,
                    }))}
                  >
                    <Icon name="flash" />
                  </button>
                  <button
                    ref={filterToggleRef}
                    type="button"
                    className={hasActiveSearchFilters ? 'search-box-action filter-toggle filter-toggle--active' : 'search-box-action filter-toggle'}
                    onClick={() => {
                      searchFiltersOpen ? closeSearchFilters() : openSearchFilters()
                    }}
                    aria-label={searchFiltersOpen ? 'Hide filters' : 'Show filters'}
                    aria-expanded={searchFiltersOpen}
                    aria-controls="search-filter-panel"
                    title="Filters"
                  >
                    <Icon name="filter" />
                    {activeSearchFilterCount > 0 ? <span>{activeSearchFilterCount}</span> : null}
                  </button>
                  {searchQuery || hasActiveSearchFilters ? (
                    <SearchClearButton onClear={clearSearchFromButton} />
                  ) : null}
                  {visibleSearchTagSuggestions.length > 0 ? (
                    <div id="search-tag-suggestions" className="search-tag-suggestions" role="listbox" aria-label="Matching tags">
                      {visibleSearchTagSuggestions.map((tag) => (
                        <button
                          id={`search-tag-suggestion-${tag}`}
                          key={tag}
                          type="button"
                          role="option"
                          aria-selected={activeSearchTagSuggestion === tag}
                          className={`btn btn-ghost search-tag-suggestion${activeSearchTagSuggestion === tag ? ' search-tag-suggestion--active' : ''}`}
                          onPointerDown={(event) => event.preventDefault()}
                          onClick={() => selectSearchTagSuggestion(tag)}
                        >
                          <span><TagLabel tag={tag} /></span>
                          {searchQueryTags.includes(tag) || searchFilters.tag === tag ? <small>Selected</small> : null}
                        </button>
                      ))}
                    </div>
                  ) : null}
                </div>
              </div>
              {searchFiltersOpen ? (
                <aside id="search-filter-panel" className="search-filter-panel" aria-label="Search filters">
                  <button type="button" className="btn btn-ghost btn-square filter-panel__close" aria-label="Close filters" onClick={closeSearchFilters}><span aria-hidden="true">×</span></button>
                  <div className="filter-panel__content">
                  <section className="filter-group filter-group--properties" aria-labelledby="filter-properties-heading">
                    <div className="filter-group__heading-row">
                      <h2 id="filter-properties-heading" className="filter-group__heading">Properties</h2>
                      {hasActiveSearchFilters ? (
                        <button
                          type="button"
                          className="btn btn-link filter-panel__desktop-clear"
                          onClick={resetSearchFilters}
                        >
                          Clear filters
                        </button>
                      ) : null}
                    </div>
                    <div className="filter-group__options">
                      <button
                        type="button"
                        className={`btn btn-ghost filter-chip filter-chip--quick-access${effectiveSearchFilters.quickAccess ? ' filter-chip--active' : ''}`}
                        aria-pressed={Boolean(effectiveSearchFilters.quickAccess)}
                        onPointerDown={(event) => event.preventDefault()}
                        onClick={() => applySearchFilter((current) => ({
                          ...current,
                          quickAccess: !current.quickAccess || undefined,
                        }))}
                      >
                        <Icon name="flash" /> Quick Access
                      </button>
                      <button
                        type="button"
                        className={`btn btn-ghost filter-chip filter-chip--type-reminder${effectiveSearchFilters.hasAlarm ? ' filter-chip--active' : ''}`}
                        aria-pressed={Boolean(effectiveSearchFilters.hasAlarm)}
                        onPointerDown={(event) => event.preventDefault()}
                        onClick={() => applySearchFilter((current) => ({
                          ...current,
                          hasAlarm: !current.hasAlarm || undefined,
                        }))}
                      >
                        <Icon name="bell" /> Has reminder
                      </button>
                      {([
                        { property: 'image', label: 'Image', icon: 'image', tone: 'type-image' },
                        { property: 'video', label: 'Video', icon: 'film', tone: 'type-video' },
                        { property: 'audio', label: 'Audio', icon: 'audio', tone: 'type-audio' },
                        { property: 'article', label: 'Article', icon: 'article', tone: 'type-article' },
                        { property: 'social', label: 'Social', icon: 'post', tone: 'type-social' },
                      ] as const).map(({ property, label, icon, tone }) => (
                        <button
                          type="button"
                          key={property}
                          className={`btn btn-ghost filter-chip filter-chip--${tone}${effectiveSearchFilters.property === property ? ' filter-chip--active' : ''}`}
                          aria-pressed={effectiveSearchFilters.property === property}
                          onPointerDown={(event) => event.preventDefault()}
                          onClick={() => applySearchFilter((current) => ({
                            ...current,
                            property: current.property === property ? undefined : property,
                          }))}
                        >
                          <Icon name={icon} /> {label}
                        </button>
                      ))}
                    </div>
                  </section>
                  <section className="filter-group filter-group--state" aria-labelledby="filter-state-heading">
                    <h2 id="filter-state-heading" className="filter-group__heading">State</h2>
                    <div className="filter-group__options">
                      {([
                        { status: 'archived', label: 'Archived', icon: 'archive', tone: 'state-archived' },
                        { status: 'trash', label: 'Deleted', icon: 'trash', tone: 'state-deleted' },
                      ] as const).map(({ status, label, icon, tone }) => (
                        <button
                          type="button"
                          key={status}
                          className={`btn btn-ghost filter-chip filter-chip--${tone}${effectiveSearchFilters.status === status ? ' filter-chip--active' : ''}`}
                          aria-pressed={effectiveSearchFilters.status === status}
                          onPointerDown={(event) => event.preventDefault()}
                          onClick={() => applySearchFilter((current) => ({
                            ...current,
                            status: current.status === status ? 'all' : status,
                          }))}
                        >
                          <Icon name={icon} /> {label}
                        </button>
                      ))}
                    </div>
                  </section>
                  {filterTags.length > 0 ? <StudioTagPicker id="search-filter-tags" tags={filterTags} selectedTags={[...(effectiveSearchFilters.tags ?? []), ...(effectiveSearchFilters.tag ? [effectiveSearchFilters.tag] : [])]} disabled={studioNavigationBusy} onSelect={toggleSearchTagFilter} expanded={searchTagsExpanded} onExpandedChange={setSearchTagsExpanded} preserveInputFocus/> : null}
                  </div>
                  <div className={`filter-panel__actions${hasActiveSearchFilters ? ' filter-panel__actions--with-clear' : ''}`}>
                    {hasActiveSearchFilters ? (
                      <button
                        type="button"
                        className="btn btn-link filter-panel__clear"
                        onClick={resetSearchFilters}
                      >
                        Clear filters
                      </button>
                    ) : null}
                    <button
                      type="button"
                      className="btn btn-primary filter-panel__show-results"
                      onClick={closeSearchFilters}
                    >
                      <span>
                        Show {renderedSearchResults.length} {renderedSearchResults.length === 1 ? 'match' : 'matches'}
                      </span>
                      <span aria-hidden="true">→</span>
                    </button>
                  </div>
                </aside>
              ) : null}
              <div className="search-results-frame">
                {exportBrowseNotice !== 'hidden' ? <NoteExportBrowseStatus
                  controller={noteExports}
                  requestError={exportRequestError}
                  requestPending={exportRequestPending}
                  settingsHint={exportBrowseNotice === 'settings-hint'}
                  onOpen={openNoteExports}
                  onDismiss={() => setExportBrowseNotice('settings-hint')}
                /> : null}
                {androidMobileShell && activeSearchFilterScope ? (
                  <section className="alert alert-soft search-filter-scope" aria-label="Active filters">
                    <Icon name={activeSearchFilterScope.icon} />
                    <div className="search-filter-scope__copy" role="status" aria-live="polite" aria-atomic="true">
                      <strong>{activeSearchFilterScope.title}</strong>
                      <span>{activeSearchFilterScope.detail}</span>
                    </div>
                    <button
                      type="button"
                      className="btn btn-ghost btn-square search-filter-scope__clear"
                      aria-label="Clear filters"
                      onClick={resetSearchFilters}
                    >
                      <Icon name="close" />
                    </button>
                  </section>
                ) : null}
                {/* The Contexts come first; the count and the layout choice sit right above the notes they are about. */}
                {contextsEnabled && !smartSearchActive && !welcomeVisible && (studioActive || androidMobileShell || contextStripVisible || hasSearchNarrowing) && !contextLabVisible ? (
                  <div id="context-strip">
                    <Suspense fallback={<div className="context-strip context-strip--loading" role="status">Opening contexts…</div>}>
                      <ContextStrip
                        preview
                        onOpenCollection={() => void navigateStudio('contexts', undefined, false)}
                        query={effectiveSearchQuery}
                        matchingNoteIds={hasSearchNarrowing?new Set(renderedSearchResults.map(result=>result.id)):undefined}
                        filterByNotes={hasActiveSearchFilters}
                        title={studioActive ? 'Contexts' : undefined}
                        collapsed={(androidMobileShell || studioActive) && !contextStripVisible && !hasSearchNarrowing}
                        headerAction={(androidMobileShell || studioActive) && !hasSearchNarrowing ? <button
                          type="button"
                          className={`btn btn-ghost btn-square context-strip__toggle${contextStripVisible ? ' context-strip__toggle--expanded' : ''}`}
                          aria-label={contextStripVisible ? 'Hide contexts' : 'Show contexts'}
                          aria-expanded={contextStripVisible}
                          onClick={() => setContextStripVisible(visible => !visible)}
                        ><Icon name="chevron" /></button> : undefined}
                        key={authSession?.user.id ?? 'local-review'}
                        storageScope={authSession?.user.id ?? 'local-review'}
                        onOpenContext={(contextId) => {
                          if (studioActive) { void navigateStudio('context', contextId); return }
                          setContextLabSelectedContextId(contextId)
                          setContextLabBrowseOverride(false)
                        }}
                      />
                    </Suspense>
                  </div>
                ) : null}
                {signInStrip.show && !smartSearchActive && !hasSearchNarrowing && browseStartup.showSummary ? <Suspense fallback={null}><SignInStrip onDismiss={signInStrip.dismiss} /></Suspense> : null}
                {!smartSearchActive && browseStartup.showSummary && !welcomeVisible ? (
                  <div ref={searchSummaryRef} className="search-summary">
                    <div className="search-summary__primary">
                      <span className="search-summary__copy">
                        <span>
                          {renderedSearchResults.length} {hasSearchNarrowing
                            ? renderedSearchResults.length === 1 ? 'note match found' : 'note matches found'
                            : renderedSearchResults.length === 1 ? 'note' : 'notes'}
                        </span>
                        <span>{effectiveSearchFilters.hasAlarm ? 'nearest alarm first' : 'recent first'}</span>
                      </span>
                      {contextsEnabled && studioActive && !hasSearchNarrowing && <button
                        type="button"
                        className="btn btn-link btn-sm search-summary__contexts-toggle"
                        aria-expanded={contextStripVisible}
                        aria-controls="context-strip"
                        onClick={() => setContextStripVisible((visible) => !visible)}
                      >{studioActive ? <Icon name="chevron" /> : null}{contextStripVisible ? 'Hide contexts' : 'Show contexts'}</button>}
                    </div>
                    <SearchLayoutSelector layout={searchLayout} onChange={changeSearchLayout} />
                  </div>
                ) : null}
                {smartSearchActive && <section id="smart-search-results" className="smart-search-results" aria-label="Smart Search results">
                  <div className="smart-search-results__heading"><h2><Icon name="spark"/>Smart Search</h2><span>{contextsEnabled ? 'Notes and Contexts' : 'Notes'}</span></div>
                  {smartSearchBusy ? <p role="status">{contextsEnabled ? 'Finding useful notes and Contexts…' : 'Finding useful notes…'}</p> : smartSearchError ? <p role="alert">{smartSearchError}</p> : smartSearchResult?.query === smartSearchQuery ? <>
                    {contextsEnabled && smartSearchResult.result.contexts.length > 0 && <div className="smart-search-results__group"><h3>Contexts</h3>{smartSearchResult.result.contexts.map(row => { const context = smartSearchResult.contexts.find(item => item.id === row.id); return context ? <button key={row.id} type="button" className="btn btn-ghost smart-search-results__item" onClick={() => { if (studioActive) void navigateStudio('context', row.id); else { setContextLabSelectedContextId(row.id); setContextLabBrowseOverride(false) } }}><Icon name="context"/><span><strong>{context.title}</strong><small>{context.purpose || 'Open Context'}</small></span><Icon name="chevron"/></button> : null })}</div>}
                    {smartSearchResult.result.notes.length > 0 && <div className="smart-search-results__group"><h3>Notes</h3>{smartSearchResult.result.notes.map(row => {
                      const note = notesById.get(row.id)
                      if (!note) return null
                      const title = note.manualTitle || note.derivedTitle || 'Untitled note'
                      const preview = (note.annotation || note.body).replace(/\s+/g, ' ').trim()
                      const detail = preview.toLocaleLowerCase().startsWith(title.toLocaleLowerCase()) ? preview.slice(title.length).trim() : preview
                      return <button key={row.id} type="button" className="btn btn-ghost smart-search-results__item" onClick={() => void openNote(note, undefined, captureSearchReturnState())}><Icon name="document"/><span><strong>{title}</strong>{detail && <small>{detail.slice(0, 160)}</small>}</span><Icon name="chevron"/></button>
                    })}</div>}
                    {!smartSearchResult.result.notes.length && !smartSearchResult.result.contexts.length && <p>No strong match. Switch Smart Search off to check standard results.</p>}
                  </> : <p>{smartSearchQuery.length < 2 ? 'Type at least two characters to search.' : !isOnline ? 'Smart Search needs a connection.' : 'Search this phrase to see Smart Search results.'}</p>}
                  {!smartSearchBusy && !smartSearchResult && !smartSearchError && smartSearchQuery.length >= 2 && isOnline ? <button type="button" className="btn smart-search-results__run" onClick={() => void runJevSearch()}>Search this phrase</button> : null}
                  {smartSearchError && isOnline ? <button type="button" className="btn smart-search-results__run" onClick={() => void runJevSearch()}>Try again</button> : null}
                </section>}
                {!smartSearchActive && <div
                  id="search-results-list"
                  className={`search-results search-results--${searchLayout}`}
                  role="listbox"
                  aria-label="Search results"
                  aria-owns={visibleSearchResults.length > 0
                    ? visibleSearchResults.map((result) => `search-result-${result.id}`).join(' ')
                    : undefined}
                >
              {visibleSearchResults.length > 0 ? (
                <div
                  ref={searchMasonryRef}
                  className="search-results__masonry-columns"
                  role="presentation"
                  style={{
                    '--search-result-removal-duration': `${APP_SETTINGS.interactions.searchResultRemovalAnimationMs}ms`,
                  } as CSSProperties}
                >
                  {visibleSearchResults.map((result) => (
                    <div
                      className={removingSearchNoteIds.has(result.id)
                        ? 'search-results__masonry-item search-results__masonry-item--removing'
                        : 'search-results__masonry-item'}
                      role="presentation"
                      key={result.id}
                    >
                      {renderSearchResult(result)}
                    </div>
                  ))}
                </div>
              ) : null}
              {browseStartup.showEmptyState ? (
                authSession && (accountRestorePhase === 'restoring' || accountRestorePhase === 'error') ? (
                  <div className="empty-state" role="status">
                    <Icon name="search" />
                    <h3>
                      {accountRestorePhase === 'restoring'
                        ? 'Restoring your notes…'
                        : 'Account restore needs attention.'}
                    </h3>
                    <p>
                      {accountRestorePhase === 'restoring'
                        ? 'miniMany is verifying the synchronized account before treating zero results as complete.'
                        : 'Local notes are safe. Reconnect or open Account & data to review the synchronization issue.'}
                    </p>
                  </div>
                ) : welcomeVisible ? (
                  <Suspense fallback={null}>
                    <WelcomePage onSave={saveWelcomeNote} />
                  </Suspense>
                ) : welcome.pending && searchEmptyCause === 'empty' ? null : (
                  <SearchEmptyState
                    cause={searchEmptyCause}
                    onClearSearch={clearSearchQuery}
                    onResetFilters={resetSearchFilters}
                  />
                )
              ) : null}
              {browseStartup.showLoadError ? (
                <div className="empty-state" role="status">
                  <Icon name="search" />
                  <h3>Local notes could not be checked.</h3>
                  <p>Close and reopen miniMany. Existing notes remain stored on this device.</p>
                </div>
              ) : null}
              {hasMoreSearchResults ? (
                <button
                  ref={searchLoadMoreRef}
                  type="button"
                  className="search-results__loader"
                  onClick={showMoreSearchResults}
                >
                  Show more notes
                </button>
              ) : null}
            </div>}
              </div>
            </div>
          </section>
        ) : null}
      </main>

      <style>{BROWSE_PRESENTATION_STYLES}</style>

      {selectionDialog && selectionDialog.scope === operationScope && <NoteSelectionDialog kind={selectionDialog.kind} notes={selectionDialog.notes} tags={suggestedTags} assets={assets} remoteAssetUrls={remoteAssetUrls} formFactor={studio.runtime.formFactor} progress={operationProgress} onClose={() => setSelectionDialog(null)} onApply={applySelectionAction} />}
      {noteExportDialog && noteExportDialog.scope === operationScope && <NoteExportDialog
        imageCount={noteExportDialog.notes.reduce((sum, note) => sum + note.assetIds.length, 0)}
        noteCount={noteExportDialog.notes.length}
        onClose={() => setNoteExportDialog(null)}
        onQueue={queueSelectedNoteExport}
      />}
      <SearchSelectionBar
        count={selectedSearchNotes.length}
        onBump={() => void bumpSelectedSearchNotes()}
        onAddToContext={contextsEnabled ? () => setAddToContextNotes({ scope: authSession!.user.id, notes: selectedSearchNotes }) : undefined}
        onMerge={() => setSelectionDialog({ kind: 'merge', notes: selectedSearchNotes, scope: operationScope })}
        onTags={() => setSelectionDialog({ kind: 'tags', notes: selectedSearchNotes, scope: operationScope })}
        onCopy={copySelectedSearchNotes}
        onExport={() => {
          if (!authSession) {
            void notify.error('Sign in to prepare a background export')
            return
          }
          setNoteExportDialog({ notes: selectedSearchNotes, scope: operationScope })
        }}
        onDismiss={dismissSearchSelection}
        onTrash={() => void trashSelectedSearchNotes()}
        open={mode === 'search' && !browseCovered && !accountPanelOpen && searchSelectionMode}
      />

      {contextsEnabled && addToContextNotes && addToContextNotes.scope === (authSession?.user.id ?? 'local-review') && <Suspense fallback={null}>
        <AddToContextDialog key={addToContextNotes.scope} scope={addToContextNotes.scope} notes={addToContextNotes.notes}
          smartSuggestions={smartSuggestionsEnabled} initialSuggestedContextIds={addToContextNotes.suggestedContextIds}
          onClose={() => setAddToContextNotes(null)} onAdded={(id, undo, addedIds, title) => {
            const returnToNote = Boolean(addToContextNotes.returnToNote)
            setAddToContextNotes(null)
            if (!returnToNote && addedIds.length === 1) {
              dismissSearchSelection()
              setContextLabSelectedContextId(id)
              setContextLabBrowseOverride(false)
            } else if (!returnToNote) dismissSearchSelection()
            // Already in the Context when it was added from the list; from a note (or several Contexts) a way there is offered.
            notifyContextAdded(returnToNote && addedIds.length === 1 ? id : null, addToContextNotes.notes[0]?.id ?? '', addedIds.length, undo, () => { if (!returnToNote) setContextLabBrowseOverride(true) }, title)
          }} />
      </Suspense>}

      {smartSuggestionsEnabled && smartSuggestion?.placement === 'sheet' && mode === 'search' && !accountPanelOpen && <SmartContextSuggestion
        key={smartSuggestion.note.id} count={smartSuggestion.ids.length} busy={smartSuggestionBusy} preview={notePreview(smartSuggestion.note)}
        title={smartSuggestion.records.find(record => record.id === smartSuggestion.ids[0])?.title}
        purpose={smartSuggestion.records.find(record => record.id === smartSuggestion.ids[0])?.purpose}
        onAdd={() => void attachSuggestedContext()}
        onChoose={() => { smartSuggestion.flow.event('owner_choice', 'ok', 'choose_contexts'); setAddToContextNotes({ scope: authSession!.user.id, notes: [smartSuggestion.note], suggestedContextIds: smartSuggestion.ids }); setSmartSuggestion(null) }}
        onDismiss={() => { smartSuggestion.flow.event('owner_choice', 'ok', 'dismissed'); setSmartSuggestion(null) }}
      />}

      {mode === 'search' && !browseCovered && !accountPanelOpen && !searchSelectionMode && !smartSuggestion ? (
        <button
          ref={searchFabRef}
          className="search-fab visual-viewport-bottom btn"
          type="button"
          onClick={openPhoneSearchSurface}
          aria-label="Search notes"
        >
          <Icon name="search" />
        </button>
      ) : null}

      {mode === 'search' && !browseCovered && !accountPanelOpen && !searchSelectionMode && !smartSuggestion ? (
        <button
          className="new-note-fab visual-viewport-bottom"
          type="button"
          onClick={() => void beginNewNote(false, false, captureSearchReturnState())}
          aria-label="New note"
        >
          <Icon name="new" />
        </button>
      ) : null}

      <footer className="app-footer">
        <span>{storagePersistent === true ? 'Persistent offline storage' : 'Offline storage active'}</span>
        <span>·</span>
        <span>
          {!loaded
            ? 'Loading local notes'
            : hasRemoteAccount && accountRestorePhase !== 'ready'
              ? accountRestorePhase === 'error' ? 'Restore incomplete' : 'Restoring account'
              : 'Ready'}
        </span>
      </footer>
    </div>
    {devToolsAllowed && markdownNotesImportOpen && MarkdownNotesImport ? <Suspense fallback={<p className="sr-only" role="status">Opening note creator…</p>}><MarkdownNotesImport key={authSession?.user.id ?? 'local'} deviceId={deviceId} enabled={!authRestoring && !authBusy && (!authSession || accountRestorePhase === 'ready')} onClose={() => setMarkdownNotesImportOpen(false)} onCreate={async note => {
      if (authBusyRef.current || authSessionRef.current?.user.id !== authSession?.user.id || (authSessionRef.current && !accountRestoreReadyRef.current)) throw Error('Account changed or is restoring. Reopen the tool when ready.')
      await repository.commitLocalNote(note)
      searchIndex.upsert(note)
      setNotes(current => updateNoteCollection(current, note))
      requestSyncRef.current({ push: true, domains: [] })
    }}/></Suspense> : null}
    <NotificationsViewport captureMode={mode === 'capture'} />
    </>
  )
}
